import React__default, { createContext, useContext, createElement, Fragment, Component, createRef, useRef, useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import Helmet$1, { Helmet } from 'react-helmet';
import ReactDOM from 'react-dom';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const MEDIA_BREAKPOINTS = {
  X_LARGE: '@media (min-width: 1201px)',
  LARGE: '@media (max-width: 1200px)',
  MEDIUM: '@media (max-width: 1000px)',
  SMALL: '@media (max-width: 700px)'
};

const EventContext = createContext({
  category: 'default category'
});
const EventProvider = ({
  children,
  category: newCategory
}) => {
  const {
    category
  } = useContext(EventContext);
  const newValue = {
    category: newCategory || category
  };
  return createElement(EventContext.Provider, {
    value: newValue
  }, children);
};

const Link = ({
  children,
  onClick,
  ...props
}) => {
  const {
    category
  } = useContext(EventContext);

  const sendClickAnalytics = () => {
    if (typeof category !== 'undefined') {
      window.mmClientApi = window.mmClientApi || [];
      window.mmClientApi.push('analytics', {
        event_category: category,
        event_action: 'click',
        event_label: ''
      });
    }
  };

  const onClickHandler = (...args) => {
    if (onClick) {
      onClick(...args);
    }

    sendClickAnalytics();
  };

  return createElement("a", _extends({}, props, {
    onClick: onClickHandler
  }), children);
};

const baseStyles = {
  base: {
    flex: '0 0 auto',
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'initial',
    fontFamily: 'inherit',
    height: '100%',
    ':visited': {
      textDecoration: 'none',
      color: 'initial'
    },
    ':active': {
      textDecoration: 'none',
      color: 'initial'
    },
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      ':hover': {
        color: 'initial'
      }
    }
  }
};
const generateLinkStyleFromTheme = (themedLinkStyle, additionalStyle = {}) => {
  return {
    style: {
      color: themedLinkStyle.idleColor,
      textDecoration: 'none',
      ':visited': {
        color: themedLinkStyle.idleColor
      },
      ':active': {
        color: themedLinkStyle.activeStateColor
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        ':hover': {
          color: themedLinkStyle.hoverStateColor
        }
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        ':hover': {
          color: themedLinkStyle.hoverStateColor
        }
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        ':hover': {
          color: themedLinkStyle.idleColor
        }
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        ':hover': {
          color: themedLinkStyle.idleColor
        }
      }
    },
    additionalStyle
  };
};

const combineStyleWithBase = (base, newStyle) => {
  return newStyle ? css(StyleSheet.create(base).base, StyleSheet.create(newStyle).style, StyleSheet.create(newStyle).additionalStyle) : css(StyleSheet.create(base).base);
};

const TextLinkComponent = props => {
  const {
    children,
    href,
    target,
    rel,
    linkStyle = null
  } = props;
  const finalStyle = combineStyleWithBase(baseStyles, linkStyle);
  return createElement(Link, {
    href: href,
    target: target,
    rel: rel,
    className: finalStyle
  }, children);
};

const TYPOGRAPHY_TAGS = {
  H1: 'h1',
  H2: 'h2',
  H3: 'h3',
  H4: 'h4',
  P: 'p',
  SPAN: 'span'
};
const COLORS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  DARK_GREY: 'darkGrey',
  GREY: 'grey',
  LIGHT_GREY: 'lightGrey',
  BLACK: 'black',
  WHITE: 'white'
};
const SURFACES = {
  EXTEND: 'extend',
  EXTEND_ROUNDED: 'extend rounded',
  NORMAL: 'normal',
  BORDER_RADIUS: 'border radius',
  BOX_SHADOW: 'box shadow'
};
const VR_LINEHEIGHT = {
  ALL: 'ALL'
};
const CONFIGURATION_TYPES = {
  COLOR: 'COLOR',
  TYPOGRAPHY: 'TYPOGRAPHY',
  SURFACE: 'SURFACE',
  VR_LINEHEIGHT: 'VERTYCAL_RHYTHM'
};
const baseConfiguration = {
  onTopCard: [{
    name: 'cardTextColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.EXTEND,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  verticalCard: [{
    name: 'cardBackgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'cardTextColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.EXTEND,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  horizontalCard: [{
    name: 'cardBackgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'cardTextColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.EXTEND,
    type: CONFIGURATION_TYPES.SURFACE
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  cardCaptionContainer: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  cardOverlay: [{
    name: 'cardOverlayColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  headlinesArticleList: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  oneThreeArticleList: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  decorationLine: [{
    name: 'cardDecorationColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  sectionHeader: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'titleColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'idleColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'visitedStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  listWithHeading: [{
    name: 'titleColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'backgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'surfaceStyle',
    value: SURFACES.NORMAL,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  list: [{
    name: 'backgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.NORMAL,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  listItem: [{
    name: 'borderColor',
    value: COLORS.GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'linkColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'linkHoverColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  scrollableArticleList: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  sixSixArticleList: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  button: [{
    name: 'backgroundColorEnabled',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColorEnabled',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'backgroundColorDisabled',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColorDisabled',
    value: COLORS.GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyleEnabled',
    value: SURFACES.EXTEND_ROUNDED,
    type: CONFIGURATION_TYPES.SURFACE
  }, {
    name: 'surfaceStyleDisabled',
    value: SURFACES.EXTEND_ROUNDED,
    type: CONFIGURATION_TYPES.SURFACE
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  h1: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.H1,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  h2: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.H2,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  h3: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.H3,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  h4: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.H4,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  span: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.SPAN,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  p: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.P,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  ul: [{
    name: 'tagStyle',
    value: TYPOGRAPHY_TAGS.P,
    type: CONFIGURATION_TYPES.TYPOGRAPHY
  }],
  main: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  mainPostPage: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'backgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  highlightsArticleList: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  primaryHeader: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'backgroundColor',
    value: COLORS.BLACK,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.BOX_SHADOW,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  primaryLinks: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'idleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'visitedStateColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeBorderBottom',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverOtherLinksColors',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  editions: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  editionsMenu: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'menuBackgroundColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'linkColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'surfaceStyle',
    value: SURFACES.BOX_SHADOW,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  ctaButton: [{
    name: 'backgroundColorNormal',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColorNormal',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'borderColorNormal',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'backgroundColorHover',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColorHover',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'borderColorHover',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'backgroundColorActive',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColorActive',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'borderColorActive',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'surfaceStyle',
    value: SURFACES.BORDER_RADIUS,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  footer: [{
    name: 'backgroundColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'borderTopColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'separatorColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  footerLinksSection: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'idleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeStateColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'visitedStateColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  footerMobileStores: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  secondaryHeader: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'backgroundColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'surfaceStyle',
    value: SURFACES.BOX_SHADOW,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  secondaryLinks: [{
    name: 'idleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'hoverOtherLinksColors',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  moreLinks: [{
    name: 'titleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'menuBackgroundColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'linkColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'hoverColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'surfaceStyle',
    value: SURFACES.BOX_SHADOW,
    type: CONFIGURATION_TYPES.SURFACE
  }],
  menuIcon: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  ampCloseMenuIcon: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  menuLinks: [{
    name: 'backgroundColor',
    value: COLORS.SECONDARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'dividerColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  menuLink: [{
    name: 'idleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'activeStateColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  menuSubLink: [{
    name: 'idleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  footerSocialLinks: [{
    name: 'titleColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'circleColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  bottomFooterBar: [{
    name: 'backgroundColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  errorPage: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'titleColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'buttonColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'buttonBackgroundColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  articleTitle: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'titleColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'metaDataColor',
    value: COLORS.GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  postSidebar: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  articleMainImage: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  imageWithFigcaptionWrapper: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'figcaptionTextColor',
    value: COLORS.GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  imageWrapper: [{
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  articleParagraph: [{
    name: 'linkColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  articleContent: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  quote: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'borderColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'citeColor',
    value: COLORS.GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'iconColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  divider: [{
    name: 'color',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  mmPlayer: [{
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  iframeEmbed: [{
    name: 'backgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  iframe: [{
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  postMainContent: [{
    name: 'backgroundColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  streamable: [{
    name: 'backgroundColor',
    value: COLORS.BLACK,
    type: CONFIGURATION_TYPES.COLOR
  }],
  listItemTitle: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'titleColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'numberColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  articleShareButton: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'buttonBackgroundColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'buttonHoverColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  copyLinkButton: [{
    name: 'color',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }],
  playIcon: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'fillColor',
    value: COLORS.WHITE,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'lineColor',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }],
  mobileTopBar: [{
    name: 'backgroundColor',
    value: COLORS.BLACK,
    type: CONFIGURATION_TYPES.COLOR
  }],
  taboola: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  postRecommendations: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  postRecommendationsHeader: [{
    name: 'color',
    value: COLORS.PRIMARY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  recommendationCardPlaceHolder: [{
    name: 'backgroundColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  ampSocialContainer: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }],
  sponsor: [{
    name: 'vrLineHeight',
    value: VR_LINEHEIGHT.ALL,
    type: CONFIGURATION_TYPES.VR_LINEHEIGHT
  }, {
    name: 'dividerColor',
    value: COLORS.LIGHT_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }, {
    name: 'textColor',
    value: COLORS.DARK_GREY,
    type: CONFIGURATION_TYPES.COLOR
  }]
};

class ConfigurationBuilder {
  constructor(overrides = { ...baseConfiguration
  }) {
    this.overrides = overrides;
  }

  replaceValueInElement(componentName, propName, value) {
    return this.overrides[componentName].map(element => {
      if (element.name === propName) {
        return { ...element,
          value
        };
      }

      return element;
    });
  }

  overrideColor(componentName, propName, color) {
    this.overrides[componentName] = this.replaceValueInElement(componentName, propName, color);
    return this;
  }

  overrideSurfaceStyle(componentName, propName, surfaceStyle) {
    this.overrides[componentName] = this.replaceValueInElement(componentName, propName, surfaceStyle);
    return this;
  }

  build() {
    return { ...this.overrides
    };
  }

}

const configurationBuilder = () => new ConfigurationBuilder();

const configuration = configurationBuilder().overrideColor('verticalCard', 'cardTextColor', 'primary').overrideColor('horizontalCard', 'cardTextColor', 'primary').overrideColor('cardOverlay', 'cardOverlayColor', 'black').overrideColor('decorationLine', 'cardDecorationColor', 'primary').overrideColor('sectionHeader', 'titleColor', 'secondary').overrideColor('sectionHeader', 'idleColor', 'secondary').overrideColor('sectionHeader', 'hoverStateColor', 'secondary').overrideColor('sectionHeader', 'activeStateColor', 'secondary').overrideColor('listWithHeading', 'titleColor', 'secondary').overrideColor('listItem', 'linkColor', 'primary').overrideColor('listItem', 'linkHoverColor', 'secondary').overrideSurfaceStyle('button', 'surfaceStyleEnabled', 'extend').overrideSurfaceStyle('button', 'surfaceStyleDisabled', 'normal').overrideColor('primaryHeader', 'backgroundColor', 'primary').overrideColor('primaryLinks', 'hoverStateColor', 'white').overrideColor('primaryLinks', 'activeStateColor', 'white').overrideColor('primaryLinks', 'activeBorderBottom', 'white').overrideColor('primaryLinks', 'hoverOtherLinksColors', 'grey').overrideColor('editionsMenu', 'menuBackgroundColor', 'primary').overrideColor('footer', 'separatorColor', 'primary').overrideColor('secondaryLinks', 'hoverStateColor', 'white').overrideColor('secondaryLinks', 'hoverOtherLinksColors', 'grey').overrideColor('moreLinks', 'hoverColor', 'white').overrideColor('mobileTopBar', 'backgroundColor', 'primary').build();
const configuration$1 = configurationBuilder().build();
const configuration$2 = configurationBuilder().overrideColor('primaryHeader', 'backgroundColor', 'primary').overrideColor('primaryLinks', 'hoverStateColor', 'grey').overrideColor('primaryLinks', 'activeStateColor', 'white').overrideColor('primaryLinks', 'activeBorderBottom', 'white').overrideColor('footer', 'separatorColor', 'primary').overrideColor('secondaryLinks', 'hoverStateColor', 'grey').overrideColor('secondaryLinks', 'activeStateColor', 'white').overrideColor('bottomFooterBar', 'backgroundColor', 'primary').overrideColor('mobileTopBar', 'backgroundColor', 'primary').build();
const configuration$3 = configurationBuilder().overrideColor('moreLinks', 'hoverColor', 'grey').overrideColor('moreLinks', 'activeColor', 'secondary').overrideColor('menuIcon', 'color', 'primary').overrideColor('menuLinks', 'backgroundColor', 'black').overrideColor('menuLink', 'activeStateColor', 'secondary').overrideColor('footerSocialLinks', 'circleColor', 'secondary').overrideColor('bottomFooterBar', 'backgroundColor', 'black').overrideColor('articleTitle', 'titleColor', 'primary').overrideColor('mobileTopBar', 'backgroundColor', 'primary').overrideColor('primaryHeader', 'backgroundColor', 'white').overrideColor('secondaryHeader', 'backgroundColor', 'darkGrey').overrideColor('primaryLinks', 'idleColor', 'darkGrey').overrideColor('primaryLinks', 'visitedStateColor', 'darkGrey').overrideColor('primaryLinks', 'hoverOtherLinksColors', 'darkGrey').overrideColor('secondaryLinks', 'hoverStateColor', 'secondary').overrideColor('secondaryLinks', 'activeStateColor', 'secondary').overrideColor('secondaryLinks', 'hoverOtherLinksColors', 'lightGrey').overrideColor('moreLinks', 'menuBackgroundColor', 'darkGrey').overrideColor('moreLinks', 'hoverColor', 'secondary').overrideColor('editions', 'color', 'darkGrey').overrideColor('editionsMenu', 'color', 'darkGrey').overrideColor('editionsMenu', 'menuBackgroundColor', 'white').overrideColor('editionsMenu', 'hoverColor', 'primary').overrideColor('editionsMenu', 'linkColor', 'darkGrey').overrideColor('editionsMenu', 'activeColor', 'primary').overrideColor('footer', 'backgroundColor', 'darkGrey').overrideColor('footer', 'borderTopColor', 'secondary').overrideColor('footer', 'separatorColor', 'secondary').overrideColor('footerLinksSection', 'hoverStateColor', 'secondary').overrideColor('cardOverlay', 'cardOverlayColor', 'black').overrideColor('decorationLine', 'cardDecorationColor', 'secondary').overrideColor('articleShareButton', 'buttonBackgroundColor', 'secondary').build();
const configuration$4 = configurationBuilder().overrideColor('cardOverlay', 'cardOverlayColor', 'black').overrideColor('editionsMenu', 'menuBackgroundColor', 'black').overrideColor('footer', 'backgroundColor', 'darkGrey').overrideColor('footer', 'separatorColor', 'primary').overrideColor('secondaryHeader', 'backgroundColor', 'darkGrey').overrideColor('moreLinks', 'menuBackgroundColor', 'darkGrey').overrideColor('bottomFooterBar', 'backgroundColor', 'black').overrideColor('editionsMenu', 'hoverColor', 'primary').overrideColor('menuLinks', 'backgroundColor', 'black').build();
const configuration$5 = configurationBuilder().build();

class VerticalRhythm {
  constructor({
    fontSize,
    lineHeight,
    fontScaleFactor,
    lineHeightMap
  }) {
    _defineProperty(this, "fontSize", void 0);

    _defineProperty(this, "lineHeight", void 0);

    _defineProperty(this, "fontScaleFactor", void 0);

    _defineProperty(this, "lineHeightMap", void 0);

    _defineProperty(this, "body", void 0);

    _defineProperty(this, "h4", void 0);

    _defineProperty(this, "h3", void 0);

    _defineProperty(this, "h2", void 0);

    _defineProperty(this, "h1", void 0);

    _defineProperty(this, "p", void 0);

    _defineProperty(this, "span", void 0);

    this.fontSize = fontSize;
    this.lineHeight = Math.ceil(fontSize * lineHeight);
    this.fontScaleFactor = fontScaleFactor;
    this.lineHeightMap = lineHeightMap;
    this.body = {
      fontSize,
      lineHeight: `${this.lineHeight}px`
    };
    this.h4 = this.body;
    this.h3 = this.scaleUp(this.h4.fontSize, lineHeightMap.h3);
    this.h2 = this.scaleUp(this.h3.fontSize, lineHeightMap.h2);
    this.h1 = this.scaleUp(this.h2.fontSize, lineHeightMap.h1);
    this.p = this.body;
    this.span = this.body;
  }

  scaleUp(fontSize, lineHeightMultiplier) {
    const scaledUpFontSize = Math.round(fontSize * this.fontScaleFactor);
    return {
      fontSize: scaledUpFontSize,
      lineHeight: `${this.lineHeight * lineHeightMultiplier}px`
    };
  }

}

const createVrFromStrategy = strategy => new VerticalRhythm(strategy);

const createTagStyle = verticalRhythmStrategies => tagName => ({
  [MEDIA_BREAKPOINTS.X_LARGE]: createVrFromStrategy(verticalRhythmStrategies.xLarge)[tagName],
  [MEDIA_BREAKPOINTS.LARGE]: createVrFromStrategy(verticalRhythmStrategies.large)[tagName],
  [MEDIA_BREAKPOINTS.MEDIUM]: createVrFromStrategy(verticalRhythmStrategies.medium)[tagName],
  [MEDIA_BREAKPOINTS.SMALL]: createVrFromStrategy(verticalRhythmStrategies.small)[tagName]
});

const createVerticalRhythm = verticalRhythmStrategies => ({
  [MEDIA_BREAKPOINTS.X_LARGE]: createVrFromStrategy(verticalRhythmStrategies.xLarge),
  [MEDIA_BREAKPOINTS.LARGE]: createVrFromStrategy(verticalRhythmStrategies.large),
  [MEDIA_BREAKPOINTS.MEDIUM]: createVrFromStrategy(verticalRhythmStrategies.medium),
  [MEDIA_BREAKPOINTS.SMALL]: createVrFromStrategy(verticalRhythmStrategies.small)
});

const vrLineHeight = verticalRhythm => Object.keys(verticalRhythm).reduce((accumulator, mediaBreakpoint) => ({ ...accumulator,
  [mediaBreakpoint]: verticalRhythm[mediaBreakpoint].lineHeight
}), {});

const createTagStyles = verticalRhythmStrategies => {
  const tagStyleCreator = createTagStyle(verticalRhythmStrategies);
  return Object.values(TYPOGRAPHY_TAGS).reduce((accumulator, tag) => ({ ...accumulator,
    [tag]: tagStyleCreator(tag)
  }), {});
};

const VRFactory = verticalRhythmStrategies => {
  const verticalRhythm = createVerticalRhythm(verticalRhythmStrategies);
  return {
    vrLineHeight: vrLineHeight(verticalRhythm),
    tagStyles: createTagStyles(verticalRhythmStrategies)
  };
};

class PaletteManager {
  constructor(palette) {
    _defineProperty(this, "palette", void 0);

    _defineProperty(this, "verticalRhythm", void 0);

    this.palette = palette;
    const {
      vrLineHeight,
      tagStyles
    } = VRFactory(palette.verticalRhythmStrategies);
    this.verticalRhythm = {
      vrLineHeight: {
        [VR_LINEHEIGHT.ALL]: vrLineHeight
      },
      tagStyles
    };
  }

  getColorByKey(key) {
    return this.palette.colors[key];
  }

  getSurfaceStyleByKey(surfaceKey) {
    return Object.entries({
      [MEDIA_BREAKPOINTS.X_LARGE]: this.palette.surfaceStyle[surfaceKey].xLarge,
      [MEDIA_BREAKPOINTS.LARGE]: this.palette.surfaceStyle[surfaceKey].large,
      [MEDIA_BREAKPOINTS.MEDIUM]: this.palette.surfaceStyle[surfaceKey].medium,
      [MEDIA_BREAKPOINTS.SMALL]: this.palette.surfaceStyle[surfaceKey].small
    }).reduce((SurfaceStyle, [mediaBreakpoint, style]) => {
      return style ? { ...SurfaceStyle,
        [mediaBreakpoint]: style
      } : SurfaceStyle;
    }, { ...this.palette.surfaceStyle[surfaceKey].allSizes
    });
  }

  getFonts() {
    return this.palette.fonts;
  }

  getTypographyStyleByKey(key) {
    return { ...this.getTagStyle(key),
      ...this.palette.typography[key].style
    };
  }

  getTagStyle(key) {
    return this.verticalRhythm.tagStyles[key];
  }

  getVrLineHeight(key) {
    return this.verticalRhythm.vrLineHeight[key];
  }

  getFontFamilyByKey(key) {
    return `${this.palette.typography[key].fontFamily}`;
  }

  getDefaultFontFamilyByKey(key) {
    return this.palette.typography[key].defaultFontFamily;
  }

}

function translatePropertyValueByType(instructions, {
  value,
  type
}) {
  if (typeof instructions === 'undefined') {
    throw new Error('must supply instructions to the transformation');
  }

  if (typeof instructions[type] !== 'function') {
    throw new Error(`there is no instruction function to type: ${type}`);
  }

  return instructions[type](value);
}

function translateProperty(instructions, property) {
  return {
    [property.name]: translatePropertyValueByType(instructions, property)
  };
}

function translateComponent(instructions, themeProperties) {
  return themeProperties.reduce((accumulator, componentProperty) => ({ ...accumulator,
    ...translateProperty(instructions, componentProperty)
  }), {});
}

function translate(instructions, configuration) {
  return Object.keys(configuration).reduce((accumulator, name) => ({ ...accumulator,
    [name]: translateComponent(instructions, configuration[name])
  }), {});
}

class ThemeManager {
  constructor(palette, themeConfiguration) {
    _defineProperty(this, "paletteManager", void 0);

    _defineProperty(this, "themeConfiguration", void 0);

    _defineProperty(this, "instructions", {
      [CONFIGURATION_TYPES.COLOR]: key => this.paletteManager.getColorByKey(key),
      [CONFIGURATION_TYPES.SURFACE]: key => this.paletteManager.getSurfaceStyleByKey(key),
      [CONFIGURATION_TYPES.TYPOGRAPHY]: key => this.paletteManager.getTypographyStyleByKey(key),
      [CONFIGURATION_TYPES.VR_LINEHEIGHT]: key => this.paletteManager.getVrLineHeight(key)
    });

    this.themeConfiguration = themeConfiguration;
    this.paletteManager = new PaletteManager(palette);
  }

  getConfiguration() {
    return translate(this.instructions, this.themeConfiguration);
  }

}

const elements = ['h1', 'h2', 'h3', 'h4', 'span', 'p'];
const ThemeContext = createContext({});
const ThemeProviderWithFonts = WebFontsComponent => {
  const ThemeProviderWithFontsComponent = props => {
    const {
      children,
      palette,
      themeConfiguration
    } = props;
    const themeManager = new ThemeManager(palette, themeConfiguration);
    const configuration = themeManager.getConfiguration();
    const fontsConfiguration = themeManager.paletteManager.getFonts();
    const fontFamilyByElement = elements.reduce((acc, element) => ({ ...acc,
      [element]: {
        fontFamily: themeManager.paletteManager.getFontFamilyByKey(element),
        defaultFontFamily: themeManager.paletteManager.getDefaultFontFamilyByKey(element)
      }
    }), {});
    return createElement(Fragment, null, createElement(WebFontsComponent, {
      fontFamilyByElement: fontFamilyByElement,
      fontsConfiguration: fontsConfiguration
    }, createElement(ThemeContext.Provider, {
      value: configuration
    }, children)));
  };

  return ThemeProviderWithFontsComponent;
};

class EmptyReporter {
  reportException(err, additionalData) {}

  reportWarning(message) {}

}
const ErrorReporting = createContext(new EmptyReporter());

/* eslint-disable */
const useTheme = (key, mapThemeProps) => {
  const reporter = useContext(ErrorReporting);
  const themeContext = useContext(ThemeContext);

  if (themeContext[key]) {
    return mapThemeProps(themeContext[key]);
  }

  reporter.reportWarning(`cannot find key: ${key} in configuration`);
  return {};
};

const componentConfigurationKey = 'sectionHeader';

const createStyles = (vrLineHeight, titleColor, marginTop) => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * marginTop}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  titleItem: {
    flex: '1 1 auto',
    color: titleColor
  }
});

const createLinkTextStyles = () => StyleSheet.create({
  linkText: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      ':hover': {
        textDecoration: 'underline'
      }
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        textDecoration: 'underline'
      }
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      ':hover': {
        color: 'initial'
      }
    }
  }
});

const createLink = ({
  linkText,
  idleColor,
  activeStateColor,
  hoverStateColor,
  href
}) => {
  if (href && linkText) {
    const linkStyle = generateLinkStyleFromTheme({
      idleColor,
      activeStateColor,
      hoverStateColor
    });
    const style = createLinkTextStyles();
    return createElement(TextLinkComponent, {
      href: href,
      linkStyle: linkStyle
    }, createElement("h4", {
      className: css(style.linkText)
    }, linkText));
  }

  return null;
};

const SectionHeaderComponent = ({
  linkText,
  vrLineHeight,
  titleColor,
  idleColor = '',
  activeStateColor = '',
  hoverStateColor = '',
  href,
  marginTop = 1,
  children
}) => {
  const style = createStyles(vrLineHeight, titleColor, marginTop);
  return createElement("div", {
    className: css(style.container)
  }, createElement("div", {
    className: css(style.titleItem)
  }, children), createLink({
    linkText,
    idleColor,
    activeStateColor,
    hoverStateColor,
    href
  }));
};

const mapThemeProps = theme => ({
  vrLineHeight: theme.vrLineHeight,
  titleColor: theme.titleColor,
  idleColor: theme.idleColor,
  activeStateColor: theme.activeStateColor,
  hoverStateColor: theme.hoverStateColor
});

const SectionHeader = props => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return createElement(SectionHeaderComponent, _extends({}, themeContext, props));
};

const PageDescription = ({
  description,
  favicon,
  pageUrl,
  title,
  ampURL
}) => createElement(Helmet, null, createElement("title", null, title), createElement("meta", {
  charSet: "utf-8"
}), createElement("meta", {
  name: "description",
  content: description
}), createElement("link", {
  rel: "icon",
  href: favicon,
  type: "image/png"
}), createElement("link", {
  rel: "canonical",
  href: pageUrl
}), ampURL ? createElement("link", {
  rel: "amphtml",
  href: ampURL
}) : null);

const FacebookMeta = ({
  appId,
  pageId
}) => createElement(Helmet, null, appId ? createElement("meta", {
  property: "fb:app_id",
  content: appId
}) : null, pageId ? createElement("meta", {
  property: "fb:pages",
  content: pageId
}) : null);

const OpenGraph = ({
  title,
  siteName,
  image,
  pageUrl
}) => createElement(Helmet, null, createElement("meta", {
  property: "og:title",
  content: title
}), createElement("meta", {
  property: "og:site_name",
  content: siteName
}), image ? createElement("meta", {
  property: "og:image",
  content: image
}) : null, createElement("meta", {
  property: "og:url",
  content: pageUrl
}));

const TwitterShare = ({
  title,
  description,
  account,
  image,
  pageUrl
}) => createElement(Helmet, null, createElement("meta", {
  property: "twitter:title",
  content: title
}), createElement("meta", {
  property: "twitter:description",
  content: description
}), account ? createElement("meta", {
  property: "twitter:site",
  content: account
}) : null, image ? createElement("meta", {
  property: "twitter:image",
  content: image
}) : null, createElement("meta", {
  property: "twitter:url",
  content: pageUrl
}), createElement("meta", {
  property: "twitter:card",
  content: "summary_large_image"
}));

const calculateImageUrl = (host, path, manipulation) => `${host + manipulation}/${path}`;

const getSrcSet = (aspectRatio, host, path) => ({
  small: calculateImageUrl(host, path, `c_fill,w_372,ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`),
  medium: calculateImageUrl(host, path, `c_fill,w_688,ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`),
  large: calculateImageUrl(host, path, `c_fill,w_912,ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`),
  lowResUrl: calculateImageUrl(host, path, `c_fill,w_16,ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`)
});

const getSrcSetAttributeValue = (small, medium, large) => {
  return `${small} 300w, ${medium} 600w, ${large} 900w`;
};

const getMinWidthBySize = size => {
  switch (size) {
    case 'small':
      return '(min-width: 300px)';

    case 'medium':
      return '(min-width: 600px)';

    case 'large':
      return '(min-width: 900px)';

    default:
      return '';
  }
};
const imageStyle = StyleSheet.create({
  base: {
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    minWidth: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    transition: 'opacity 0.2s ease-in-out',
    willChange: 'opacity, transform',
    objectFit: 'cover',
    minHeight: '100%'
  }
});
const getCalculatedThumbnail = (aspectRatio, description, host, path) => {
  const {
    small,
    medium,
    lowResUrl,
    large
  } = getSrcSet(aspectRatio, host, path);
  return {
    imageUrl: medium,
    lowResUrl,
    alt: description,
    title: description,
    srcSet: getSrcSetAttributeValue(small, medium, large),
    sizes: '(max-width: 300px) 300px,(max-width: 600px) 600px,(min-width: 600px) 900px'
  };
};

const numArticlesForSchema = 5;
const sortByDate = (article1, article2) => {
  const article1Time = new Date(article1.featuredAt);
  const article2Time = new Date(article2.featuredAt);
  if (article2Time > article1Time) return 1;
  if (article2Time < article1Time) return -1;
  return 0;
};

const getTopArticlesByDate = articles => articles.sort(sortByDate).splice(0, Math.min(numArticlesForSchema, articles.length));

const getSchema = (topArticles, publisherName, logoUrl) => ({
  '@context': 'http://schema.org',
  '@type': 'ItemList',
  itemListElement: topArticles.map((item, position) => {
    const imageUrl = getSrcSet({
      x: 16,
      y: 9
    }, item.image.host, item.image.path).large;
    return {
      '@type': 'NewsArticle',
      position: position + 1,
      url: item.articleUrl,
      headline: item.title,
      image: imageUrl,
      datePublished: item.featuredAt,
      author: {
        '@type': 'Person',
        name: item.author.name
      },
      publisher: {
        '@type': 'Organization',
        name: publisherName,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      }
    };
  })
});

const ItemsListSchema = ({
  itemListElement,
  publisherName,
  logoUrl
}) => {
  const topArticles = getTopArticlesByDate(itemListElement);
  const scriptContent = getSchema(topArticles, publisherName, logoUrl);
  return createElement(Helmet, null, createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(scriptContent)));
};

const MetaHeaderTags = ({
  canonicalUrl,
  favicon,
  description,
  facebookAppId,
  facebookPageId,
  title,
  twitterAccount,
  siteName,
  pageImage,
  articles,
  logoUrl,
  ampURL
}) => {
  return createElement(Fragment, null, createElement(PageDescription, {
    pageUrl: canonicalUrl,
    favicon: favicon,
    description: description,
    title: title,
    ampURL: ampURL
  }), createElement(FacebookMeta, {
    appId: facebookAppId,
    pageId: facebookPageId
  }), createElement(OpenGraph, {
    pageUrl: canonicalUrl,
    image: pageImage,
    title: title,
    siteName: siteName
  }), createElement(TwitterShare, {
    title: title,
    description: description,
    account: twitterAccount,
    image: pageImage,
    pageUrl: canonicalUrl
  }), createElement(ItemsListSchema, {
    publisherName: siteName,
    logoUrl: logoUrl,
    itemListElement: articles
  }));
};

const dateToIsoDate = date => {
  return new Date(date).toISOString();
};

const getAuthorsListSchema = authors => {
  if (authors.length === 1) {
    return {
      '@type': 'Person',
      name: authors[0].name
    };
  }

  return authors.map(author => ({
    '@type': 'Person',
    name: author.name
  }));
};

const getSchemaData = (canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical
    },
    headline: title,
    image,
    datePublished: dateToIsoDate(datePublished),
    dateModified: dateToIsoDate(dateModified),
    author: getAuthorsListSchema(authors),
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl
      }
    }
  };
};
const GoogleNewsSchema = props => {
  const {
    canonical,
    title,
    image,
    datePublished,
    authors,
    publisherName,
    logoUrl,
    dateModified
  } = props;
  const schema = getSchemaData(canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified);
  return createElement(Helmet$1, null, createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(schema)));
};

const NoIndexMeta = props => {
  const {
    noIndex
  } = props;
  return noIndex ? createElement(Helmet$1, null, createElement("meta", {
    name: "robots",
    content: "noindex"
  })) : null;
};

const RobotsMeta = () => createElement(Helmet$1, null, createElement("meta", {
  name: "robots",
  content: "max-snippet:70, max-image-preview:large,max-video-preview:20"
}));
const PostMetaHeaderTags = ({
  canonicalUrl,
  favicon,
  description,
  facebookAppId,
  facebookPageId,
  title,
  twitterAccount,
  siteName,
  pageImage,
  logoUrl,
  authors,
  createdAt,
  updatedAt,
  noIndex,
  ampURL
}) => {
  const imageUrl = getSrcSet({
    x: 16,
    y: 9
  }, pageImage.host, pageImage.path).large;
  return createElement(Fragment, null, createElement(PageDescription, {
    pageUrl: canonicalUrl,
    favicon: favicon,
    description: description,
    title: title,
    ampURL: ampURL
  }), createElement(NoIndexMeta, {
    noIndex: noIndex
  }), createElement(FacebookMeta, {
    appId: facebookAppId,
    pageId: facebookPageId
  }), createElement(OpenGraph, {
    pageUrl: canonicalUrl,
    image: imageUrl,
    title: title,
    siteName: siteName
  }), createElement(TwitterShare, {
    title: title,
    description: description,
    account: twitterAccount,
    image: imageUrl,
    pageUrl: canonicalUrl
  }), createElement(GoogleNewsSchema, {
    canonical: canonicalUrl,
    title: title,
    image: imageUrl,
    datePublished: createdAt,
    dateModified: updatedAt,
    authors: authors,
    publisherName: siteName,
    logoUrl: logoUrl
  }), createElement(RobotsMeta, null));
};

class ErrorBoundary extends Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      hasError: false
    });
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true
    };
  }

  componentDidCatch(error, info) {
    const errorReporter = this.context;
    errorReporter.reportException(error, info);
  }

  render() {
    const {
      hasError
    } = this.state;
    const {
      children
    } = this.props;
    return hasError ? createElement("div", {
      className: "has-error"
    }) : createElement(Fragment, null, children);
  }

}

_defineProperty(ErrorBoundary, "contextType", ErrorReporting);

function createScriptTag({
  type,
  source,
  async,
  name,
  attributes = {}
}) {
  if (type === 'URL' && source.length > 0) {
    return createElement("script", _extends({
      async: async,
      src: source,
      type: "text/javascript",
      key: name
    }, attributes));
  } // TODO - add html sanitization in the builder side or here...


  if (type === 'code' && source.length > 0) {
    return createElement("script", _extends({
      type: "text/javascript",
      key: name
    }, attributes), source);
  }

  return null;
}

const ExternalScriptsComponent = ({
  scripts
}) => {
  return createElement(Helmet$1, null, scripts.map(createScriptTag));
};
const ExternalScripts = props => createElement(ErrorBoundary, null, createElement(ExternalScriptsComponent, props));

const ellipsify = lines => {
  return {
    style: {
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkitLineClamp': `${lines}`,
      '-webkitBoxOrient': 'vertical'
    }
  };
};

const CARD_CAPTION_CONTAINER_TYPES = {
  HORIZONTAL: 'horizontal',
  ON_TOP: 'onTop',
  VERTICAL: 'vertical',
  DEFAULT: 'default'
};
const componentConfigThemeKey = 'cardCaptionContainer';

class CardCaptionContainerComponent extends Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "createStyles", vrLineHeight => StyleSheet.create({
      [CARD_CAPTION_CONTAINER_TYPES.DEFAULT]: {
        overflow: 'hidden'
      },
      [CARD_CAPTION_CONTAINER_TYPES.ON_TOP]: {
        background: 'transparent',
        position: 'absolute',
        bottom: 0,
        [MEDIA_BREAKPOINTS.X_LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
        }
      },
      [CARD_CAPTION_CONTAINER_TYPES.HORIZONTAL]: {
        flex: '1 1 66%',
        position: 'relative',
        [MEDIA_BREAKPOINTS.X_LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
          '-webkitLineClamp': '2'
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
          '-webkitLineClamp': '3',
          flex: '0 0 66.66%'
        }
      },
      [CARD_CAPTION_CONTAINER_TYPES.VERTICAL]: {
        [MEDIA_BREAKPOINTS.X_LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.LARGE]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
        }
      }
    }));
  }

  render() {
    const {
      numberOfLines,
      children,
      type,
      vrLineHeight
    } = this.props;
    const cardStyles = this.createStyles(vrLineHeight); // @ts-ignore TS2345

    const {
      style
    } = StyleSheet.create(ellipsify(numberOfLines));
    return createElement("div", {
      className: css(style, cardStyles[CARD_CAPTION_CONTAINER_TYPES.DEFAULT], cardStyles[type])
    }, children);
  }

}

const mapThemeProps$1 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const CardCaptionContainer = props => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps$1);
  return createElement(CardCaptionContainerComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$1 = 'decorationLine';

const getDecorationLineStyle = cardDecorationColor => {
  return StyleSheet.create({
    style: {
      height: '2px',
      width: '100%',
      backgroundColor: cardDecorationColor
    }
  });
};

const DecorationLineComponent = props => {
  const {
    cardDecorationColor
  } = props;
  const decorationLineStyle = getDecorationLineStyle(cardDecorationColor);
  return createElement("div", {
    className: css(decorationLineStyle.style)
  });
};

const mapThemeToProps = theme => ({
  cardDecorationColor: theme.cardDecorationColor
});

const DecorationLine = props => {
  const themeContext = useTheme(componentConfigurationKey$1, mapThemeToProps);
  return createElement(DecorationLineComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$1 = 'playIcon';

const playIconStyle = ({
  vrLineHeight,
  lineColor,
  fillColor
}) => StyleSheet.create({
  icon: {
    height: '15%',
    minHeight: '24px',
    width: 'auto',
    position: 'absolute',
    fill: lineColor,
    opacity: 0.75,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      top: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.75}px`,
      left: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.75}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      top: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.75}px`,
      left: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.75}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      top: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.75}px`,
      left: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.75}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      top: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.75}px`,
      left: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.75}px`
    }
  },
  path: {
    fill: fillColor
  }
});

const PlayIconComponent = props => {
  const style = playIconStyle(props);
  return React__default.createElement("svg", {
    className: css(style.icon),
    viewBox: "0 0 36 36",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "Layer_2",
    "data-name": "Layer 2"
  }, React__default.createElement("g", {
    id: "Layer_1-2",
    "data-name": "Layer 1"
  }, React__default.createElement("path", {
    className: css(style.path),
    d: "M18,2A16,16,0,1,0,34,18,16,16,0,0,0,18,2Zm7.58,16.74-5.16,3.07-5.17,3.07A.83.83,0,0,1,14,24.14V11.86a.83.83,0,0,1,1.25-.74l5.17,3.07,5.16,3.07A.86.86,0,0,1,25.58,18.74Z"
  }), React__default.createElement("path", {
    d: "M18,2A16,16,0,1,1,2,18,16,16,0,0,1,18,2M14.84,25a.81.81,0,0,0,.41-.12l5.17-3.07,5.16-3.07a.86.86,0,0,0,0-1.48l-5.16-3.07-5.17-3.07a.81.81,0,0,0-.41-.12.85.85,0,0,0-.84.86V24.14a.85.85,0,0,0,.84.86M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0ZM16,22.11V13.89l3.4,2L22.92,18,19.4,20.09l-3.4,2Z"
  }))));
};

const mapThemeProps$2 = theme => ({
  vrLineHeight: theme.vrLineHeight,
  lineColor: theme.lineColor,
  fillColor: theme.fillColor
});

const PlayIcon = () => {
  const themeContext = useTheme(componentConfigThemeKey$1, mapThemeProps$2);
  return React__default.createElement(PlayIconComponent, themeContext);
};

const style = StyleSheet.create({
  videoCoverWrap: {
    position: 'relative',
    display: 'flex'
  }
});
const VideoCoverWrapper = ({
  children
}) => createElement("div", {
  className: css(style.videoCoverWrap)
}, children, createElement(PlayIcon, null));
const ConditionalVideoWrap = ({
  shouldWarp,
  children
}) => shouldWarp ? createElement(VideoCoverWrapper, null, children) : createElement(Fragment, null, children);

const createStyle = () => StyleSheet.create({
  wrapper: {
    display: 'flex',
    width: '100%',
    height: '100%'
  }
});

const WrapperForSafariBorderRadiusBug = ({
  surfaceStyle,
  children
}) => {
  const style = createStyle();
  return createElement("div", {
    className: css(StyleSheet.create({
      surfaceStyle
    }).surfaceStyle, style.wrapper)
  }, children);
};

const intersectionObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: [0, 0.05, 0.1]
};
const createIntersectionObserver = (callback, target) => {
  // @ts-ignore
  const observer = new window.IntersectionObserver(callback, intersectionObserverOptions);

  if (target) {
    observer.observe(target);
  }
};

const placeholderStyle = StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.2s ease-in-out',
    willChange: 'opacity, transform'
  },
  initial: {
    opacity: 1
  },
  ready: {
    opacity: 0
  }
});
const defaultBlurRadius = 40;
class BlurredImageComponent extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "drawImageOnCanvas", event => {
      const {
        blur
      } = this.props;
      const ctx = this.getCanvas().getContext('2d');
      const {
        width,
        height
      } = this.getDimensions();
      this.setCanvasDimensions(width, height);
      ctx.filter = `blur(${blur}px)`;
      ctx.drawImage(event.target, 0, 0, width, height);
    });

    _defineProperty(this, "removeCanvas", () => {
      this.setState({
        shouldShowCanvas: false
      });
    });

    _defineProperty(this, "img", void 0);

    _defineProperty(this, "canvasRef", void 0);

    this.state = {
      shouldShowCanvas: true
    };
    this.canvasRef = createRef();
  }

  componentDidMount() {
    this.createImage();
    const {
      getRefCallback
    } = this.props;

    if (typeof getRefCallback === 'function') {
      getRefCallback(this.canvasRef);
    }
  }

  componentWillUnmount() {
    if (this.img) {
      this.img.onload = null;
    }
  }

  getCanvas() {
    return this.canvasRef.current;
  }

  getDimensions() {
    return this.getCanvas().getBoundingClientRect();
  }

  setCanvasDimensions(width, height) {
    this.getCanvas().width = width;
    this.getCanvas().height = height;
  }

  createImage() {
    const {
      src
    } = this.props;
    this.img = new window.Image();
    this.img.onload = this.drawImageOnCanvas;
    this.img.src = src;
  }

  render() {
    const {
      status
    } = this.props;
    const {
      shouldShowCanvas
    } = this.state;
    return shouldShowCanvas ? createElement("canvas", {
      className: css(placeholderStyle.style, placeholderStyle[status]),
      ref: this.canvasRef,
      onTransitionEnd: this.removeCanvas
    }) : null;
  }

}
const BlurredImage = BlurredImageComponent;

const EagerImage = props => {
  const {
    imageUrl,
    srcSet,
    sizes,
    alt,
    title
  } = props;
  return createElement("img", {
    className: css(imageStyle.base),
    srcSet: srcSet,
    sizes: sizes,
    src: imageUrl,
    alt: alt,
    title: title
  });
};

const statusLabels = {
  initial: 'initial',
  ready: 'ready'
};
const lazyImageStyle = StyleSheet.create({
  style: {
    willChange: 'opacity, transform'
  },
  initial: {
    opacity: 0
  },
  ready: {
    opacity: 1
  }
});
class LazyImageComponent extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "observer", null);

    _defineProperty(this, "getBlurredImageRef", ref => {
      this.blurRef = ref;
      this.observer = this.createIntersectionObserver();
    });

    _defineProperty(this, "intersectionCallback", entries => entries.filter(entry => entry.intersectionRatio > 0).forEach(() => this.loadImage()));

    _defineProperty(this, "blurRef", void 0);

    _defineProperty(this, "imageRef", void 0);

    this.imageRef = createRef();
    this.blurRef = createRef();
    this.state = {
      status: statusLabels.initial
    };
  }

  componentWillUnmount() {
    this.observer = this.stopObservingIntersections();
  }

  getImageStyle() {
    const {
      status
    } = this.state;
    return css(imageStyle.base, lazyImageStyle[status], lazyImageStyle.style);
  }

  getBlurRadius() {
    return defaultBlurRadius;
  }

  createIntersectionObserver() {
    return createIntersectionObserver(this.intersectionCallback, this.blurRef.current);
  }

  stopObservingIntersections() {
    if (this.observer && this.blurRef.current) {
      this.observer.unobserve(this.blurRef.current);
    }
  }

  loadImage() {
    const {
      srcSet,
      imageUrl
    } = this.props;
    const img = this.imageRef.current;

    img.onload = () => this.setState({
      status: statusLabels.ready
    });

    this.stopObservingIntersections();
    img.srcset = srcSet;
    img.src = imageUrl;
  }

  render() {
    const {
      status
    } = this.state;
    const {
      sizes,
      alt,
      title,
      lowResUrl
    } = this.props;
    return createElement(Fragment, null, createElement(BlurredImage, {
      src: lowResUrl,
      blur: this.getBlurRadius(),
      getRefCallback: this.getBlurredImageRef,
      status: status
    }), createElement("img", {
      className: this.getImageStyle(),
      ref: this.imageRef,
      alt: alt,
      title: title,
      sizes: sizes
    }), createElement("noscript", null, createElement(EagerImage, this.props)));
  }

}
const LazyImage = LazyImageComponent;

const setEventEmitter = (eventName, eventHandler) => {
  if (window.__mmClientApiSubscriptions__ && window.__mmClientApiSubscriptions__.get(eventName)) {
    window.__mmClientApiSubscriptions__.get(eventName).forEach(params => eventHandler(params));
  }
};

const options = {
  root: null,
  threshold: 0.5
};

const getBlockIndex = block => {
  const blocks = Array.prototype.slice.call(document.querySelectorAll('.content-block'));

  for (let i = 0; i < blocks.length; i += 1) {
    if (blocks[i] === block) return i;
  }

  return -1;
};

const getBlockDataId = block => {
  return block.getAttribute('data-id');
};

const createObserverCallback = templateName => (e, observer) => {
  e.forEach(entry => {
    if (entry.isIntersecting) {
      observer.unobserve(entry.target);
      const index = getBlockIndex(entry.target);
      const id = getBlockDataId(entry.target);
      setEventEmitter('viewabilityArticleBlockEvent', callback => callback({
        id,
        index,
        templateName
      }));
    }
  });
};

const ViewabilityHandlerContext = createContext({});
const useViewabilityHandler = () => {
  const ref = useRef(null);
  const context = useContext(ViewabilityHandlerContext);
  const {
    intersectionObserver
  } = context;
  useEffect(() => {
    if (!ref.current || !intersectionObserver) return;
    const element = ref.current;
    element.classList.add('content-block');
    intersectionObserver.observe(element);
  }, [ref]);
  return ref;
};
const createViewabilityHandlerContext = templateName => {
  if (typeof IntersectionObserver === 'undefined') return {};
  const viewabilityIntersectionObserver = new IntersectionObserver(createObserverCallback(templateName), options);
  return {
    intersectionObserver: viewabilityIntersectionObserver
  };
};

const useViewabilityCallback = dataId => {
  useEffect(() => {
    if (dataId) {
      window.mmClientApi = window.mmClientApi || [];
      const {
        mmClientApi
      } = window;
      mmClientApi.push('viewabilityArticleBlockEvent', htmlData => {
        if (htmlData.id === dataId) {
          mmClientApi.push('analytics', {
            event_category: htmlData.templateName,
            event_action: 'scroll depth',
            event_label: `block position=${htmlData.index}`
          });
        }
      });
    }
  }, [dataId]);
};

const componentConfigThemeKey$2 = 'imageWrapper';
const getStyle = (aspectRatio, backgroundColor) => StyleSheet.create({
  base: {
    backgroundColor,
    position: 'relative',
    overflow: 'hidden',
    margin: 0,
    boxSizing: 'border-box',
    flex: '1 1 100%',
    minWidth: '0%',
    paddingTop: `calc(100% / (${aspectRatio.x / aspectRatio.y}))`
  }
});
const ImageWrapperComponent = ({
  additionalStyle,
  aspectRatio,
  backgroundColor,
  dataId,
  children
}) => {
  useViewabilityCallback(dataId);
  const imageWrapperStyle = getStyle(aspectRatio, backgroundColor);
  return additionalStyle ? createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, createElement("div", {
    className: css(imageWrapperStyle.base)
  }, children)) : createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(imageWrapperStyle.base)
  }, children);
};

const mapThemeProps$3 = theme => {
  return {
    backgroundColor: theme.backgroundColor
  };
};

const ImageWrapper = props => {
  const themeContext = useTheme(componentConfigThemeKey$2, mapThemeProps$3);
  return createElement(ErrorBoundary, null, createElement(ImageWrapperComponent, _extends({}, themeContext, props)));
};

const Image = props => {
  const {
    description,
    host,
    path,
    aspectRatio = {
      x: 16,
      y: 9
    },
    additionalStyle,
    lazyLoad,
    dataId
  } = props;
  const CalculatedImage = lazyLoad ? LazyImage : EagerImage;
  return createElement(ImageWrapper, {
    aspectRatio: aspectRatio,
    additionalStyle: additionalStyle,
    dataId: dataId
  }, createElement(CalculatedImage, getCalculatedThumbnail(aspectRatio, description, host, path)));
};

let DIVIDED_CARD_TYPES;

(function (DIVIDED_CARD_TYPES) {
  DIVIDED_CARD_TYPES["HORIZONTAL"] = "HORIZONTAL";
  DIVIDED_CARD_TYPES["VERTICAL"] = "VERTICAL";
})(DIVIDED_CARD_TYPES || (DIVIDED_CARD_TYPES = {}));

const getHorizontalCardSmallScreenHeight = vrLineHeight => {
  return vrLineHeight ? {
    height: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 4}px`
  } : '';
};

class DividedCardComponent extends Component {
  createCardStyles(cardBackgroundColor, cardTextColor, vrLineHeight) {
    return StyleSheet.create({
      text: {
        overflow: 'hidden'
      },
      dividedCard: {
        width: '100%',
        height: '100%',
        textDecoration: 'none',
        color: cardTextColor,
        backgroundColor: cardBackgroundColor,
        '-webkit-mask-image': '-webkit-radial-gradient(white, black)'
      },
      [DIVIDED_CARD_TYPES.HORIZONTAL]: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        [MEDIA_BREAKPOINTS.SMALL]: getHorizontalCardSmallScreenHeight(vrLineHeight)
      },
      [DIVIDED_CARD_TYPES.VERTICAL]: {
        display: 'inline-block'
      }
    });
  }

  render() {
    const {
      title,
      image,
      articleUrl,
      coverType,
      dividedCardType,
      numberOfLines = 2,
      cardBackgroundColor,
      cardTextColor,
      surfaceStyle,
      showDecorationLine = false,
      vrLineHeight = null
    } = this.props;
    const cardStyles = this.createCardStyles(cardBackgroundColor, cardTextColor, vrLineHeight);
    return createElement(WrapperForSafariBorderRadiusBug, {
      surfaceStyle: surfaceStyle
    }, createElement(Link, {
      className: css(cardStyles.dividedCard, cardStyles[dividedCardType], StyleSheet.create({
        surfaceStyle
      }).surfaceStyle),
      href: articleUrl
    }, createElement(ConditionalVideoWrap, {
      shouldWarp: coverType === 'video'
    }, createElement(Image, _extends({}, image, {
      lazyLoad: true
    }))), showDecorationLine ? createElement(DecorationLine, null) : null, createElement(CardCaptionContainer, {
      numberOfLines: numberOfLines,
      type: CARD_CAPTION_CONTAINER_TYPES[dividedCardType]
    }, createElement("h3", {
      className: css(cardStyles.text)
    }, title))));
  }

}

const numberOfLines = 3;
const componentConfigThemeKey$3 = 'verticalCard';

const mapThemeProps$4 = theme => {
  return {
    cardBackgroundColor: theme.cardBackgroundColor,
    cardTextColor: theme.cardTextColor,
    cardTextTypography: theme.cardTextTypography,
    cardDecorationColor: theme.cardDecorationColor,
    surfaceStyle: theme.surfaceStyle
  };
};

const VerticalCard = props => {
  const themeContext = useTheme(componentConfigThemeKey$3, mapThemeProps$4);
  return createElement(DividedCardComponent, _extends({}, themeContext, props, {
    dividedCardType: DIVIDED_CARD_TYPES.VERTICAL,
    showDecorationLine: true,
    numberOfLines: numberOfLines
  }));
};

const componentConfigurationKey$2 = 'scrollableArticleList';

const createStyles$1 = vrLineHeight => StyleSheet.create({
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1200px'
  },
  scrollContainer: {
    '-webkit-overflow-scrolling': 'touch',
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    overflowY: 'visible',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      overflow: 'visible',
      justifyContent: 'center'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    },
    '::-webkit-scrollbar': {
      display: 'none'
    }
  },
  scrollItem: {
    flex: '0 0 auto',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
    },
    position: 'relative',
    boxSizing: 'border-box',
    '@media (max-width: 330px)': {
      flexBasis: '85%'
    },
    ':first-child': {
      marginLeft: 0
    },
    ':last-child': {
      marginRight: 0
    }
  }
});

const ScrollableArticleListComponent = ({
  cardsProps,
  vrLineHeight
}) => {
  const styles = createStyles$1(vrLineHeight);

  const createCard = props => {
    const {
      id
    } = props;
    return createElement("div", {
      className: css(styles.scrollItem),
      key: id
    }, createElement(VerticalCard, props));
  };

  return createElement("div", {
    className: css(styles.listWrapper)
  }, createElement("div", {
    className: css(styles.scrollContainer)
  }, cardsProps.map(createCard)));
};
ScrollableArticleListComponent.defaultProps = {
  cardsProps: []
};

const mapThemeProps$5 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const ScrollableArticleList = props => {
  const themeContext = useTheme(componentConfigurationKey$2, mapThemeProps$5);
  return createElement(ErrorBoundary, null, createElement(ScrollableArticleListComponent, _extends({}, themeContext, props)));
};

const numberOfLines$1 = 3;
const componentConfigThemeKey$4 = 'horizontalCard';

const mapThemeProps$6 = theme => {
  return {
    cardBackgroundColor: theme.cardBackgroundColor,
    cardTextColor: theme.cardTextColor,
    cardTextTypography: theme.cardTextTypography,
    surfaceStyle: theme.surfaceStyle,
    cardDecorationColor: theme.cardBackgroundColor,
    vrLineHeight: theme.vrLineHeight
  };
};

const HorizontalCardComponent = props => createElement(DividedCardComponent, _extends({}, props, {
  dividedCardType: DIVIDED_CARD_TYPES.HORIZONTAL,
  numberOfLines: numberOfLines$1
}));

const HorizontalCard = props => {
  const themeContext = useTheme(componentConfigThemeKey$4, mapThemeProps$6);
  return createElement(HorizontalCardComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$5 = 'oneThreeArticleList';

const createStyle$1 = vrLineHeight => StyleSheet.create({
  container: {
    display: 'grid',
    gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    gridTemplateRows: '1fr',
    gridTemplateColumns: '1fr 1fr',
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridColumnGap: '0',
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
      gridTemplateRows: 'auto',
      gridTemplateColumns: '1fr'
    }
  },
  horizontalCardsWrapper: {
    position: 'relative'
  },
  horizontalCardsContainer: {
    maxHeight: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr 1fr 1fr',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
    }
  },
  horizontalCardWrapper: {
    minWidth: '0',
    minHeight: '0',
    boxSizing: 'border-box',
    padding: '0.1px',
    position: 'relative',
    ':last-child': {
      marginBottom: '0'
    }
  }
});

const OneThreeArticleListComponent = ({
  cardsProps,
  vrLineHeight
}) => {
  const [highlight, ...moreCards] = cardsProps;
  const style = createStyle$1(vrLineHeight);

  const createCard = (cardProps, i) => createElement("div", {
    className: css(style.horizontalCardWrapper),
    key: i
  }, createElement(HorizontalCard, cardProps));

  return createElement("div", {
    className: css(style.container)
  }, createElement(VerticalCard, highlight), createElement("div", {
    className: css(style.horizontalCardsWrapper)
  }, createElement("div", {
    className: css(style.horizontalCardsContainer)
  }, moreCards.map(createCard))));
};
OneThreeArticleListComponent.defaultProps = {
  cardsProps: []
};

const mapThemeToProps$1 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const OneThreeArticleList = props => {
  const themeContext = useTheme(componentConfigThemeKey$5, mapThemeToProps$1);
  return createElement(ErrorBoundary, null, createElement(OneThreeArticleListComponent, _extends({}, themeContext, props)));
};

const createStyles$2 = (borderColor, linksColor, linkHoverColor, vrLineHeight) => StyleSheet.create({
  sectionItem: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,

      /*
        width is calculation of 50% - margins. The list is 200% width
        therefore to fit the visual container we set the item width to 50%
       */
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px 0`,
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      width: `calc(50% - ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px)`
    },
    borderTop: `1px solid ${borderColor}`,
    flex: '0 0 auto',
    boxSizing: 'border-box',
    position: 'relative',
    ':first-child': {
      borderTop: 'none'
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    }
  },
  link: {
    textDecoration: 'none',
    boxSizing: 'border-box',
    color: linksColor,
    ':visited': {
      color: linksColor,
      textDecoration: 'none'
    },
    ':active': {
      color: linksColor,
      textDecoration: 'none'
    },
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none'
      }
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none'
      }
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      '-webkitLineClamp': '2',
      ':hover': {
        color: 'initial'
      }
    }
  }
});

const componentConfigurationKey$3 = 'listItem';
const ListItemComponent = ({
  borderColor,
  linkColor,
  linkHoverColor,
  vrLineHeight,
  title,
  articleUrl,
  numberOfLines = 2
}) => {
  const styles = createStyles$2(borderColor, linkColor, linkHoverColor, vrLineHeight); // @ts-ignore TS2345

  const {
    style
  } = StyleSheet.create(ellipsify(numberOfLines));
  return createElement(Link, {
    href: articleUrl,
    className: css(styles.link, styles.sectionItem),
    key: articleUrl
  }, createElement("h4", {
    className: css(style)
  }, title));
};

const mapThemeProps$7 = theme => ({
  borderColor: theme.borderColor,
  linkColor: theme.linkColor,
  linkHoverColor: theme.linkHoverColor,
  vrLineHeight: theme.vrLineHeight
});

const ListItem = props => {
  const themeContext = useTheme(componentConfigurationKey$3, mapThemeProps$7);
  return createElement(ListItemComponent, _extends({}, themeContext, props));
};

const LIST_WRAPPER_STYLE = {
  flex: '1',
  position: 'relative'
};
const LIST_STYLE = {
  position: 'absolute',
  top: '0',
  left: '0',
  bottom: '0',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  width: '200%',
  // we preserve width for two columns here to be able to hide wrapping children
  minWidth: '200%'
};

const componentConfigurationKey$4 = 'listWithHeading';

const createStyles$3 = (titleColor, backgroundColor, vrLineHeight) => StyleSheet.create({
  listWrapper: { ...LIST_WRAPPER_STYLE,
    backgroundColor
  },
  list: { ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.SMALL]: {
      position: 'relative'
    }
  },
  title: {
    color: titleColor,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
    }
  }
});

const ListSectionComponent = ({
  listProps,
  displayName,
  titleColor,
  backgroundColor,
  vrLineHeight,
  surfaceStyle,
  itemNumLines
}) => {
  const styles = createStyles$3(titleColor, backgroundColor, vrLineHeight);

  const createHeading = () => createElement("h1", {
    className: css(styles.title)
  }, displayName);

  return createElement("div", {
    className: css(StyleSheet.create({
      surfaceStyle
    }).surfaceStyle, styles.listWrapper)
  }, createElement(DecorationLine, null), createElement("div", {
    className: css(styles.list)
  }, createHeading(), listProps.map((listItemProps, index) => createElement(ListItem, _extends({}, listItemProps, {
    numberOfLines: itemNumLines,
    key: index
  })))));
};
ListSectionComponent.defaultProps = {
  listProps: [],
  displayName: '',
  itemNumLines: 1
};

const mapThemeToProps$2 = theme => ({
  titleColor: theme.titleColor,
  backgroundColor: theme.backgroundColor,
  vrLineHeight: theme.vrLineHeight,
  surfaceStyle: theme.surfaceStyle
});

const ListWithHeading = props => {
  const themeContext = useTheme(componentConfigurationKey$4, mapThemeToProps$2);
  return createElement(ListSectionComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$6 = 'cardOverlay';
const hex2rgba = (hex, alpha = 1) => {
  // @ts-ignore
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const CardOverlayStyles = cardOverlayColor => {
  return StyleSheet.create({
    style: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      bottom: '0px',
      background: `linear-gradient(0deg, ${hex2rgba(cardOverlayColor, 0.7)} 0%, rgba(0,0,0,0) 50%)`
    }
  });
};

class CardOverlayComponent extends Component {
  render() {
    const {
      cardOverlayColor
    } = this.props;
    const cardOverlayStyle = CardOverlayStyles(cardOverlayColor);
    return createElement("div", {
      className: css(cardOverlayStyle.style)
    });
  }

}

const mapThemeProps$8 = theme => ({
  cardOverlayColor: theme.cardOverlayColor
});

const CardOverlay = props => {
  const themeContext = useTheme(componentConfigThemeKey$6, mapThemeProps$8);
  return createElement(CardOverlayComponent, _extends({}, themeContext, props));
};

const numberOfLines$2 = 2;
const componentConfigThemeKey$7 = 'onTopCard';

const createCardStyles = cardTextColor => StyleSheet.create({
  card: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: cardTextColor,
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)'
  }
});

const OnTopCardComponent = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    cardTextColor,
    surfaceStyle,
    lazyLoad = false
  } = props;
  const cardStyles = createCardStyles(cardTextColor);
  return createElement(WrapperForSafariBorderRadiusBug, {
    surfaceStyle: surfaceStyle
  }, createElement(Link, {
    className: css(cardStyles.card, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle),
    href: articleUrl
  }, createElement(Image, _extends({}, image, {
    lazyLoad: lazyLoad
  })), createElement(CardOverlay, null), createElement(CardCaptionContainer, {
    numberOfLines: numberOfLines$2,
    type: CARD_CAPTION_CONTAINER_TYPES.ON_TOP
  }, createElement("h3", null, title)), coverType === 'video' ? createElement(PlayIcon, null) : null));
};

const mapThemeProps$9 = theme => {
  return {
    cardTextColor: theme.cardTextColor,
    cardTextTagName: theme.cardTextTagName,
    surfaceStyle: theme.surfaceStyle
  };
};

const OnTopCard = props => {
  const themeContext = useTheme(componentConfigThemeKey$7, mapThemeProps$9);
  return createElement(OnTopCardComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$5 = 'headlinesArticleList';

const createStyles$4 = vrLineHeight => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
    }
  },
  cardWrapper: {
    boxSizing: 'border-box',
    paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flex: '0 0 66.67%'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 0 66.67%'
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 0 66.67%'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingRight: '0',
      flex: '0 0 100%',
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px`
    }
  },
  listWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flex: '0 1 33.33%'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 1 33.33%'
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flex: '0 1 33.33%'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingLeft: '0',
      flex: '0 1 100%',
      paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px`
    }
  }
});

const HeadlinesArticleListComponent = ({
  cardsProps,
  displayName,
  vrLineHeight
}) => {
  const styles = createStyles$4(vrLineHeight);
  const [onTopCardProps, ...listProps] = cardsProps;
  return createElement("div", {
    className: css(styles.container)
  }, createElement("div", {
    className: css(styles.cardWrapper)
  }, createElement(OnTopCard, onTopCardProps)), createElement("div", {
    className: css(styles.listWrapper)
  }, createElement(ListWithHeading, {
    listProps: listProps,
    displayName: displayName,
    itemNumLines: 2
  })));
};

const mapThemeToProps$3 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const HeadlinesArticleList = props => {
  const themeContext = useTheme(componentConfigurationKey$5, mapThemeToProps$3);
  return createElement(ErrorBoundary, null, createElement(HeadlinesArticleListComponent, _extends({}, themeContext, props)));
};

const sectionStyle = {
  maxWidth: '1200px',
  margin: '0 auto'
};
const SectionTag = ({
  children
}) => {
  return React__default.createElement("section", {
    className: css(StyleSheet.create({
      sectionStyle
    }).sectionStyle)
  }, children);
};
const Section = SectionTag;

const componentConfigThemeKey$8 = 'sixSixArticleList';

const isNewChunk = (index, chunkSize) => index % chunkSize === 0;

const getArticlesInChunks = (articles, chunkSize) => {
  let currentChunk = [];
  return articles.reduce((chunks, item, index) => {
    if (isNewChunk(index, chunkSize)) {
      currentChunk = [item];
      chunks.push(currentChunk);
    } else {
      currentChunk.push(item);
    }

    return chunks;
  }, []);
};

const getSixSixArticleListStyle = vrLineHeight => {
  return StyleSheet.create({
    poop: {
      RUBBISH: 'EXCRAMENT'
    },
    verticalCardsGrid: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridTemplateColumns: '1fr 1fr 1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: '1fr 1fr 1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
        gridTemplateColumns: '1fr 1fr',
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
        gridTemplateColumns: '1fr',
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    },
    horizontalCardsGrid: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    }
  });
};

const SixSixArticleListComponent = ({
  cardsProps,
  order = 'vertical-horizontal',
  vrLineHeight
}) => {
  const SixSixArticleListStyle = getSixSixArticleListStyle(vrLineHeight);
  const firstSectionStyle = order === 'vertical-horizontal' ? SixSixArticleListStyle.verticalCardsGrid : SixSixArticleListStyle.horizontalCardsGrid;
  const secondSectionStyle = order === 'vertical-horizontal' ? SixSixArticleListStyle.horizontalCardsGrid : SixSixArticleListStyle.verticalCardsGrid;
  const FirstSectionCard = order === 'vertical-horizontal' ? VerticalCard : HorizontalCard;
  const SecondSectionCard = order === 'vertical-horizontal' ? HorizontalCard : VerticalCard;

  const createGrids = (cards, index) => {
    return index % 2 === 0 ? createElement("div", {
      className: css(firstSectionStyle),
      key: index
    }, cards.map(props => createElement(FirstSectionCard, _extends({
      key: props.id
    }, props)))) : createElement("div", {
      className: css(secondSectionStyle),
      key: index
    }, cards.map(props => createElement(SecondSectionCard, _extends({
      key: props.id
    }, props))));
  };

  return createElement(Fragment, null, getArticlesInChunks(cardsProps, 6).map(createGrids));
};

const mapThemeToProps$4 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const SixSixArticleList = props => {
  const themeContext = useTheme(componentConfigThemeKey$8, mapThemeToProps$4);
  return createElement(ErrorBoundary, null, createElement(SixSixArticleListComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$6 = 'list';

const createStyles$5 = backgroundColor => StyleSheet.create({
  listWrapper: { ...LIST_WRAPPER_STYLE,
    backgroundColor
  },
  list: { ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.SMALL]: {
      position: 'relative'
    }
  }
});

const ListComponent = ({
  listProps,
  backgroundColor,
  surfaceStyle
}) => {
  const styles = createStyles$5(backgroundColor);
  return createElement("div", {
    className: css(StyleSheet.create({
      surfaceStyle
    }).surfaceStyle, styles.listWrapper)
  }, createElement(DecorationLine, null), createElement("div", {
    className: css(styles.list)
  }, listProps.map((listItemProps, index) => createElement(ListItem, _extends({}, listItemProps, {
    key: index
  })))));
};
ListComponent.defaultProps = {
  listProps: []
};

const mapThemeToProps$5 = theme => ({
  backgroundColor: theme.backgroundColor,
  surfaceStyle: theme.surfaceStyle
});

const List = props => {
  const themeContext = useTheme(componentConfigurationKey$6, mapThemeToProps$5);
  return createElement(ListComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$7 = 'highlightsArticleList';

const createStyles$6 = vrLineHeight => StyleSheet.create({
  container: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
    [MEDIA_BREAKPOINTS.LARGE]: {
      gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      gridTemplateColumns: '1fr 1fr',
      gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto auto',
      gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
    }
  }
});

const HighlightsArticleListComponent = ({
  cardsProps,
  vrLineHeight
}) => {
  const styles = createStyles$6(vrLineHeight);
  const [onTopCardProps, ...listProps] = cardsProps;
  return createElement("div", {
    className: css(styles.container)
  }, createElement(OnTopCard, onTopCardProps), createElement(List, {
    listProps: listProps
  }));
};

const mapThemeToProps$6 = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const HighlightsArticleList = props => {
  const themeContext = useTheme(componentConfigurationKey$7, mapThemeToProps$6);
  return createElement(ErrorBoundary, null, createElement(HighlightsArticleListComponent, _extends({}, themeContext, props)));
};

const style$1 = {
  '-webkit-font-smoothing': 'antialiased',
  margin: 0
};

const componentConfigThemeKey$9 = 'button';

const getStyle$1 = (backgroundColorEnabled, textColorEnabled, backgroundColorDisabled, textColorDisabled, vrLineHeight, disabled) => {
  return StyleSheet.create({
    buttonStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: disabled ? backgroundColorDisabled : backgroundColorEnabled,
      color: disabled ? textColorDisabled : textColorEnabled,
      cursor: disabled ? 'auto' : 'pointer',
      outline: 'none',
      border: 'none',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    }
  });
};

const ButtonComponent = props => {
  const {
    backgroundColorEnabled,
    textColorEnabled,
    backgroundColorDisabled,
    textColorDisabled,
    surfaceStyleEnabled,
    surfaceStyleDisabled,
    label,
    onClick,
    vrLineHeight,
    disabled = false
  } = props;
  const buttonStyle = getStyle$1(backgroundColorEnabled, textColorEnabled, backgroundColorDisabled, textColorDisabled, vrLineHeight, disabled);
  const surface = disabled ? surfaceStyleDisabled : surfaceStyleEnabled;
  return React__default.createElement("button", {
    type: "button",
    disabled: disabled,
    className: css(buttonStyle.buttonStyle, StyleSheet.create({
      surface
    }).surface, StyleSheet.create({
      style: style$1
    }).style),
    onClick: onClick
  }, label);
};

const mapThemeProps$a = theme => {
  return {
    backgroundColorEnabled: theme.backgroundColorEnabled,
    textColorEnabled: theme.textColorEnabled,
    backgroundColorDisabled: theme.backgroundColorDisabled,
    textColorDisabled: theme.textColorDisabled,
    surfaceStyleEnabled: theme.surfaceStyleEnabled,
    surfaceStyleDisabled: theme.surfaceStyleDisabled,
    vrLineHeight: theme.vrLineHeight
  };
};

const Button = props => {
  const themeContext = useTheme(componentConfigThemeKey$9, mapThemeProps$a);
  return React__default.createElement(ButtonComponent, _extends({}, themeContext, props));
};

const themeKeys = ['h1', 'h2', 'h3', 'h4', 'p', 'span', 'ul'];
let globalStyles = {
  h4: {},
  h3: {},
  p: {}
};

const removeUndefinedStyles = (reporter, styleObject) => {
  return Object.keys(styleObject).reduce((prevValue, key) => {
    if (typeof styleObject[key] !== 'undefined') {
      return { ...prevValue,
        [key]: styleObject[key]
      };
    }

    reporter.reportWarning(`Style objects in Theme should not contain undefined properties: key:${key} has undefined value`);
    return prevValue;
  }, {});
};

const constructTagStyle = (reporter, tagStyle) => {
  return { ...removeUndefinedStyles(reporter, tagStyle),
    ...style$1
  };
};

const getGeneratedGlobalStyles = () => {
  return {
    h3: () => ({
      style: globalStyles.h3
    }),
    h4: () => ({
      style: globalStyles.h4
    }),
    p: () => ({
      style: globalStyles.p
    })
  };
};
const generateGlobalStyles = (reporter, configuration) => {
  const styles = Object.keys(configuration).reduce((prevValue, tag) => {
    if (themeKeys.includes(tag)) {
      const {
        tagStyle
      } = configuration[tag];

      if (typeof tagStyle !== 'undefined') {
        return { ...prevValue,
          [tag]: constructTagStyle(reporter, tagStyle)
        };
      }
    }

    return prevValue;
  }, {});
  globalStyles = styles;
  return styles;
};

const GLOBALS = '__GLOBAL_STYLES__';
const globalExtension = {
  selectorHandler: (selector, baseSelector, generateSubtreeStyles) => {
    return baseSelector.includes(GLOBALS) ? generateSubtreeStyles(selector) : null;
  }
};

const normalizeCss = () => ({
  figure: {
    margin: 0
  },
  ul: {
    margin: 0,
    padding: 0
  },
  li: {
    margin: 0,
    padding: 0
  },
  img: {
    borderStyle: 'none'
  },
  h1: {
    fontSize: '2em',
    margin: '0.67em 0',
    marginBlockEnd: 0,
    marginBlockStart: 0
  },
  hr: {
    boxSizing: 'content-box',
    height: 0
  },
  pre: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  'abbr[title]': {
    borderBottom: 'none',
    textDecoration: 'underline'
  },
  strong: {
    fontWeight: 'bolder'
  },
  b: {
    fontWeight: 'bolder'
  },
  samp: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  kbd: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  code: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  small: {
    fontSize: '80%'
  },
  sup: {
    fontSize: '75%',
    lineHeight: 0,
    position: 'relative',
    verticalAlign: 'baseline',
    top: '-0.5em'
  },
  sub: {
    fontSize: '75%',
    lineHeight: 0,
    position: 'relative',
    verticalAlign: 'baseline',
    bottom: '-0.25em'
  },
  textarea: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  select: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0,
    textTransform: 'none'
  },
  button: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0,
    overflow: 'visible',
    textTransform: 'none',
    padding: 0
  },
  input: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  optgroup: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  'button:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="button"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="submit"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="reset"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  fieldset: {
    padding: '0.35em 0.75em 0.625em'
  },
  legend: {
    padding: 0
  },
  progress: {
    verticalAlign: 'baseline'
  },
  details: {
    display: 'block'
  },
  summary: {
    display: 'list-item'
  },
  template: {
    display: 'none'
  },
  '[hidden]': {
    display: 'none'
  }
});

const componentConfigurationKey$8 = 'mobileTopBar';
const MobileTopBarColorComponent = props => {
  const {
    backgroundColor
  } = props;
  return createElement(Helmet, null, createElement("meta", {
    name: "theme-color",
    content: backgroundColor
  }));
};

const mapThemeProps$b = theme => ({
  backgroundColor: theme.backgroundColor
});

const MobileTopBarColor = () => {
  const themeContext = useTheme(componentConfigurationKey$8, mapThemeProps$b);
  return createElement(ErrorBoundary, null, createElement(MobileTopBarColorComponent, themeContext));
};

/* eslint no-underscore-dangle: [1, { "allowAfterThis": true }] */
const extended = StyleSheet.extend([globalExtension]); // This code causes aphrodite to ignore its caching system, and to create a new style object with every render
// The code is necessary due to a bug in aphrodite - the extended stylesheet seems to have issues with multiple re-renders

const potentialMemoryLeak = prefix => {
  return prefix + Math.random().toString(36).substring(7);
};

const AppContent = props => {
  const reporter = useContext(ErrorReporting);
  const themeContext = useContext(ThemeContext);
  const globalStyles = generateGlobalStyles(reporter, themeContext);
  const normalizedCss = normalizeCss();
  const combinedStyles = Object.keys(normalizedCss).reduce((acc, key) => {
    return { ...acc,
      [key]: { ...acc[key],
        ...normalizedCss[key]
      }
    };
  }, globalStyles);
  const styles = extended.StyleSheet.create({
    [GLOBALS]: combinedStyles
  }); // eslint-disable-next-line no-underscore-dangle

  if (!styles[GLOBALS]._name) {
    // eslint-disable-next-line no-underscore-dangle
    styles[GLOBALS]._name = potentialMemoryLeak(GLOBALS);
  }

  try {
    extended.css(styles[GLOBALS]);
  } catch (e) {
    console.error(e);
  }

  const {
    children
  } = props;
  return createElement(Fragment, null, createElement(MobileTopBarColor, null), children);
};

/* eslint no-underscore-dangle: [1, { "allowAfterThis": true }] */
const extended$1 = StyleSheet.extend([globalExtension]); // This code causes aphrodite to ignore its caching system, and to create a new style object with every render
// The code is necessary due to a bug in aphrodite - the extended stylesheet seems to have issues with multiple re-renders

const potentialMemoryLeak$1 = () => {
  return GLOBALS + Math.random().toString(36).substring(7);
};

const getStyles = () => StyleSheet.create({
  style: {
    maxWidth: '1200px',
    margin: 'auto'
  }
});

const AMPAppContent = props => {
  const applyGlobalStyles = (reporter, context) => {
    const styles = extended$1.StyleSheet.create({
      [GLOBALS]: { ...normalizeCss(),
        ...generateGlobalStyles(reporter, context)
      }
    }); // eslint-disable-next-line no-underscore-dangle

    styles[GLOBALS]._name = potentialMemoryLeak$1();
    const componentStyle = getStyles();
    const {
      children
    } = props;
    return createElement(Fragment, null, createElement(Helmet, null, createElement("body", {
      className: extended$1.css(styles[GLOBALS])
    })), createElement(MobileTopBarColor, null), createElement("div", {
      className: css(componentStyle.style)
    }, children));
  };

  return createElement(ErrorReporting.Consumer, null, reporter => createElement(ThemeContext.Consumer, null, context => applyGlobalStyles(reporter, context)));
};

const componentConfigurationKey$9 = 'sectionHeader';

const createStyles$7 = vrLineHeight => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const LoadMoreButtonWrapperComponent = ({
  vrLineHeight,
  children
}) => {
  const style = createStyles$7(vrLineHeight);
  return createElement("div", {
    className: css(style.container)
  }, children);
};

const mapThemeProps$c = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const LoadMoreButtonWrapper = props => {
  const themeContext = useTheme(componentConfigurationKey$9, mapThemeProps$c);
  return createElement(LoadMoreButtonWrapperComponent, _extends({}, themeContext, props));
};

const primaryHeaderHeight = 48;
const secondaryHeaderHeight = 40;
const notActiveLinkIdleColor = (isHoveredLink, hoverOtherLinksColors, idleColor) => isHoveredLink ? hoverOtherLinksColors : idleColor;
const getHeaderHeight = isSecondaryHeaderEnabled => isSecondaryHeaderEnabled ? secondaryHeaderHeight + primaryHeaderHeight : primaryHeaderHeight;

const componentConfigThemeKey$a = 'main';
const MainWrapperComponent = ({
  vrLineHeight,
  backgroundColor,
  isSecondaryHeaderEnabled,
  noPadding,
  children
}) => {
  const headerHeight = getHeaderHeight(isSecondaryHeaderEnabled);
  const horizontalPadding = noPadding ? 0 : 1;
  const style = {
    backgroundColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      paddingTop: `${headerHeight + vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * horizontalPadding}px`,
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * horizontalPadding}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      paddingTop: `${headerHeight + vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * horizontalPadding}px`,
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * horizontalPadding}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5 * horizontalPadding}px`,
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5 * horizontalPadding}px`,
      paddingTop: `${headerHeight + vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5 * horizontalPadding}px`,
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5 * horizontalPadding}px`
    },
    paddingTop: `${headerHeight + vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`
  };
  return React__default.createElement("main", {
    className: css(StyleSheet.create({
      style
    }).style)
  }, children);
};

const mapThemeProps$d = theme => ({
  vrLineHeight: theme.vrLineHeight,
  backgroundColor: theme.backgroundColor
});

const MainWrapper = props => {
  const {
    configThemeKey = componentConfigThemeKey$a
  } = props;
  const themeContext = useTheme(configThemeKey, mapThemeProps$d);
  return React__default.createElement(MainWrapperComponent, _extends({}, themeContext, props));
};

const footerSectionPadding = vrLineHeight => ({
  [MEDIA_BREAKPOINTS.X_LARGE]: {
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px 0`
  },
  [MEDIA_BREAKPOINTS.LARGE]: {
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0`
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0`
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0`
  }
});
const generateSeparatorSections = separatorStyle => (acc, section, index, arr) => {
  const res = section;
  return index < arr.length - 1 ? [...acc, res, createElement("div", {
    key: index,
    className: css(separatorStyle)
  })] : [...acc, res];
};

const componentConfigurationKey$a = 'footerLinksSection';

const getStyle$2 = theme => {
  const {
    vrLineHeight
  } = theme;
  const padding = footerSectionPadding(vrLineHeight);
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: theme.color
    },
    gridWrapper: {
      display: 'flex',
      justifyContent: 'center'
    },
    linksWrapper: {
      display: 'inline-grid',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridTemplateColumns: 'auto auto auto',
        gridGap: `. ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridTemplateColumns: 'auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
      }
    }
  });
};

const FooterLinksSectionComponent = props => {
  const {
    title,
    links
  } = props;
  const style = getStyle$2(props);
  const linkStyle = generateLinkStyleFromTheme({ ...props
  });
  return createElement("div", {
    className: css(style.section)
  }, createElement("h4", {
    className: css(style.title)
  }, title), createElement("div", {
    className: css(style.gridWrapper)
  }, createElement("div", {
    className: css(style.linksWrapper)
  }, links.map((link, index) => createElement(TextLinkComponent, {
    key: index,
    href: link.href,
    linkStyle: linkStyle
  }, createElement("p", null, link.text))))));
};

const mapThemeToProps$7 = theme => ({
  color: theme.color,
  vrLineHeight: theme.vrLineHeight,
  idleColor: theme.idleColor,
  activeStateColor: theme.activeStateColor,
  hoverStateColor: theme.hoverStateColor
});

const FooterLinksSection = props => {
  const themeContext = useTheme(componentConfigurationKey$a, mapThemeToProps$7);
  return createElement(FooterLinksSectionComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$b = 'footerSocialLinks';

const getSocialLinksStyle = (vrLineHeight, titleColor, circleColor, numberOfColumns) => {
  const padding = footerSectionPadding(vrLineHeight);
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: titleColor
    },
    storesWrapper: {
      display: 'grid',
      gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
      }
    },
    circle: {
      width: '40px',
      height: '40px',
      backgroundColor: circleColor,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  });
};

const createStoreItem = circleStyle => (socialLink, index) => {
  return createElement(TextLinkComponent, {
    href: socialLink.href,
    key: index,
    rel: "noopener",
    target: "_blank"
  }, createElement("div", {
    className: css(circleStyle)
  }, createElement("img", {
    alt: socialLink.alt,
    src: socialLink.src,
    height: "16"
  })));
};

const maxSocialItemsPerRow = 5;
const FooterSocialLinksComponent = props => {
  const {
    title,
    imageLinks,
    vrLineHeight,
    titleColor,
    circleColor
  } = props;
  const style = getSocialLinksStyle(vrLineHeight, titleColor, circleColor, Math.min(imageLinks.length, maxSocialItemsPerRow));
  return createElement("div", {
    className: css(style.section)
  }, createElement("h4", {
    className: css(style.title)
  }, title), createElement("div", {
    className: css(style.storesWrapper)
  }, imageLinks.map(createStoreItem(style.circle))));
};

const mapThemeProps$e = theme => ({
  vrLineHeight: theme.vrLineHeight,
  titleColor: theme.titleColor,
  circleColor: theme.circleColor
});

const FooterSocialLinks = props => {
  const themeContext = useTheme(componentConfigurationKey$b, mapThemeProps$e);
  return createElement(FooterSocialLinksComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$c = 'footerMobileStores';

const getStyle$3 = (theme, numColumns = 2) => {
  const {
    vrLineHeight
  } = theme;
  const padding = footerSectionPadding(vrLineHeight);
  const columns = Array(numColumns).fill('auto').join(' ');
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: theme.color
    },
    storesWrapper: {
      display: 'grid',
      gridTemplateColumns: columns,
      justifyContent: 'center',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
      }
    },
    storeItem: {
      backgroundColor: 'wheat'
    }
  });
};

const createStoreItem$1 = (storeLink, index) => {
  return createElement(TextLinkComponent, {
    href: storeLink.href,
    key: index,
    target: "_blank"
  }, createElement("img", {
    alt: storeLink.alt,
    src: storeLink.src,
    height: "40"
  }));
};

const FooterMobileStoresComponent = props => {
  const {
    title,
    imageLinks
  } = props;
  const style = getStyle$3(props, imageLinks.length);
  return createElement("div", {
    className: css(style.section)
  }, createElement("h4", {
    className: css(style.title)
  }, title), createElement("div", {
    className: css(style.storesWrapper)
  }, imageLinks.map(createStoreItem$1)));
};

const mapThemeProps$f = theme => ({
  vrLineHeight: theme.vrLineHeight,
  color: theme.color
});

const FooterMobileStores = props => {
  const themeContext = useTheme(componentConfigurationKey$c, mapThemeProps$f);
  return createElement(ErrorBoundary, null, createElement(FooterMobileStoresComponent, _extends({}, themeContext, props)));
};

const getSeparatorStyle = borderTopColor => {
  return StyleSheet.create({
    style: {
      height: '1px',
      width: '100%',
      backgroundColor: borderTopColor,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'block'
      }
    }
  });
};

const getWrapperStyle = numberOfSections => {
  return StyleSheet.create({
    style: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridTemplateColumns: `repeat(${numberOfSections}, 1fr)`,
        justifyItems: 'center'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: `repeat(${numberOfSections}, 1fr)`,
        justifyItems: 'center'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridTemplateColumns: '1fr'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridTemplateColumns: '1fr'
      }
    }
  });
};

const getSocialSection = socialSection => {
  return socialSection ? createElement(FooterSocialLinks, _extends({
    key: "social-links"
  }, socialSection)) : null;
};

const getAppsSection = appsSection => {
  return appsSection ? createElement(FooterMobileStores, _extends({
    key: "mobile-links"
  }, appsSection)) : null;
};

const AdditionalFooterSections = props => {
  const {
    socialSection,
    appsSection,
    separatorColor
  } = props;
  const additionalFooterSections = [getSocialSection(socialSection), getAppsSection(appsSection)].filter(section => section !== null);
  return additionalFooterSections.length === 0 ? null : createElement("div", {
    className: css(getWrapperStyle(additionalFooterSections.length).style)
  }, additionalFooterSections.reduce(generateSeparatorSections(getSeparatorStyle(separatorColor).style), []));
};

const componentConfigurationKey$d = 'bottomFooterBar';

const getBottomFooterBarStyle = ({
  backgroundColor,
  vrLineHeight,
  textColor
}) => StyleSheet.create({
  style: {
    backgroundColor,
    color: textColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      textAlign: 'center'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      textAlign: 'center'
    }
  }
});

const BottomFooterBarComponent = props => {
  const bottomFooterBarStyle = getBottomFooterBarStyle(props);
  const {
    textColor,
    year,
    rightsReservedText,
    poweredByLink
  } = props;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: textColor,
    activeStateColor: textColor,
    hoverStateColor: textColor
  });
  return createElement("p", {
    className: css(bottomFooterBarStyle.style)
  }, createElement(TextLinkComponent, {
    href: poweredByLink.href,
    linkStyle: linkStyle,
    rel: "noopener",
    target: "_blank"
  }, poweredByLink.text), ` © ${year} ${rightsReservedText}`);
};

const mapThemeProps$g = theme => {
  return {
    backgroundColor: theme.backgroundColor,
    vrLineHeight: theme.vrLineHeight,
    textColor: theme.textColor
  };
};

const BottomFooterBar = props => {
  const themeContext = useTheme(componentConfigurationKey$d, mapThemeProps$g);
  return createElement(BottomFooterBarComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$e = 'footer';

const getFooterStyle = theme => {
  const {
    vrLineHeight,
    borderTopColor,
    backgroundColor
  } = theme;
  return StyleSheet.create({
    footer: {
      backgroundColor,
      borderTop: `solid 2px ${borderTopColor}`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.75}px`
      }
    },
    footerContent: {
      margin: 'auto',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        maxWidth: '1088px'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        maxWidth: '852px'
      }
    }
  });
};

const getSeparatorStyle$1 = separatorColor => {
  return StyleSheet.create({
    style: {
      height: '1px',
      backgroundColor: separatorColor
    }
  });
};

const getLinksSection = linksSection => {
  return linksSection ? createElement(FooterLinksSection, _extends({
    key: "footer-links"
  }, linksSection)) : null;
};

const getAdditionalFooterSection = (socialSection, appsSection, separatorColor) => {
  if (socialSection === null && appsSection === null) return null;
  return createElement(AdditionalFooterSections, {
    key: "additional-links",
    socialSection: socialSection,
    appsSection: appsSection,
    separatorColor: separatorColor
  });
};

const getFooterItems = (linksSection, socialSection, appsSection, separatorColor) => {
  return [getLinksSection(linksSection), getAdditionalFooterSection(socialSection, appsSection, separatorColor)].filter(section => section !== null).reduce(generateSeparatorSections(getSeparatorStyle$1(separatorColor).style), []);
};

const FooterComponent = props => {
  const footerComponentStyle = getFooterStyle(props);
  const {
    linksSection,
    socialSection,
    appsSection,
    separatorColor,
    bottomFooterBar
  } = props;
  return createElement("footer", null, createElement("div", {
    className: css(footerComponentStyle.footer)
  }, createElement("div", {
    className: css(footerComponentStyle.footerContent)
  }, getFooterItems(linksSection, socialSection, appsSection, separatorColor))), createElement(BottomFooterBar, bottomFooterBar));
};

const mapThemeProps$h = theme => {
  return {
    backgroundColor: theme.backgroundColor,
    borderTopColor: theme.borderTopColor,
    separatorColor: theme.separatorColor,
    vrLineHeight: theme.vrLineHeight
  };
};

const Footer = props => {
  const themeContext = useTheme(componentConfigurationKey$e, mapThemeProps$h);
  return createElement(ErrorBoundary, null, createElement(FooterComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$b = 'primaryLinks';
const radiusBorder = 4;

const getLinkStyle = (activeBorderBottom, isActive = false) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom
      } : {}
    }
  });
};

const getContainerStyle = vrLineHeight => {
  return StyleSheet.create({
    linksContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    }
  });
};

const additionalStyle = {
  [MEDIA_BREAKPOINTS.X_LARGE]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.LARGE]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    display: 'none'
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    display: 'none'
  }
};

const createLink$1 = ({
  idleColor,
  activeStateColor,
  hoverStateColor,
  activeBorderBottom,
  hoverOtherLinksColors,
  vrLineHeight
}, isHoveredLink, pagePath) => (linkData, index) => {
  const isActiveLink = linkData.href === pagePath;
  const style = getLinkStyle(activeBorderBottom, isActiveLink);
  const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: calculatedIdleColor,
    activeStateColor,
    hoverStateColor
  }, additionalStyle);
  const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
  return React__default.createElement(TextLinkComponent, {
    key: index,
    href: linkData.href,
    linkStyle: linkStyle
  }, React__default.createElement("span", {
    className: css(contentStyle.style, style.link)
  }, linkData.text));
};

class PrimaryLinksComponent extends React__default.Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "onLinkHover", () => {
      this.setState({
        isHoveredLink: true
      });
    });

    _defineProperty(this, "onLinkLeave", () => {
      this.setState({
        isHoveredLink: false
      });
    });

    this.state = {
      isHoveredLink: false
    };
  }

  render() {
    const {
      links,
      vrLineHeight,
      pagePath
    } = this.props;
    const {
      isHoveredLink
    } = this.state;
    const style = getContainerStyle(vrLineHeight);
    return React__default.createElement("div", {
      className: css(style.linksContainer),
      onMouseEnter: this.onLinkHover,
      onMouseLeave: this.onLinkLeave
    }, links.map(createLink$1(this.props, isHoveredLink, pagePath)));
  }

}

const mapThemeProps$i = theme => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    activeBorderBottom: theme.activeBorderBottom,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    vrLineHeight: theme.vrLineHeight
  };
};

const PrimaryLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$b, mapThemeProps$i);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(PrimaryLinksComponent, _extends({}, themeContext, props)));
};

const getLogoStyle = () => {
  return StyleSheet.create({
    base: {
      height: '24px',
      alignItems: 'center'
    },
    largeSvg: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    },
    smallSvg: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'inline-flex'
      }
    },
    image: {
      height: '100%'
    }
  });
};

const Logo = props => {
  const {
    href,
    alt,
    svgLarge,
    svgSmall
  } = props;
  const style = getLogoStyle();
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Link, {
    className: css(style.largeSvg, style.base),
    href: href
  }, React__default.createElement("img", {
    className: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgLarge)}`
  })), React__default.createElement(Link, {
    className: css(style.smallSvg, style.base),
    href: href
  }, React__default.createElement("img", {
    className: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgSmall)}`
  })));
};

let Directions;

(function (Directions) {
  Directions["UP"] = "180deg";
  Directions["DOWN"] = "0deg";
  Directions["LEFT"] = "90deg";
  Directions["RIGHT"] = "270deg";
})(Directions || (Directions = {}));

const getStyle$4 = direction => StyleSheet.create({
  caret: {
    transform: `rotate(${direction})`,
    transition: 'transform 400ms'
  }
});

const ChevronIcon = props => {
  const {
    direction = Directions.DOWN,
    color = '#FFF'
  } = props;
  return React__default.createElement("svg", {
    className: css(getStyle$4(direction).caret),
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "Icons-/-dropdown",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M11,8.09909655 L6.5,13 L5,11.3663655 L8.09099026,8 L5,4.63363448 L6.5,3 L11,7.90090345 L10.9090097,8 L11,8.09909655 Z",
    id: "dropdown-icon",
    fill: color,
    fillRule: "nonzero",
    transform: "rotate(90 8 8)"
  })));
};

const getWrapperStyle$1 = vrLineHeight => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px 0`
    },
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px 0`
    }
  }
});

const getContentStyle = (configThemeKey, hovering, isActive) => {
  switch (configThemeKey) {
    case 'editionsMenu':
      return StyleSheet.create(hovering ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());

    case 'moreLinks':
      return StyleSheet.create(isActive ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());

    default:
      return StyleSheet.create(getGeneratedGlobalStyles().p());
  }
};

class DropDownMenuLink extends React__default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      hovering: false
    });

    _defineProperty(this, "mouseEnterHandler", () => {
      this.setState({
        hovering: true
      });
    });

    _defineProperty(this, "mouseLeaveHandler", () => {
      this.setState({
        hovering: false
      });
    });
  }

  render() {
    const {
      linkColor,
      hoverColor,
      activeColor,
      href,
      text,
      vrLineHeight,
      isActive,
      configThemeKey
    } = this.props;
    const {
      hovering
    } = this.state;
    const wrapperStyle = getWrapperStyle$1(vrLineHeight);
    const contentStyle = getContentStyle(configThemeKey, hovering, isActive);
    const linkStyle = generateLinkStyleFromTheme({
      idleColor: isActive ? activeColor : linkColor,
      activeStateColor: activeColor,
      hoverStateColor: isActive ? activeColor : hoverColor
    }, {
      width: '100%'
    });
    return React__default.createElement("li", {
      onMouseEnter: this.mouseEnterHandler,
      onMouseLeave: this.mouseLeaveHandler,
      className: css(wrapperStyle.wrapper)
    }, React__default.createElement(TextLinkComponent, {
      href: href,
      linkStyle: linkStyle
    }, React__default.createElement("span", {
      className: css(contentStyle.style)
    }, text)));
  }

}

const getStyle$5 = theme => {
  const {
    vrLineHeight,
    menuBackgroundColor
  } = theme;
  return StyleSheet.create({
    menuWrapper: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      minWidth: '160px',
      boxSizing: 'border-box',
      background: menuBackgroundColor,
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      }
    }
  });
};

const getLinksList = (menuLinks, linkColor, hoverColor, activeColor, vrLineHeight, configThemeKey, activeEndpoint) => {
  const isActive = link => link.href === activeEndpoint;

  return menuLinks.map((link, index) => React__default.createElement(DropDownMenuLink, {
    key: index,
    href: link.href,
    text: link.text,
    linkColor: linkColor,
    hoverColor: hoverColor,
    activeColor: activeColor,
    vrLineHeight: vrLineHeight,
    isActive: isActive(link),
    configThemeKey: configThemeKey
  }));
};

const DropDownMenuBase = props => {
  const {
    surfaceStyle,
    vrLineHeight,
    menuLinks,
    configThemeKey,
    activeEndpoint
  } = props;
  const style = getStyle$5(props);
  const {
    linkColor,
    hoverColor,
    activeColor
  } = props;
  return React__default.createElement("ul", {
    className: css(style.menuWrapper, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle)
  }, getLinksList(menuLinks, linkColor, hoverColor, activeColor, vrLineHeight, configThemeKey, activeEndpoint));
};

const mapThemeProps$j = theme => ({
  menuBackgroundColor: theme.menuBackgroundColor,
  linkColor: theme.linkColor,
  hoverColor: theme.hoverColor,
  activeColor: theme.activeColor,
  vrLineHeight: theme.vrLineHeight,
  surfaceStyle: theme.surfaceStyle
});

const DropDownMenu = props => {
  const {
    configThemeKey
  } = props;

  const DropDownMenuWithTheme = menuProps => {
    const themeContext = useTheme(configThemeKey, mapThemeProps$j);
    return React__default.createElement(ErrorBoundary, null, React__default.createElement(DropDownMenuBase, _extends({}, themeContext, menuProps, {
      configThemeKey: configThemeKey
    })));
  };

  return React__default.createElement(DropDownMenuWithTheme, props);
};

const componentConfigThemeKey$c = 'editions';

const getEditionsStyle = color => {
  return StyleSheet.create({
    editionsWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    },
    editions: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      width: '144px',
      color,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      }
    }
  });
};

const getActiveEdition = (editions, editionEndpoint) => {
  return editions.find(edition => edition.href === editionEndpoint) || editions[0];
};
class EditionsComponent extends React__default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      menuOpen: false
    });

    _defineProperty(this, "editionsClickHandler", () => {
      const {
        menuOpen
      } = this.state;
      this.setState({
        menuOpen: !menuOpen
      });
    });
  }

  render() {
    const {
      editions,
      color,
      editionEndpoint
    } = this.props;
    const {
      menuOpen
    } = this.state;
    const style = getEditionsStyle(color);
    const activeEdition = getActiveEdition(editions, editionEndpoint);
    return React__default.createElement("div", {
      className: css(style.editionsWrapper)
    }, React__default.createElement("button", {
      type: "button",
      className: css(style.editions),
      onClick: this.editionsClickHandler
    }, React__default.createElement("h4", null, activeEdition.text), React__default.createElement(ChevronIcon, {
      direction: menuOpen ? Directions.UP : Directions.DOWN,
      color: color
    })), menuOpen && React__default.createElement(DropDownMenu, {
      menuLinks: editions,
      configThemeKey: "editionsMenu",
      activeEndpoint: editionEndpoint
    }));
  }

}

const mapThemeProps$k = theme => {
  return {
    color: theme.color
  };
};

const Editions = props => {
  const themeContext = useTheme(componentConfigThemeKey$c, mapThemeProps$k);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(EditionsComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$d = 'ctaButton';

const getStyle$6 = ({
  backgroundColorNormal,
  textColorNormal,
  borderColorNormal,
  backgroundColorHover,
  textColorHover,
  borderColorHover,
  backgroundColorActive,
  textColorActive,
  borderColorActive,
  vrLineHeight
}) => {
  return StyleSheet.create({
    ctaButton: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: backgroundColorNormal,
      color: textColorNormal,
      border: `${borderColorNormal} 1px solid`,
      cursor: 'pointer',
      borderWidth: '1px',
      textDecoration: 'none',
      height: '32px',
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: textColorHover,
        border: `${borderColorHover} 1px solid`
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: textColorActive,
        border: `${borderColorActive} 1px solid`
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    }
  });
};

const CtaButtonComponent = props => {
  const {
    surfaceStyle,
    href,
    text
  } = props;
  const style = getStyle$6(props);
  const linkTextStyle = StyleSheet.create(getGeneratedGlobalStyles().p());
  return React__default.createElement(Link, {
    className: css(style.ctaButton, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle, linkTextStyle.style),
    href: href
  }, text);
};

const mapThemeProps$l = theme => {
  return {
    backgroundColorNormal: theme.backgroundColorNormal,
    textColorNormal: theme.textColorNormal,
    borderColorNormal: theme.borderColorNormal,
    backgroundColorHover: theme.backgroundColorHover,
    textColorHover: theme.textColorHover,
    borderColorHover: theme.borderColorHover,
    backgroundColorActive: theme.backgroundColorActive,
    textColorActive: theme.textColorActive,
    borderColorActive: theme.borderColorActive,
    surfaceStyle: theme.surfaceStyle,
    vrLineHeight: theme.vrLineHeight
  };
};

const CtaButton = props => {
  const themeContext = useTheme(componentConfigThemeKey$d, mapThemeProps$l);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(CtaButtonComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$e = 'menuIcon';

const getMenuIconStyle = (isOpen, color) => StyleSheet.create({
  style: {
    height: '16px'
  },
  wrapper: {
    width: '16px',
    height: '16px',
    position: 'relative',
    transition: '.5s ease-in-out',
    cursor: 'pointer'
  },
  span: {
    display: 'block',
    position: 'absolute',
    height: '2px',
    width: '100%',
    backgroundColor: color,
    opacity: 1,
    left: 0,
    transition: '.25s ease-in-out',
    transformOrigin: 'right center'
  },
  firstSpan: (() => {
    return isOpen ? {
      transform: 'rotate(-45deg)',
      width: '130%',
      left: '-6px',
      top: 0
    } : {
      top: 0
    };
  })(),
  secondSpan: (() => {
    return isOpen ? {
      top: '7px',
      transform: 'scaleX(0.1)',
      opacity: 0
    } : {
      top: '7px'
    };
  })(),
  thirdSpan: (() => {
    return isOpen ? {
      transform: 'rotate(45deg)',
      top: '14px',
      width: '130%',
      left: '-6px'
    } : {
      top: '14px'
    };
  })()
});

const MenuIconComponent = props => {
  const {
    isMenuOpen,
    onClick,
    color
  } = props;
  const style = getMenuIconStyle(isMenuOpen, color);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: css(style.wrapper),
    onClick: onClick
  }, React__default.createElement("span", {
    className: css(style.span, style.firstSpan)
  }), React__default.createElement("span", {
    className: css(style.span, style.secondSpan)
  }), React__default.createElement("span", {
    className: css(style.span, style.thirdSpan)
  })));
};

const mapThemeProps$m = theme => {
  return {
    color: theme.color
  };
};

const MenuIcon = props => {
  const themeContext = useTheme(componentConfigThemeKey$e, mapThemeProps$m);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(MenuIconComponent, _extends({}, themeContext, props)));
};

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var reactIs_production_min = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: !0
});
var b = "function" === typeof Symbol && Symbol.for,
    c = b ? Symbol.for("react.element") : 60103,
    d = b ? Symbol.for("react.portal") : 60106,
    e = b ? Symbol.for("react.fragment") : 60107,
    f = b ? Symbol.for("react.strict_mode") : 60108,
    g = b ? Symbol.for("react.profiler") : 60114,
    h = b ? Symbol.for("react.provider") : 60109,
    k = b ? Symbol.for("react.context") : 60110,
    l = b ? Symbol.for("react.async_mode") : 60111,
    m = b ? Symbol.for("react.concurrent_mode") : 60111,
    n = b ? Symbol.for("react.forward_ref") : 60112,
    p = b ? Symbol.for("react.suspense") : 60113,
    q = b ? Symbol.for("react.suspense_list") : 60120,
    r = b ? Symbol.for("react.memo") : 60115,
    t = b ? Symbol.for("react.lazy") : 60116,
    v = b ? Symbol.for("react.fundamental") : 60117,
    w = b ? Symbol.for("react.responder") : 60118,
    x = b ? Symbol.for("react.scope") : 60119;

function y(a) {
  if ("object" === typeof a && null !== a) {
    var u = a.$$typeof;

    switch (u) {
      case c:
        switch (a = a.type, a) {
          case l:
          case m:
          case e:
          case g:
          case f:
          case p:
            return a;

          default:
            switch (a = a && a.$$typeof, a) {
              case k:
              case n:
              case h:
                return a;

              default:
                return u;
            }

        }

      case t:
      case r:
      case d:
        return u;
    }
  }
}

function z(a) {
  return y(a) === m;
}

exports.typeOf = y;
exports.AsyncMode = l;
exports.ConcurrentMode = m;
exports.ContextConsumer = k;
exports.ContextProvider = h;
exports.Element = c;
exports.ForwardRef = n;
exports.Fragment = e;
exports.Lazy = t;
exports.Memo = r;
exports.Portal = d;
exports.Profiler = g;
exports.StrictMode = f;
exports.Suspense = p;

exports.isValidElementType = function (a) {
  return "string" === typeof a || "function" === typeof a || a === e || a === m || a === g || a === f || a === p || a === q || "object" === typeof a && null !== a && (a.$$typeof === t || a.$$typeof === r || a.$$typeof === h || a.$$typeof === k || a.$$typeof === n || a.$$typeof === v || a.$$typeof === w || a.$$typeof === x);
};

exports.isAsyncMode = function (a) {
  return z(a) || y(a) === l;
};

exports.isConcurrentMode = z;

exports.isContextConsumer = function (a) {
  return y(a) === k;
};

exports.isContextProvider = function (a) {
  return y(a) === h;
};

exports.isElement = function (a) {
  return "object" === typeof a && null !== a && a.$$typeof === c;
};

exports.isForwardRef = function (a) {
  return y(a) === n;
};

exports.isFragment = function (a) {
  return y(a) === e;
};

exports.isLazy = function (a) {
  return y(a) === t;
};

exports.isMemo = function (a) {
  return y(a) === r;
};

exports.isPortal = function (a) {
  return y(a) === d;
};

exports.isProfiler = function (a) {
  return y(a) === g;
};

exports.isStrictMode = function (a) {
  return y(a) === f;
};

exports.isSuspense = function (a) {
  return y(a) === p;
};
});

unwrapExports(reactIs_production_min);
var reactIs_production_min_1 = reactIs_production_min.typeOf;
var reactIs_production_min_2 = reactIs_production_min.AsyncMode;
var reactIs_production_min_3 = reactIs_production_min.ConcurrentMode;
var reactIs_production_min_4 = reactIs_production_min.ContextConsumer;
var reactIs_production_min_5 = reactIs_production_min.ContextProvider;
var reactIs_production_min_6 = reactIs_production_min.Element;
var reactIs_production_min_7 = reactIs_production_min.ForwardRef;
var reactIs_production_min_8 = reactIs_production_min.Fragment;
var reactIs_production_min_9 = reactIs_production_min.Lazy;
var reactIs_production_min_10 = reactIs_production_min.Memo;
var reactIs_production_min_11 = reactIs_production_min.Portal;
var reactIs_production_min_12 = reactIs_production_min.Profiler;
var reactIs_production_min_13 = reactIs_production_min.StrictMode;
var reactIs_production_min_14 = reactIs_production_min.Suspense;
var reactIs_production_min_15 = reactIs_production_min.isValidElementType;
var reactIs_production_min_16 = reactIs_production_min.isAsyncMode;
var reactIs_production_min_17 = reactIs_production_min.isConcurrentMode;
var reactIs_production_min_18 = reactIs_production_min.isContextConsumer;
var reactIs_production_min_19 = reactIs_production_min.isContextProvider;
var reactIs_production_min_20 = reactIs_production_min.isElement;
var reactIs_production_min_21 = reactIs_production_min.isForwardRef;
var reactIs_production_min_22 = reactIs_production_min.isFragment;
var reactIs_production_min_23 = reactIs_production_min.isLazy;
var reactIs_production_min_24 = reactIs_production_min.isMemo;
var reactIs_production_min_25 = reactIs_production_min.isPortal;
var reactIs_production_min_26 = reactIs_production_min.isProfiler;
var reactIs_production_min_27 = reactIs_production_min.isStrictMode;
var reactIs_production_min_28 = reactIs_production_min.isSuspense;

var reactIs_development = createCommonjsModule(function (module, exports) {

if (process.env.NODE_ENV !== "production") {
  (function () {

    Object.defineProperty(exports, '__esModule', {
      value: true
    }); // The Symbol used to tag the ReactElement-like types. If there is no native Symbol
    // nor polyfill, then a plain number is used for performance.

    var hasSymbol = typeof Symbol === 'function' && Symbol.for;
    var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
    var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
    var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
    var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
    var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
    var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
    var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
    // (unstable) APIs that have been removed. Can we remove the symbols?

    var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
    var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
    var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
    var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
    var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
    var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
    var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
    var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
    var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
    var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

    function isValidElementType(type) {
      return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
      type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE);
    }
    /**
     * Forked from fbjs/warning:
     * https://github.com/facebook/fbjs/blob/e66ba20ad5be433eb54423f2b097d829324d9de6/packages/fbjs/src/__forks__/warning.js
     *
     * Only change is we use console.warn instead of console.error,
     * and do nothing when 'console' is not supported.
     * This really simplifies the code.
     * ---
     * Similar to invariant but only logs a warning if the condition is not met.
     * This can be used to log issues in development environments in critical
     * paths. Removing the logging code for production environments will keep the
     * same logic and follow the same code paths.
     */


    var lowPriorityWarningWithoutStack = function () {};

    {
      var printWarning = function (format) {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        var argIndex = 0;
        var message = 'Warning: ' + format.replace(/%s/g, function () {
          return args[argIndex++];
        });

        if (typeof console !== 'undefined') {
          console.warn(message);
        }

        try {
          // --- Welcome to debugging React ---
          // This error was thrown as a convenience so that you can use this stack
          // to find the callsite that caused this warning to fire.
          throw new Error(message);
        } catch (x) {}
      };

      lowPriorityWarningWithoutStack = function (condition, format) {
        if (format === undefined) {
          throw new Error('`lowPriorityWarningWithoutStack(condition, format, ...args)` requires a warning ' + 'message argument');
        }

        if (!condition) {
          for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
            args[_key2 - 2] = arguments[_key2];
          }

          printWarning.apply(void 0, [format].concat(args));
        }
      };
    }
    var lowPriorityWarningWithoutStack$1 = lowPriorityWarningWithoutStack;

    function typeOf(object) {
      if (typeof object === 'object' && object !== null) {
        var $$typeof = object.$$typeof;

        switch ($$typeof) {
          case REACT_ELEMENT_TYPE:
            var type = object.type;

            switch (type) {
              case REACT_ASYNC_MODE_TYPE:
              case REACT_CONCURRENT_MODE_TYPE:
              case REACT_FRAGMENT_TYPE:
              case REACT_PROFILER_TYPE:
              case REACT_STRICT_MODE_TYPE:
              case REACT_SUSPENSE_TYPE:
                return type;

              default:
                var $$typeofType = type && type.$$typeof;

                switch ($$typeofType) {
                  case REACT_CONTEXT_TYPE:
                  case REACT_FORWARD_REF_TYPE:
                  case REACT_PROVIDER_TYPE:
                    return $$typeofType;

                  default:
                    return $$typeof;
                }

            }

          case REACT_LAZY_TYPE:
          case REACT_MEMO_TYPE:
          case REACT_PORTAL_TYPE:
            return $$typeof;
        }
      }

      return undefined;
    } // AsyncMode is deprecated along with isAsyncMode


    var AsyncMode = REACT_ASYNC_MODE_TYPE;
    var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
    var ContextConsumer = REACT_CONTEXT_TYPE;
    var ContextProvider = REACT_PROVIDER_TYPE;
    var Element = REACT_ELEMENT_TYPE;
    var ForwardRef = REACT_FORWARD_REF_TYPE;
    var Fragment = REACT_FRAGMENT_TYPE;
    var Lazy = REACT_LAZY_TYPE;
    var Memo = REACT_MEMO_TYPE;
    var Portal = REACT_PORTAL_TYPE;
    var Profiler = REACT_PROFILER_TYPE;
    var StrictMode = REACT_STRICT_MODE_TYPE;
    var Suspense = REACT_SUSPENSE_TYPE;
    var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

    function isAsyncMode(object) {
      {
        if (!hasWarnedAboutDeprecatedIsAsyncMode) {
          hasWarnedAboutDeprecatedIsAsyncMode = true;
          lowPriorityWarningWithoutStack$1(false, 'The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
        }
      }
      return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
    }

    function isConcurrentMode(object) {
      return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
    }

    function isContextConsumer(object) {
      return typeOf(object) === REACT_CONTEXT_TYPE;
    }

    function isContextProvider(object) {
      return typeOf(object) === REACT_PROVIDER_TYPE;
    }

    function isElement(object) {
      return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
    }

    function isForwardRef(object) {
      return typeOf(object) === REACT_FORWARD_REF_TYPE;
    }

    function isFragment(object) {
      return typeOf(object) === REACT_FRAGMENT_TYPE;
    }

    function isLazy(object) {
      return typeOf(object) === REACT_LAZY_TYPE;
    }

    function isMemo(object) {
      return typeOf(object) === REACT_MEMO_TYPE;
    }

    function isPortal(object) {
      return typeOf(object) === REACT_PORTAL_TYPE;
    }

    function isProfiler(object) {
      return typeOf(object) === REACT_PROFILER_TYPE;
    }

    function isStrictMode(object) {
      return typeOf(object) === REACT_STRICT_MODE_TYPE;
    }

    function isSuspense(object) {
      return typeOf(object) === REACT_SUSPENSE_TYPE;
    }

    exports.typeOf = typeOf;
    exports.AsyncMode = AsyncMode;
    exports.ConcurrentMode = ConcurrentMode;
    exports.ContextConsumer = ContextConsumer;
    exports.ContextProvider = ContextProvider;
    exports.Element = Element;
    exports.ForwardRef = ForwardRef;
    exports.Fragment = Fragment;
    exports.Lazy = Lazy;
    exports.Memo = Memo;
    exports.Portal = Portal;
    exports.Profiler = Profiler;
    exports.StrictMode = StrictMode;
    exports.Suspense = Suspense;
    exports.isValidElementType = isValidElementType;
    exports.isAsyncMode = isAsyncMode;
    exports.isConcurrentMode = isConcurrentMode;
    exports.isContextConsumer = isContextConsumer;
    exports.isContextProvider = isContextProvider;
    exports.isElement = isElement;
    exports.isForwardRef = isForwardRef;
    exports.isFragment = isFragment;
    exports.isLazy = isLazy;
    exports.isMemo = isMemo;
    exports.isPortal = isPortal;
    exports.isProfiler = isProfiler;
    exports.isStrictMode = isStrictMode;
    exports.isSuspense = isSuspense;
  })();
}
});

unwrapExports(reactIs_development);
var reactIs_development_1 = reactIs_development.typeOf;
var reactIs_development_2 = reactIs_development.AsyncMode;
var reactIs_development_3 = reactIs_development.ConcurrentMode;
var reactIs_development_4 = reactIs_development.ContextConsumer;
var reactIs_development_5 = reactIs_development.ContextProvider;
var reactIs_development_6 = reactIs_development.Element;
var reactIs_development_7 = reactIs_development.ForwardRef;
var reactIs_development_8 = reactIs_development.Fragment;
var reactIs_development_9 = reactIs_development.Lazy;
var reactIs_development_10 = reactIs_development.Memo;
var reactIs_development_11 = reactIs_development.Portal;
var reactIs_development_12 = reactIs_development.Profiler;
var reactIs_development_13 = reactIs_development.StrictMode;
var reactIs_development_14 = reactIs_development.Suspense;
var reactIs_development_15 = reactIs_development.isValidElementType;
var reactIs_development_16 = reactIs_development.isAsyncMode;
var reactIs_development_17 = reactIs_development.isConcurrentMode;
var reactIs_development_18 = reactIs_development.isContextConsumer;
var reactIs_development_19 = reactIs_development.isContextProvider;
var reactIs_development_20 = reactIs_development.isElement;
var reactIs_development_21 = reactIs_development.isForwardRef;
var reactIs_development_22 = reactIs_development.isFragment;
var reactIs_development_23 = reactIs_development.isLazy;
var reactIs_development_24 = reactIs_development.isMemo;
var reactIs_development_25 = reactIs_development.isPortal;
var reactIs_development_26 = reactIs_development.isProfiler;
var reactIs_development_27 = reactIs_development.isStrictMode;
var reactIs_development_28 = reactIs_development.isSuspense;

var reactIs = createCommonjsModule(function (module) {

if (process.env.NODE_ENV === 'production') {
  module.exports = reactIs_production_min;
} else {
  module.exports = reactIs_development;
}
});

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
/* eslint-disable no-unused-vars */

var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
  if (val === null || val === undefined) {
    throw new TypeError('Object.assign cannot be called with null or undefined');
  }

  return Object(val);
}

function shouldUseNative() {
  try {
    if (!Object.assign) {
      return false;
    } // Detect buggy property enumeration order in older V8 versions.
    // https://bugs.chromium.org/p/v8/issues/detail?id=4118


    var test1 = new String('abc'); // eslint-disable-line no-new-wrappers

    test1[5] = 'de';

    if (Object.getOwnPropertyNames(test1)[0] === '5') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test2 = {};

    for (var i = 0; i < 10; i++) {
      test2['_' + String.fromCharCode(i)] = i;
    }

    var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
      return test2[n];
    });

    if (order2.join('') !== '0123456789') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test3 = {};
    'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
      test3[letter] = letter;
    });

    if (Object.keys(Object.assign({}, test3)).join('') !== 'abcdefghijklmnopqrst') {
      return false;
    }

    return true;
  } catch (err) {
    // We don't expect any of the above to throw, but better to be safe.
    return false;
  }
}

var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
  var from;
  var to = toObject(target);
  var symbols;

  for (var s = 1; s < arguments.length; s++) {
    from = Object(arguments[s]);

    for (var key in from) {
      if (hasOwnProperty.call(from, key)) {
        to[key] = from[key];
      }
    }

    if (getOwnPropertySymbols) {
      symbols = getOwnPropertySymbols(from);

      for (var i = 0; i < symbols.length; i++) {
        if (propIsEnumerable.call(from, symbols[i])) {
          to[symbols[i]] = from[symbols[i]];
        }
      }
    }
  }

  return to;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';
var ReactPropTypesSecret_1 = ReactPropTypesSecret;

var printWarning = function () {};

if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;

  var loggedTypeFailures = {};
  var has = Function.call.bind(Object.prototype.hasOwnProperty);

  printWarning = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}
/**
 * Assert that the values match with the type specs.
 * Error messages are memorized and will only be shown once.
 *
 * @param {object} typeSpecs Map of name to a ReactPropType
 * @param {object} values Runtime values that need to be type-checked
 * @param {string} location e.g. "prop", "context", "child context"
 * @param {string} componentName Name of the component for error messages.
 * @param {?Function} getStack Returns the component stack.
 * @private
 */


function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has(typeSpecs, typeSpecName)) {
        var error; // Prop type validation may throw. In case they do, we don't want to
        // fail the render phase where it didn't fail before. So we log it.
        // After these have been cleaned up, we'll let them throw.

        try {
          // This is intentionally an invariant that gets caught. It's the same
          // behavior as without this statement except with a better message.
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error((componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' + 'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.');
            err.name = 'Invariant Violation';
            throw err;
          }

          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$1);
        } catch (ex) {
          error = ex;
        }

        if (error && !(error instanceof Error)) {
          printWarning((componentName || 'React class') + ': type specification of ' + location + ' `' + typeSpecName + '` is invalid; the type checker ' + 'function must return `null` or an `Error` but returned a ' + typeof error + '. ' + 'You may have forgotten to pass an argument to the type checker ' + 'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' + 'shape all require an argument).');
        }

        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          // Only monitor this failure once because there tends to be a lot of the
          // same error.
          loggedTypeFailures[error.message] = true;
          var stack = getStack ? getStack() : '';
          printWarning('Failed ' + location + ' type: ' + error.message + (stack != null ? stack : ''));
        }
      }
    }
  }
}
/**
 * Resets warning cache when testing.
 *
 * @private
 */


checkPropTypes.resetWarningCache = function () {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};

var checkPropTypes_1 = checkPropTypes;

var has$1 = Function.call.bind(Object.prototype.hasOwnProperty);

var printWarning$1 = function () {};

if (process.env.NODE_ENV !== 'production') {
  printWarning$1 = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

function emptyFunctionThatReturnsNull() {
  return null;
}

var factoryWithTypeCheckers = function (isValidElement, throwOnDirectAccess) {
  /* global Symbol */
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

  /**
   * Returns the iterator method function contained on the iterable object.
   *
   * Be sure to invoke the function with the iterable as context:
   *
   *     var iteratorFn = getIteratorFn(myIterable);
   *     if (iteratorFn) {
   *       var iterator = iteratorFn.call(myIterable);
   *       ...
   *     }
   *
   * @param {?object} maybeIterable
   * @return {?function}
   */

  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);

    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }
  /**
   * Collection of methods that allow declaration and validation of props that are
   * supplied to React components. Example usage:
   *
   *   var Props = require('ReactPropTypes');
   *   var MyArticle = React.createClass({
   *     propTypes: {
   *       // An optional string prop named "description".
   *       description: Props.string,
   *
   *       // A required enum prop named "category".
   *       category: Props.oneOf(['News','Photos']).isRequired,
   *
   *       // A prop named "dialog" that requires an instance of Dialog.
   *       dialog: Props.instanceOf(Dialog).isRequired
   *     },
   *     render: function() { ... }
   *   });
   *
   * A more formal specification of how these methods are used:
   *
   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
   *   decl := ReactPropTypes.{type}(.isRequired)?
   *
   * Each and every declaration produces a function with the same signature. This
   * allows the creation of custom validation functions. For example:
   *
   *  var MyLink = React.createClass({
   *    propTypes: {
   *      // An optional string or URI prop named "href".
   *      href: function(props, propName, componentName) {
   *        var propValue = props[propName];
   *        if (propValue != null && typeof propValue !== 'string' &&
   *            !(propValue instanceof URI)) {
   *          return new Error(
   *            'Expected a string or an URI for ' + propName + ' in ' +
   *            componentName
   *          );
   *        }
   *      }
   *    },
   *    render: function() {...}
   *  });
   *
   * @internal
   */


  var ANONYMOUS = '<<anonymous>>'; // Important!
  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.

  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),
    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker
  };
  /**
   * inlined Object.is polyfill to avoid requiring consumers ship their own
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
   */

  /*eslint-disable no-self-compare*/

  function is(x, y) {
    // SameValue algorithm
    if (x === y) {
      // Steps 1-5, 7-10
      // Steps 6.b-6.e: +0 != -0
      return x !== 0 || 1 / x === 1 / y;
    } else {
      // Step 6.a: NaN == NaN
      return x !== x && y !== y;
    }
  }
  /*eslint-enable no-self-compare*/

  /**
   * We use an Error-like object for backward compatibility as people may call
   * PropTypes directly and inspect their output. However, we don't use real
   * Errors anymore. We don't inspect their stack anyway, and creating them
   * is prohibitively expensive if they are created too often, such as what
   * happens in oneOfType() for any type before the one that matched.
   */


  function PropTypeError(message) {
    this.message = message;
    this.stack = '';
  } // Make `instanceof Error` still work for returned errors.


  PropTypeError.prototype = Error.prototype;

  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }

    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;

      if (secret !== ReactPropTypesSecret_1) {
        if (throwOnDirectAccess) {
          // New behavior only for users of `prop-types` package
          var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use `PropTypes.checkPropTypes()` to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          // Old behavior for people using React.PropTypes
          var cacheKey = componentName + ':' + propName;

          if (!manualPropTypeCallCache[cacheKey] && // Avoid spamming the console because they are often not actionable except for lib authors
          manualPropTypeWarningCount < 3) {
            printWarning$1('You are manually calling a React.PropTypes validation ' + 'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' + 'and will throw in the standalone `prop-types` package. ' + 'You may be seeing this warning due to a third-party PropTypes ' + 'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.');
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }

      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }

          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }

        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }

    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);
    return chainedCheckType;
  }

  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== expectedType) {
        // `propValue` being instance of, say, date/regexp, pass the 'object'
        // check, but we can offer a more precise error message here rather than
        // 'of type `object`'.
        var preciseType = getPreciseType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }

  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }

      var propValue = props[propName];

      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }

      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret_1);

        if (error instanceof Error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!reactIs.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning$1('Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' + 'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).');
        } else {
          printWarning$1('Invalid argument supplied to oneOf, expected an array.');
        }
      }

      return emptyFunctionThatReturnsNull;
    }

    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }

      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);

        if (type === 'symbol') {
          return String(value);
        }

        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }

      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }

      for (var key in propValue) {
        if (has$1(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

          if (error instanceof Error) {
            return error;
          }
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning$1('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }

    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];

      if (typeof checker !== 'function') {
        printWarning$1('Invalid argument supplied to oneOfType. Expected an array of check functions, but ' + 'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.');
        return emptyFunctionThatReturnsNull;
      }
    }

    function validate(props, propName, componentName, location, propFullName) {
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];

        if (checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret_1) == null) {
          return null;
        }
      }

      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }

      for (var key in shapeTypes) {
        var checker = shapeTypes[key];

        if (!checker) {
          continue;
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      } // We need to check all keys in case some are required but missing from
      // props.


      var allKeys = objectAssign({}, props[propName], shapeTypes);

      for (var key in allKeys) {
        var checker = shapeTypes[key];

        if (!checker) {
          return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' + '\nBad object: ' + JSON.stringify(props[propName], null, '  ') + '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  '));
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;

      case 'boolean':
        return !propValue;

      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }

        if (propValue === null || isValidElement(propValue)) {
          return true;
        }

        var iteratorFn = getIteratorFn(propValue);

        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;

          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            // Iterator will provide entry [k,v] tuples rather than values.
            while (!(step = iterator.next()).done) {
              var entry = step.value;

              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }

        return true;

      default:
        return false;
    }
  }

  function isSymbol(propType, propValue) {
    // Native Symbol.
    if (propType === 'symbol') {
      return true;
    } // falsy value can't be a Symbol


    if (!propValue) {
      return false;
    } // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'


    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    } // Fallback for non-spec compliant Symbols which are polyfilled.


    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }

    return false;
  } // Equivalent of `typeof` but with special handling for array and regexp.


  function getPropType(propValue) {
    var propType = typeof propValue;

    if (Array.isArray(propValue)) {
      return 'array';
    }

    if (propValue instanceof RegExp) {
      // Old webkits (at least until Android 4.0) return 'function' rather than
      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
      // passes PropTypes.object.
      return 'object';
    }

    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }

    return propType;
  } // This handles more types than `getPropType`. Only used for error messages.
  // See `createPrimitiveTypeChecker`.


  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }

    var propType = getPropType(propValue);

    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }

    return propType;
  } // Returns a string that is postfixed to a warning about an invalid type.
  // For example, "undefined" or "of type array"


  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);

    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;

      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;

      default:
        return type;
    }
  } // Returns class name of the object, if any.


  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }

    return propValue.constructor.name;
  }

  ReactPropTypes.checkPropTypes = checkPropTypes_1;
  ReactPropTypes.resetWarningCache = checkPropTypes_1.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

function emptyFunction() {}

function emptyFunctionWithReset() {}

emptyFunctionWithReset.resetWarningCache = emptyFunction;

var factoryWithThrowingShims = function () {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret_1) {
      // It is still safe when called from React.
      return;
    }

    var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use PropTypes.checkPropTypes() to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
    err.name = 'Invariant Violation';
    throw err;
  }
  shim.isRequired = shim;

  function getShim() {
    return shim;
  }
  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.

  var ReactPropTypes = {
    array: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,
    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,
    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

var propTypes = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
if (process.env.NODE_ENV !== 'production') {
  var ReactIs = reactIs; // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod


  var throwOnDirectAccess = true;
  module.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  module.exports = factoryWithThrowingShims();
}
});

function hasClass(element, className) {
  if (element.classList) return !!className && element.classList.contains(className);
  return (" " + (element.className.baseVal || element.className) + " ").indexOf(" " + className + " ") !== -1;
}

function addClass(element, className) {
  if (element.classList) element.classList.add(className);else if (!hasClass(element, className)) if (typeof element.className === 'string') element.className = element.className + " " + className;else element.setAttribute('class', (element.className && element.className.baseVal || '') + " " + className);
}

function replaceClassName(origClass, classToRemove) {
  return origClass.replace(new RegExp("(^|\\s)" + classToRemove + "(?:\\s|$)", 'g'), '$1').replace(/\s+/g, ' ').replace(/^\s*|\s*$/g, '');
}

function removeClass(element, className) {
  if (element.classList) {
    element.classList.remove(className);
  } else if (typeof element.className === 'string') {
    element.className = replaceClassName(element.className, className);
  } else {
    element.setAttribute('class', replaceClassName(element.className && element.className.baseVal || '', className));
  }
}

var config = {
  disabled: false
};

var timeoutsShape = process.env.NODE_ENV !== 'production' ? propTypes.oneOfType([propTypes.number, propTypes.shape({
  enter: propTypes.number,
  exit: propTypes.number,
  appear: propTypes.number
}).isRequired]) : null;
var classNamesShape = process.env.NODE_ENV !== 'production' ? propTypes.oneOfType([propTypes.string, propTypes.shape({
  enter: propTypes.string,
  exit: propTypes.string,
  active: propTypes.string
}), propTypes.shape({
  enter: propTypes.string,
  enterDone: propTypes.string,
  enterActive: propTypes.string,
  exit: propTypes.string,
  exitDone: propTypes.string,
  exitActive: propTypes.string
})]) : null;

var TransitionGroupContext = React__default.createContext(null);

var UNMOUNTED = 'unmounted';
var EXITED = 'exited';
var ENTERING = 'entering';
var ENTERED = 'entered';
var EXITING = 'exiting';
/**
 * The Transition component lets you describe a transition from one component
 * state to another _over time_ with a simple declarative API. Most commonly
 * it's used to animate the mounting and unmounting of a component, but can also
 * be used to describe in-place transition states as well.
 *
 * ---
 *
 * **Note**: `Transition` is a platform-agnostic base component. If you're using
 * transitions in CSS, you'll probably want to use
 * [`CSSTransition`](https://reactcommunity.org/react-transition-group/css-transition)
 * instead. It inherits all the features of `Transition`, but contains
 * additional features necessary to play nice with CSS transitions (hence the
 * name of the component).
 *
 * ---
 *
 * By default the `Transition` component does not alter the behavior of the
 * component it renders, it only tracks "enter" and "exit" states for the
 * components. It's up to you to give meaning and effect to those states. For
 * example we can add styles to a component when it enters or exits:
 *
 * ```jsx
 * import { Transition } from 'react-transition-group';
 *
 * const duration = 300;
 *
 * const defaultStyle = {
 *   transition: `opacity ${duration}ms ease-in-out`,
 *   opacity: 0,
 * }
 *
 * const transitionStyles = {
 *   entering: { opacity: 1 },
 *   entered:  { opacity: 1 },
 *   exiting:  { opacity: 0 },
 *   exited:  { opacity: 0 },
 * };
 *
 * const Fade = ({ in: inProp }) => (
 *   <Transition in={inProp} timeout={duration}>
 *     {state => (
 *       <div style={{
 *         ...defaultStyle,
 *         ...transitionStyles[state]
 *       }}>
 *         I'm a fade Transition!
 *       </div>
 *     )}
 *   </Transition>
 * );
 * ```
 *
 * There are 4 main states a Transition can be in:
 *  - `'entering'`
 *  - `'entered'`
 *  - `'exiting'`
 *  - `'exited'`
 *
 * Transition state is toggled via the `in` prop. When `true` the component
 * begins the "Enter" stage. During this stage, the component will shift from
 * its current transition state, to `'entering'` for the duration of the
 * transition and then to the `'entered'` stage once it's complete. Let's take
 * the following example (we'll use the
 * [useState](https://reactjs.org/docs/hooks-reference.html#usestate) hook):
 *
 * ```jsx
 * function App() {
 *   const [inProp, setInProp] = useState(false);
 *   return (
 *     <div>
 *       <Transition in={inProp} timeout={500}>
 *         {state => (
 *           // ...
 *         )}
 *       </Transition>
 *       <button onClick={() => setInProp(true)}>
 *         Click to Enter
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * When the button is clicked the component will shift to the `'entering'` state
 * and stay there for 500ms (the value of `timeout`) before it finally switches
 * to `'entered'`.
 *
 * When `in` is `false` the same thing happens except the state moves from
 * `'exiting'` to `'exited'`.
 */

var Transition =
/*#__PURE__*/
function (_React$Component) {
  _inheritsLoose(Transition, _React$Component);

  function Transition(props, context) {
    var _this;

    _this = _React$Component.call(this, props, context) || this;
    var parentGroup = context; // In the context of a TransitionGroup all enters are really appears

    var appear = parentGroup && !parentGroup.isMounting ? props.enter : props.appear;
    var initialStatus;
    _this.appearStatus = null;

    if (props.in) {
      if (appear) {
        initialStatus = EXITED;
        _this.appearStatus = ENTERING;
      } else {
        initialStatus = ENTERED;
      }
    } else {
      if (props.unmountOnExit || props.mountOnEnter) {
        initialStatus = UNMOUNTED;
      } else {
        initialStatus = EXITED;
      }
    }

    _this.state = {
      status: initialStatus
    };
    _this.nextCallback = null;
    return _this;
  }

  Transition.getDerivedStateFromProps = function getDerivedStateFromProps(_ref, prevState) {
    var nextIn = _ref.in;

    if (nextIn && prevState.status === UNMOUNTED) {
      return {
        status: EXITED
      };
    }

    return null;
  }; // getSnapshotBeforeUpdate(prevProps) {
  //   let nextStatus = null
  //   if (prevProps !== this.props) {
  //     const { status } = this.state
  //     if (this.props.in) {
  //       if (status !== ENTERING && status !== ENTERED) {
  //         nextStatus = ENTERING
  //       }
  //     } else {
  //       if (status === ENTERING || status === ENTERED) {
  //         nextStatus = EXITING
  //       }
  //     }
  //   }
  //   return { nextStatus }
  // }


  var _proto = Transition.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.updateStatus(true, this.appearStatus);
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var nextStatus = null;

    if (prevProps !== this.props) {
      var status = this.state.status;

      if (this.props.in) {
        if (status !== ENTERING && status !== ENTERED) {
          nextStatus = ENTERING;
        }
      } else {
        if (status === ENTERING || status === ENTERED) {
          nextStatus = EXITING;
        }
      }
    }

    this.updateStatus(false, nextStatus);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.cancelNextCallback();
  };

  _proto.getTimeouts = function getTimeouts() {
    var timeout = this.props.timeout;
    var exit, enter, appear;
    exit = enter = appear = timeout;

    if (timeout != null && typeof timeout !== 'number') {
      exit = timeout.exit;
      enter = timeout.enter; // TODO: remove fallback for next major

      appear = timeout.appear !== undefined ? timeout.appear : enter;
    }

    return {
      exit: exit,
      enter: enter,
      appear: appear
    };
  };

  _proto.updateStatus = function updateStatus(mounting, nextStatus) {
    if (mounting === void 0) {
      mounting = false;
    }

    if (nextStatus !== null) {
      // nextStatus will always be ENTERING or EXITING.
      this.cancelNextCallback();
      var node = ReactDOM.findDOMNode(this);

      if (nextStatus === ENTERING) {
        this.performEnter(node, mounting);
      } else {
        this.performExit(node);
      }
    } else if (this.props.unmountOnExit && this.state.status === EXITED) {
      this.setState({
        status: UNMOUNTED
      });
    }
  };

  _proto.performEnter = function performEnter(node, mounting) {
    var _this2 = this;

    var enter = this.props.enter;
    var appearing = this.context ? this.context.isMounting : mounting;
    var timeouts = this.getTimeouts();
    var enterTimeout = appearing ? timeouts.appear : timeouts.enter; // no enter animation skip right to ENTERED
    // if we are mounting and running this it means appear _must_ be set

    if (!mounting && !enter || config.disabled) {
      this.safeSetState({
        status: ENTERED
      }, function () {
        _this2.props.onEntered(node);
      });
      return;
    }

    this.props.onEnter(node, appearing);
    this.safeSetState({
      status: ENTERING
    }, function () {
      _this2.props.onEntering(node, appearing);

      _this2.onTransitionEnd(node, enterTimeout, function () {
        _this2.safeSetState({
          status: ENTERED
        }, function () {
          _this2.props.onEntered(node, appearing);
        });
      });
    });
  };

  _proto.performExit = function performExit(node) {
    var _this3 = this;

    var exit = this.props.exit;
    var timeouts = this.getTimeouts(); // no exit animation skip right to EXITED

    if (!exit || config.disabled) {
      this.safeSetState({
        status: EXITED
      }, function () {
        _this3.props.onExited(node);
      });
      return;
    }

    this.props.onExit(node);
    this.safeSetState({
      status: EXITING
    }, function () {
      _this3.props.onExiting(node);

      _this3.onTransitionEnd(node, timeouts.exit, function () {
        _this3.safeSetState({
          status: EXITED
        }, function () {
          _this3.props.onExited(node);
        });
      });
    });
  };

  _proto.cancelNextCallback = function cancelNextCallback() {
    if (this.nextCallback !== null) {
      this.nextCallback.cancel();
      this.nextCallback = null;
    }
  };

  _proto.safeSetState = function safeSetState(nextState, callback) {
    // This shouldn't be necessary, but there are weird race conditions with
    // setState callbacks and unmounting in testing, so always make sure that
    // we can cancel any pending setState callbacks after we unmount.
    callback = this.setNextCallback(callback);
    this.setState(nextState, callback);
  };

  _proto.setNextCallback = function setNextCallback(callback) {
    var _this4 = this;

    var active = true;

    this.nextCallback = function (event) {
      if (active) {
        active = false;
        _this4.nextCallback = null;
        callback(event);
      }
    };

    this.nextCallback.cancel = function () {
      active = false;
    };

    return this.nextCallback;
  };

  _proto.onTransitionEnd = function onTransitionEnd(node, timeout, handler) {
    this.setNextCallback(handler);
    var doesNotHaveTimeoutOrListener = timeout == null && !this.props.addEndListener;

    if (!node || doesNotHaveTimeoutOrListener) {
      setTimeout(this.nextCallback, 0);
      return;
    }

    if (this.props.addEndListener) {
      this.props.addEndListener(node, this.nextCallback);
    }

    if (timeout != null) {
      setTimeout(this.nextCallback, timeout);
    }
  };

  _proto.render = function render() {
    var status = this.state.status;

    if (status === UNMOUNTED) {
      return null;
    }

    var _this$props = this.props,
        children = _this$props.children,
        childProps = _objectWithoutPropertiesLoose(_this$props, ["children"]); // filter props for Transtition


    delete childProps.in;
    delete childProps.mountOnEnter;
    delete childProps.unmountOnExit;
    delete childProps.appear;
    delete childProps.enter;
    delete childProps.exit;
    delete childProps.timeout;
    delete childProps.addEndListener;
    delete childProps.onEnter;
    delete childProps.onEntering;
    delete childProps.onEntered;
    delete childProps.onExit;
    delete childProps.onExiting;
    delete childProps.onExited;

    if (typeof children === 'function') {
      // allows for nested Transitions
      return React__default.createElement(TransitionGroupContext.Provider, {
        value: null
      }, children(status, childProps));
    }

    var child = React__default.Children.only(children);
    return (// allows for nested Transitions
      React__default.createElement(TransitionGroupContext.Provider, {
        value: null
      }, React__default.cloneElement(child, childProps))
    );
  };

  return Transition;
}(React__default.Component);

Transition.contextType = TransitionGroupContext;
Transition.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A `function` child can be used instead of a React element. This function is
   * called with the current transition status (`'entering'`, `'entered'`,
   * `'exiting'`, `'exited'`), which can be used to apply context
   * specific props to a component.
   *
   * ```jsx
   * <Transition in={this.state.in} timeout={150}>
   *   {state => (
   *     <MyComponent className={`fade fade-${state}`} />
   *   )}
   * </Transition>
   * ```
   */
  children: propTypes.oneOfType([propTypes.func.isRequired, propTypes.element.isRequired]).isRequired,

  /**
   * Show the component; triggers the enter or exit states
   */
  in: propTypes.bool,

  /**
   * By default the child component is mounted immediately along with
   * the parent `Transition` component. If you want to "lazy mount" the component on the
   * first `in={true}` you can set `mountOnEnter`. After the first enter transition the component will stay
   * mounted, even on "exited", unless you also specify `unmountOnExit`.
   */
  mountOnEnter: propTypes.bool,

  /**
   * By default the child component stays mounted after it reaches the `'exited'` state.
   * Set `unmountOnExit` if you'd prefer to unmount the component after it finishes exiting.
   */
  unmountOnExit: propTypes.bool,

  /**
   * Normally a component is not transitioned if it is shown when the
   * `<Transition>` component mounts. If you want to transition on the first
   * mount set `appear` to `true`, and the component will transition in as soon
   * as the `<Transition>` mounts.
   *
   * > **Note**: there are no special appear states like `appearing`/`appeared`, this prop
   * > only adds an additional enter transition. However, in the
   * > `<CSSTransition>` component that first enter transition does result in
   * > additional `.appear-*` classes, that way you can choose to style it
   * > differently.
   */
  appear: propTypes.bool,

  /**
   * Enable or disable enter transitions.
   */
  enter: propTypes.bool,

  /**
   * Enable or disable exit transitions.
   */
  exit: propTypes.bool,

  /**
   * The duration of the transition, in milliseconds.
   * Required unless `addEndListener` is provided.
   *
   * You may specify a single timeout for all transitions:
   *
   * ```jsx
   * timeout={500}
   * ```
   *
   * or individually:
   *
   * ```jsx
   * timeout={{
   *  appear: 500,
   *  enter: 300,
   *  exit: 500,
   * }}
   * ```
   *
   * - `appear` defaults to the value of `enter`
   * - `enter` defaults to `0`
   * - `exit` defaults to `0`
   *
   * @type {number | { enter?: number, exit?: number, appear?: number }}
   */
  timeout: function timeout(props) {
    var pt = timeoutsShape;
    if (!props.addEndListener) pt = pt.isRequired;

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return pt.apply(void 0, [props].concat(args));
  },

  /**
   * Add a custom transition end trigger. Called with the transitioning
   * DOM node and a `done` callback. Allows for more fine grained transition end
   * logic. **Note:** Timeouts are still used as a fallback if provided.
   *
   * ```jsx
   * addEndListener={(node, done) => {
   *   // use the css transitionend event to mark the finish of a transition
   *   node.addEventListener('transitionend', done, false);
   * }}
   * ```
   */
  addEndListener: propTypes.func,

  /**
   * Callback fired before the "entering" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool) -> void
   */
  onEnter: propTypes.func,

  /**
   * Callback fired after the "entering" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntering: propTypes.func,

  /**
   * Callback fired after the "entered" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool) -> void
   */
  onEntered: propTypes.func,

  /**
   * Callback fired before the "exiting" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExit: propTypes.func,

  /**
   * Callback fired after the "exiting" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExiting: propTypes.func,

  /**
   * Callback fired after the "exited" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExited: propTypes.func // Name the function so it is clearer in the documentation

} : {};

function noop() {}

Transition.defaultProps = {
  in: false,
  mountOnEnter: false,
  unmountOnExit: false,
  appear: false,
  enter: true,
  exit: true,
  onEnter: noop,
  onEntering: noop,
  onEntered: noop,
  onExit: noop,
  onExiting: noop,
  onExited: noop
};
Transition.UNMOUNTED = 0;
Transition.EXITED = 1;
Transition.ENTERING = 2;
Transition.ENTERED = 3;
Transition.EXITING = 4;

var _addClass = function addClass$1(node, classes) {
  return node && classes && classes.split(' ').forEach(function (c) {
    return addClass(node, c);
  });
};

var removeClass$1 = function removeClass$1(node, classes) {
  return node && classes && classes.split(' ').forEach(function (c) {
    return removeClass(node, c);
  });
};
/**
 * A transition component inspired by the excellent
 * [ng-animate](http://www.nganimate.org/) library, you should use it if you're
 * using CSS transitions or animations. It's built upon the
 * [`Transition`](https://reactcommunity.org/react-transition-group/transition)
 * component, so it inherits all of its props.
 *
 * `CSSTransition` applies a pair of class names during the `appear`, `enter`,
 * and `exit` states of the transition. The first class is applied and then a
 * second `*-active` class in order to activate the CSS transition. After the
 * transition, matching `*-done` class names are applied to persist the
 * transition state.
 *
 * ```jsx
 * function App() {
 *   const [inProp, setInProp] = useState(false);
 *   return (
 *     <div>
 *       <CSSTransition in={inProp} timeout={200} classNames="my-node">
 *         <div>
 *           {"I'll receive my-node-* classes"}
 *         </div>
 *       </CSSTransition>
 *       <button type="button" onClick={() => setInProp(true)}>
 *         Click to Enter
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * When the `in` prop is set to `true`, the child component will first receive
 * the class `example-enter`, then the `example-enter-active` will be added in
 * the next tick. `CSSTransition` [forces a
 * reflow](https://github.com/reactjs/react-transition-group/blob/5007303e729a74be66a21c3e2205e4916821524b/src/CSSTransition.js#L208-L215)
 * between before adding the `example-enter-active`. This is an important trick
 * because it allows us to transition between `example-enter` and
 * `example-enter-active` even though they were added immediately one after
 * another. Most notably, this is what makes it possible for us to animate
 * _appearance_.
 *
 * ```css
 * .my-node-enter {
 *   opacity: 0;
 * }
 * .my-node-enter-active {
 *   opacity: 1;
 *   transition: opacity 200ms;
 * }
 * .my-node-exit {
 *   opacity: 1;
 * }
 * .my-node-exit-active {
 *   opacity: 0;
 *   transition: opacity 200ms;
 * }
 * ```
 *
 * `*-active` classes represent which styles you want to animate **to**.
 *
 * **Note**: If you're using the
 * [`appear`](http://reactcommunity.org/react-transition-group/transition#Transition-prop-appear)
 * prop, make sure to define styles for `.appear-*` classes as well.
 */


var CSSTransition =
/*#__PURE__*/
function (_React$Component) {
  _inheritsLoose(CSSTransition, _React$Component);

  function CSSTransition() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.appliedClasses = {
      appear: {},
      enter: {},
      exit: {}
    };

    _this.onEnter = function (node, appearing) {
      _this.removeClasses(node, 'exit');

      _this.addClass(node, appearing ? 'appear' : 'enter', 'base');

      if (_this.props.onEnter) {
        _this.props.onEnter(node, appearing);
      }
    };

    _this.onEntering = function (node, appearing) {
      var type = appearing ? 'appear' : 'enter';

      _this.addClass(node, type, 'active');

      if (_this.props.onEntering) {
        _this.props.onEntering(node, appearing);
      }
    };

    _this.onEntered = function (node, appearing) {
      var type = appearing ? 'appear' : 'enter';

      _this.removeClasses(node, type);

      _this.addClass(node, type, 'done');

      if (_this.props.onEntered) {
        _this.props.onEntered(node, appearing);
      }
    };

    _this.onExit = function (node) {
      _this.removeClasses(node, 'appear');

      _this.removeClasses(node, 'enter');

      _this.addClass(node, 'exit', 'base');

      if (_this.props.onExit) {
        _this.props.onExit(node);
      }
    };

    _this.onExiting = function (node) {
      _this.addClass(node, 'exit', 'active');

      if (_this.props.onExiting) {
        _this.props.onExiting(node);
      }
    };

    _this.onExited = function (node) {
      _this.removeClasses(node, 'exit');

      _this.addClass(node, 'exit', 'done');

      if (_this.props.onExited) {
        _this.props.onExited(node);
      }
    };

    _this.getClassNames = function (type) {
      var classNames = _this.props.classNames;
      var isStringClassNames = typeof classNames === 'string';
      var prefix = isStringClassNames && classNames ? classNames + "-" : '';
      var baseClassName = isStringClassNames ? "" + prefix + type : classNames[type];
      var activeClassName = isStringClassNames ? baseClassName + "-active" : classNames[type + "Active"];
      var doneClassName = isStringClassNames ? baseClassName + "-done" : classNames[type + "Done"];
      return {
        baseClassName: baseClassName,
        activeClassName: activeClassName,
        doneClassName: doneClassName
      };
    };

    return _this;
  }

  var _proto = CSSTransition.prototype;

  _proto.addClass = function addClass(node, type, phase) {
    var className = this.getClassNames(type)[phase + "ClassName"];

    if (type === 'appear' && phase === 'done') {
      className += " " + this.getClassNames('enter').doneClassName;
    } // This is for to force a repaint,
    // which is necessary in order to transition styles when adding a class name.


    if (phase === 'active') {
      /* eslint-disable no-unused-expressions */
      node && node.scrollTop;
    }

    this.appliedClasses[type][phase] = className;

    _addClass(node, className);
  };

  _proto.removeClasses = function removeClasses(node, type) {
    var _this$appliedClasses$ = this.appliedClasses[type],
        baseClassName = _this$appliedClasses$.base,
        activeClassName = _this$appliedClasses$.active,
        doneClassName = _this$appliedClasses$.done;
    this.appliedClasses[type] = {};

    if (baseClassName) {
      removeClass$1(node, baseClassName);
    }

    if (activeClassName) {
      removeClass$1(node, activeClassName);
    }

    if (doneClassName) {
      removeClass$1(node, doneClassName);
    }
  };

  _proto.render = function render() {
    var _this$props = this.props,
        _ = _this$props.classNames,
        props = _objectWithoutPropertiesLoose(_this$props, ["classNames"]);

    return React__default.createElement(Transition, _extends$1({}, props, {
      onEnter: this.onEnter,
      onEntered: this.onEntered,
      onEntering: this.onEntering,
      onExit: this.onExit,
      onExiting: this.onExiting,
      onExited: this.onExited
    }));
  };

  return CSSTransition;
}(React__default.Component);

CSSTransition.defaultProps = {
  classNames: ''
};
CSSTransition.propTypes = process.env.NODE_ENV !== "production" ? _extends$1({}, Transition.propTypes, {
  /**
   * The animation classNames applied to the component as it appears, enters,
   * exits or has finished the transition. A single name can be provided and it
   * will be suffixed for each stage: e.g.
   *
   * `classNames="fade"` applies `fade-appear`, `fade-appear-active`,
   * `fade-appear-done`, `fade-enter`, `fade-enter-active`, `fade-enter-done`,
   * `fade-exit`, `fade-exit-active`, and `fade-exit-done`.
   *
   * **Note**: `fade-appear-done` and `fade-enter-done` will _both_ be applied.
   * This allows you to define different behavior for when appearing is done and
   * when regular entering is done, using selectors like
   * `.fade-enter-done:not(.fade-appear-done)`. For example, you could apply an
   * epic entrance animation when element first appears in the DOM using
   * [Animate.css](https://daneden.github.io/animate.css/). Otherwise you can
   * simply use `fade-enter-done` for defining both cases.
   *
   * Each individual classNames can also be specified independently like:
   *
   * ```js
   * classNames={{
   *  appear: 'my-appear',
   *  appearActive: 'my-active-appear',
   *  appearDone: 'my-done-appear',
   *  enter: 'my-enter',
   *  enterActive: 'my-active-enter',
   *  enterDone: 'my-done-enter',
   *  exit: 'my-exit',
   *  exitActive: 'my-active-exit',
   *  exitDone: 'my-done-exit',
   * }}
   * ```
   *
   * If you want to set these classes using CSS Modules:
   *
   * ```js
   * import styles from './styles.css';
   * ```
   *
   * you might want to use camelCase in your CSS file, that way could simply
   * spread them instead of listing them one by one:
   *
   * ```js
   * classNames={{ ...styles }}
   * ```
   *
   * @type {string | {
   *  appear?: string,
   *  appearActive?: string,
   *  appearDone?: string,
   *  enter?: string,
   *  enterActive?: string,
   *  enterDone?: string,
   *  exit?: string,
   *  exitActive?: string,
   *  exitDone?: string,
   * }}
   */
  classNames: classNamesShape,

  /**
   * A `<Transition>` callback fired immediately after the 'enter' or 'appear' class is
   * applied.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEnter: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'enter-active' or
   * 'appear-active' class is applied.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntering: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'enter' or
   * 'appear' classes are **removed** and the `done` class is added to the DOM node.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntered: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit' class is
   * applied.
   *
   * @type Function(node: HtmlElement)
   */
  onExit: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit-active' is applied.
   *
   * @type Function(node: HtmlElement)
   */
  onExiting: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit' classes
   * are **removed** and the `exit-done` class is added to the DOM node.
   *
   * @type Function(node: HtmlElement)
   */
  onExited: propTypes.func
}) : {};

const componentConfigThemeKey$f = 'menuSubLink';

const getMenuSubLinkStyle = vrLineHeight => StyleSheet.create({
  style: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 1.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 1.5}px`
    }
  }
});

const MenuSubLinkComponent = props => {
  const {
    href,
    text,
    idleColor,
    vrLineHeight
  } = props;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor
  });
  const style = getMenuSubLinkStyle(vrLineHeight);
  return React__default.createElement("li", {
    className: css(style.style)
  }, React__default.createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, React__default.createElement("p", null, text)));
};

const mapThemeProps$n = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    idleColor: theme.idleColor
  };
};

const MenuSubLink = props => {
  const themeContext = useTheme(componentConfigThemeKey$f, mapThemeProps$n);
  return React__default.createElement(MenuSubLinkComponent, _extends({}, themeContext, props));
};

const animationFactor = 100;
const menuEnterDuration = animationFactor * 2;
const menuExitDuration = animationFactor * 3;
const menuExitDelay = animationFactor * 3;
const menuEnterTimeOut = menuEnterDuration;
const menuExitTimeOut = menuExitDuration + menuExitDelay;
const linkEnterDuration = animationFactor * 2;
const linkEnterDelay = animationFactor;
const linkExitDuration = animationFactor * 3;
const linkExitDelay = animationFactor * 3;
const linkEnterTimeOut = linkEnterDuration + linkEnterDelay;
const linkExitTimeOut = linkExitDuration + linkExitDelay;
const chevronIconEnterDuration = animationFactor * 3;
const chevronIconEnterDelay = animationFactor * 3;
const chevronIconExitDuration = animationFactor * 3;
const chevronIconEnterTimeOut = chevronIconEnterDuration + chevronIconEnterDelay;
const chevronIconExitTimeOut = chevronIconExitDuration;
const subLinkEnterDuration = animationFactor * 2;
const subLinksTotalDelay = animationFactor * 2;
const subLinkEnterTimeOut = subLinkEnterDuration + subLinksTotalDelay;

const componentConfigThemeKey$g = 'menuLink';

const getMenuLinkStyle = (vrLineHeight, linkColor) => StyleSheet.create({
  style: {
    color: linkColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  enter: {
    opacity: 0
  },
  enterActive: {
    opacity: 1,
    transition: `opacity ${chevronIconEnterDuration}ms ease-out`,
    transitionDelay: `${chevronIconEnterDelay}ms`
  },
  exit: {
    opacity: 1
  },
  exitActive: {
    opacity: 0,
    transition: `opacity ${chevronIconExitDuration}ms ease-in`
  },
  chevronIcon: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse'
  }
});

const getSubLinkStyle = (index, numberOfSubLinks, currentSubLinkDelay) => {
  return StyleSheet.create({
    enter: {
      opacity: 0
    },
    enterActive: {
      opacity: 1,
      transition: `opacity ${subLinkEnterDuration}ms ease-out`,
      transitionDelay: `${currentSubLinkDelay * index}ms`
    }
  });
};

const createMenuSubLinks = (numberOfSubLinks, isLinkOpen) => (link, index) => {
  const style = getSubLinkStyle(index, numberOfSubLinks, subLinksTotalDelay / numberOfSubLinks);
  return React__default.createElement(CSSTransition, {
    key: index,
    in: isLinkOpen,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: subLinkEnterTimeOut,
      exit: 0
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive)
    }
  }, React__default.createElement(MenuSubLink, {
    href: link.href,
    text: link.text
  }));
};

const MenuLinkComponent = props => {
  const {
    href,
    text,
    onClick,
    index,
    idleColor,
    activeStateColor,
    isLinkOpen,
    vrLineHeight,
    childrenLinks,
    startChevronIconAnimation
  } = props;
  const linkColor = isLinkOpen ? activeStateColor : idleColor;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: linkColor
  });
  const style = getMenuLinkStyle(vrLineHeight, linkColor);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("li", null, React__default.createElement("div", {
    className: css(style.style)
  }, React__default.createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, React__default.createElement("h4", null, text)), childrenLinks.length !== 0 ? React__default.createElement("div", {
    className: css(style.chevronIcon),
    onClick: () => onClick(index)
  }, React__default.createElement(CSSTransition, {
    in: startChevronIconAnimation,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: chevronIconEnterTimeOut,
      exit: chevronIconExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, React__default.createElement(ChevronIcon, {
    direction: isLinkOpen ? Directions.UP : Directions.DOWN,
    color: linkColor
  }))) : null), React__default.createElement("ul", null, childrenLinks.map(createMenuSubLinks(childrenLinks.length, isLinkOpen)))));
};

const mapThemeProps$o = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor
  };
};

const MenuLink = props => {
  const themeContext = useTheme(componentConfigThemeKey$g, mapThemeProps$o);
  return React__default.createElement(MenuLinkComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$h = 'menuLinks';

const getMenuLinksStyle = (backgroundColor, dividerColor, vrLineHeight) => StyleSheet.create({
  style: {
    backgroundColor,
    position: 'fixed',
    width: '100%',
    height: `calc(100vh - ${primaryHeaderHeight}px)`,
    zIndex: 2,
    top: `${primaryHeaderHeight}px`,
    left: 0,
    overflowY: 'auto'
  },
  enter: {
    opacity: 0,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${menuEnterDuration}ms, opacity ${menuEnterDuration}ms`,
    transitionTimingFunction: 'ease-out'
  },
  exit: {
    opacity: 1,
    transform: 'translate(0)'
  },
  exitActive: {
    opacity: 0,
    transform: 'translate(-100%)',
    transition: `transform ${menuExitDuration}ms ease-in , opacity ${menuExitDuration}ms ease-in`,
    transitionDelay: `${menuExitDelay}ms`
  },
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const getMenuLinkStyle$1 = () => StyleSheet.create({
  enter: {
    opacity: 0.5,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${linkEnterDuration}ms ease-out, opacity ${linkEnterDuration}ms ease-in`,
    transitionDelay: `${linkEnterDelay}ms`
  },
  exit: {
    transform: 'translate(0)',
    opacity: 1
  },
  exitActive: {
    opacity: 0.5,
    transform: 'translate(-100%)',
    transition: `transform ${linkExitDuration}ms ease-in, opacity ${linkExitDuration}ms ease-in`,
    transitionDelay: `${linkExitDelay}ms`
  }
});

const createMenuLink = (onClick, currentOpenLink, shouldAppear, startIndex = 0) => (link, index) => {
  const style = getMenuLinkStyle$1();
  return createElement(CSSTransition, {
    key: index,
    unmountOnExit: true,
    mountOnEnter: true,
    in: shouldAppear,
    timeout: {
      enter: linkEnterTimeOut,
      exit: linkExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, state => createElement(MenuLink, {
    key: index,
    href: link.href,
    text: link.text,
    childrenLinks: link.childrenLinks,
    index: startIndex + index,
    onClick: onClick,
    isLinkOpen: currentOpenLink !== null && currentOpenLink === startIndex + index,
    startChevronIconAnimation: state === 'entering' || state === 'entered'
  }));
};
const createEditionsLinks = (editions, editionEndpoint) => {
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  return [{
    href: activeEdition.href,
    text: activeEdition.text,
    childrenLinks: editions.map(edition => ({
      href: edition.href,
      text: edition.text
    }))
  }];
};
const MenuLinksComponent = props => {
  const {
    backgroundColor,
    dividerColor,
    vrLineHeight,
    menuLinks,
    editions,
    isMenuOpen,
    onClick,
    currentOpenLink,
    editionEndpoint
  } = props;
  const style = getMenuLinksStyle(backgroundColor, dividerColor, vrLineHeight);
  const editionsLinks = editions ? createEditionsLinks(editions, editionEndpoint) : [];
  return createElement(CSSTransition, {
    in: isMenuOpen,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: menuEnterTimeOut,
      exit: menuExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, state => createElement("ul", {
    className: css(style.style)
  }, menuLinks.map(createMenuLink(onClick, currentOpenLink, state === 'entering' || state === 'entered')), createElement("div", {
    className: css(style.divider)
  }), editionsLinks.map(createMenuLink(onClick, currentOpenLink, state === 'entering' || state === 'entered', menuLinks.length))));
};

const mapThemeProps$p = theme => {
  return {
    backgroundColor: theme.backgroundColor,
    dividerColor: theme.dividerColor,
    vrLineHeight: theme.vrLineHeight
  };
};

const MenuLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$h, mapThemeProps$p);
  return createElement(ErrorBoundary, null, createElement(MenuLinksComponent, _extends({}, themeContext, props)));
};

const getMenuStyle = () => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      display: 'inline-flex'
    }
  }
});

class Menu extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "clickOnMenuIcon", () => {
      const {
        isMenuOpen,
        currentOpenLink
      } = this.state;
      this.setState({
        isMenuOpen: !isMenuOpen,
        currentOpenLink: isMenuOpen ? currentOpenLink : null
      });
    });

    _defineProperty(this, "clickOnLinkDropDown", index => {
      const {
        currentOpenLink
      } = this.state;
      this.setState({
        currentOpenLink: currentOpenLink === index ? null : index
      });
    });

    this.state = {
      isMenuOpen: false,
      currentOpenLink: null
    };
  }

  render() {
    const {
      isMenuOpen,
      currentOpenLink
    } = this.state;
    const {
      menu,
      editions,
      editionEndpoint
    } = this.props;
    const style = getMenuStyle();
    return createElement("div", {
      className: css(style.style)
    }, createElement(MenuIcon, {
      isMenuOpen: isMenuOpen,
      onClick: this.clickOnMenuIcon
    }), createElement(MenuLinks, {
      menuLinks: menu,
      editions: editions,
      isMenuOpen: isMenuOpen,
      onClick: this.clickOnLinkDropDown,
      currentOpenLink: currentOpenLink,
      editionEndpoint: editionEndpoint
    }));
  }

}

const embeddedVideoLogoPaddingInPx = 20;

const getInjectedVideoLogoStyle = () => {
  return StyleSheet.create({
    wrapper: {
      height: `${primaryHeaderHeight}px`
    },
    video: {
      height: '100%',
      alignItems: 'center',
      display: 'inline-flex',
      margin: `0 -${embeddedVideoLogoPaddingInPx}px`
    },
    source: {
      height: '100%'
    }
  });
};

const VideoLogo = props => {
  const {
    videoPath,
    href
  } = props;
  const style = getInjectedVideoLogoStyle();
  React__default.useEffect(() => {
    setEventEmitter('injectVideoLogoSuccessfully', callback => callback());
  }, [videoPath]);
  return React__default.createElement(Link, {
    className: css(style.wrapper),
    href: href
  }, React__default.createElement("video", {
    className: css(style.video),
    loop: true,
    autoPlay: true,
    muted: true
  }, React__default.createElement("source", {
    className: css(style.source),
    src: videoPath,
    type: "video/mp4"
  })));
};

const componentConfigThemeKey$i = 'primaryHeader';

const getStyle$7 = (vrLineHeight, backgroundColor) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${primaryHeaderHeight}px`,
      display: 'flex',
      justifyContent: 'space-between',
      zIndex: 100
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    },
    itemsLeftContainer: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    }
  });
};

const PrimaryHeaderComponent = props => {
  const {
    vrLineHeight,
    backgroundColor,
    primaryLinks,
    primaryLogos,
    editions,
    surfaceStyle,
    cta,
    menu,
    videoLogo,
    pagePath,
    editionEndpoint
  } = props;
  const style = getStyle$7(vrLineHeight, backgroundColor);
  return React__default.createElement("div", {
    className: css(style.header, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle)
  }, React__default.createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsLeftContainer)
  }, menu ? React__default.createElement(Menu, {
    menu: menu,
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null, videoLogo ? React__default.createElement(VideoLogo, _extends({}, videoLogo, {
    href: primaryLogos.href
  })) : React__default.createElement(Logo, primaryLogos), primaryLinks ? React__default.createElement(PrimaryLinks, {
    links: primaryLinks,
    pagePath: pagePath
  }) : null), React__default.createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsRightContainer)
  }, cta ? React__default.createElement(CtaButton, cta) : null, editions ? React__default.createElement(Editions, {
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null));
};

const mapThemeProps$q = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle
  };
};

const PrimaryHeader = props => {
  const themeContext = useTheme(componentConfigThemeKey$i, mapThemeProps$q);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(PrimaryHeaderComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$j = 'moreLinks';

const getStyle$8 = theme => {
  const {
    titleColor,
    menuBackground
  } = theme;
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      height: '100%',
      display: 'flex'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none'
    },
    buttonTitle: {
      color: titleColor,
      marginRight: '9px'
    },
    menuBody: {
      listStyle: 'none',
      width: '100%',
      position: 'absolute',
      backgroundColor: menuBackground
    }
  });
};

class MoreLinksComponent extends Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      menuOpen: false
    });

    _defineProperty(this, "toggleMenu", () => {
      const {
        menuOpen
      } = this.state;
      this.setState({
        menuOpen: !menuOpen
      });
    });
  }

  render() {
    const {
      moreLinksText,
      links,
      pagePath
    } = this.props;
    const {
      menuOpen
    } = this.state;
    const style = getStyle$8(this.props);
    const titleStyle = StyleSheet.create(getGeneratedGlobalStyles().p());
    const chevronDirection = menuOpen ? Directions.UP : Directions.DOWN;
    return createElement("div", {
      className: css(style.wrapper)
    }, createElement("button", {
      type: "button",
      className: css(style.button),
      onClick: this.toggleMenu
    }, createElement("span", {
      className: css(titleStyle.style, style.buttonTitle)
    }, moreLinksText), createElement(ChevronIcon, {
      direction: chevronDirection
    })), menuOpen && createElement(DropDownMenu, {
      configThemeKey: "moreLinks",
      menuLinks: links,
      highlightActive: true,
      activeEndpoint: pagePath
    }));
  }

}

const mapThemeToProps$8 = theme => ({
  titleColor: theme.titleColor,
  menuBackground: theme.menuBackground,
  linkColor: theme.linkColor,
  hoverColor: theme.hoverColor
});

const MoreLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$j, mapThemeToProps$8);
  return createElement(MoreLinksComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$k = 'secondaryLinks';

const getTextStyle = (vrLineHeight, isLastLink) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: isLastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px` : 0
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: isLastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px` : 0
      }
    }
  });
};

const getContainerStyle$1 = vrLineHeight => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        overflowX: 'auto'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        overflowX: 'auto'
      }
    },
    dropDownMenuWrapper: {
      height: '100%',
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      }
    }
  });
};

const isLastLink = (index, linksLength) => index + 1 === linksLength;
class SecondaryLinksComponent extends React__default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      isHoveredLink: false
    });

    _defineProperty(this, "createLink", ({
      idleColor,
      activeStateColor,
      hoverStateColor,
      vrLineHeight,
      hoverOtherLinksColors
    }, linksLength, isHoveredLink, pagePath) => (linkData, index) => {
      const isActiveLink = linkData.href === pagePath;
      const {
        maxVisibleLinks = 6
      } = this.props;
      const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
      const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;
      const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1 ? {
        [MEDIA_BREAKPOINTS.LARGE]: {
          display: 'none'
        },
        [MEDIA_BREAKPOINTS.X_LARGE]: {
          display: 'none'
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          display: 'inline-flex'
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          display: 'inline-flex'
        }
      } : {};
      const linkStyle = generateLinkStyleFromTheme({
        idleColor: calculatedIdleColor,
        activeStateColor,
        hoverStateColor: calculatedHoverColor
      }, linkStyleBasedOnScreen);
      const style = getTextStyle(vrLineHeight, isLastLink(index, linksLength));
      const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
      return React__default.createElement(TextLinkComponent, {
        key: index,
        href: linkData.href,
        linkStyle: linkStyle
      }, React__default.createElement("span", {
        className: css(contentStyle.style, style.link)
      }, linkData.text));
    });

    _defineProperty(this, "onLinkHover", () => {
      this.setState({
        isHoveredLink: true
      });
    });

    _defineProperty(this, "onLinkLeave", () => {
      this.setState({
        isHoveredLink: false
      });
    });
  }

  render() {
    const {
      links,
      vrLineHeight,
      moreLinksText,
      maxVisibleLinks = 6,
      pagePath
    } = this.props;
    const {
      isHoveredLink
    } = this.state;
    const style = getContainerStyle$1(vrLineHeight);
    const moreLinks = links.length > maxVisibleLinks ? links.slice(maxVisibleLinks - 1) : [];
    return React__default.createElement("div", {
      className: css(style.container)
    }, React__default.createElement("div", {
      className: css(style.container),
      onMouseEnter: this.onLinkHover,
      onMouseLeave: this.onLinkLeave
    }, links.map(this.createLink(this.props, links.length, isHoveredLink, pagePath))), React__default.createElement("div", {
      className: css(style.dropDownMenuWrapper)
    }, moreLinks.length > 0 && React__default.createElement(MoreLinks, {
      moreLinksText: moreLinksText,
      links: moreLinks,
      pagePath: pagePath
    })));
  }

}

const mapThemeProps$r = theme => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    vrLineHeight: theme.vrLineHeight,
    hoverOtherLinksColors: theme.hoverOtherLinksColors
  };
};

const SecondaryLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$k, mapThemeProps$r);
  return React__default.createElement(SecondaryLinksComponent, _extends({}, themeContext, props));
};

const AnimationDirection = {
  UP: 'foldingUp',
  DOWN: 'foldingDown',
  NONE: 'none'
};

const componentConfigThemeKey$l = 'secondaryHeader';
const maxVisibleLinksOnLargeAndXLarge = 6;

const getStyle$9 = (vrLineHeight, backgroundColor, animationDuration) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 50,
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(-100%)'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(-100%)'
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms'
      }
    }
  });
};

const getClassWithAnimations = (animation, baseStyle, surfaceStyle) => {
  switch (animation) {
    case AnimationDirection.UP:
      return css(baseStyle.header, surfaceStyle.surfaceStyle, baseStyle.foldingUp);

    case AnimationDirection.DOWN:
      return css(baseStyle.header, surfaceStyle.surfaceStyle, baseStyle.foldingDown);

    default:
      return css(baseStyle.header);
  }
};

const SecondaryHeaderComponent = props => {
  const {
    vrLineHeight,
    backgroundColor,
    surfaceStyle,
    secondaryLinks,
    secondaryLogos,
    moreLinksText,
    animationDuration = 0,
    animation = AnimationDirection.NONE,
    pagePath
  } = props;
  const style = getStyle$9(vrLineHeight, backgroundColor, animationDuration);
  const generatedSurfaceStyle = StyleSheet.create({
    surfaceStyle
  });
  const finalStyle = getClassWithAnimations(animation, style, generatedSurfaceStyle);
  return React__default.createElement("div", {
    className: finalStyle
  }, secondaryLogos ? React__default.createElement(Logo, secondaryLogos) : null, secondaryLinks ? React__default.createElement(SecondaryLinks, {
    links: secondaryLinks,
    maxVisibleLinks: maxVisibleLinksOnLargeAndXLarge,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null);
};

const mapThemeProps$s = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle
  };
};

const SecondaryHeader = props => {
  const themeContext = useTheme(componentConfigThemeKey$l, mapThemeProps$s);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(SecondaryHeaderComponent, _extends({}, themeContext, props)));
};

const getStyle$a = isSecondaryHeaderEnabled => {
  const height = getHeaderHeight(isSecondaryHeaderEnabled);
  return StyleSheet.create({
    anchor: {
      position: 'fixed',
      top: height,
      left: 0,
      right: 0,
      height: 0,
      transition: '800ms'
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        top: 0
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        top: 0
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        top: height
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        top: height
      }
    }
  });
};

const MontiAnchor = props => {
  const {
    isSecondaryHeaderEnabled,
    menuAnimation
  } = props;
  const style = getStyle$a(isSecondaryHeaderEnabled);
  let finalStyle;

  switch (menuAnimation) {
    case 'foldingUp':
      finalStyle = css(style.anchor, style.foldingUp);
      break;

    case 'foldingDown':
      finalStyle = css(style.anchor, style.foldingDown);
      break;

    default:
      finalStyle = css(style.anchor);
  }

  return createElement(ErrorBoundary, null, createElement("div", {
    className: `${finalStyle} video-top-anchor`
  }));
};

const animationDuration = 200;

const getStyle$b = () => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
      transform: 'translate3d(0, 0, 0)',
      transition: `${animationDuration}ms`
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        transform: 'translateY(0)'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        transform: 'translateY(0)'
      }
    }
  });
};

class Header extends React__default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "scrollTicking", false);

    _defineProperty(this, "firstMenuFoldThreshold", 640);

    _defineProperty(this, "secondMenuFoldThreshold", 100);

    _defineProperty(this, "state", {
      menuFirstFolded: false,
      menuFoldState: AnimationDirection.NONE
    });

    _defineProperty(this, "getMenuAnimation", originalScrollPosition => {
      const {
        menuFirstFolded,
        menuFoldState
      } = this.state;
      const currentScrollPosition = window.scrollY;
      const deltaY = currentScrollPosition - originalScrollPosition;

      if (currentScrollPosition > this.firstMenuFoldThreshold && !menuFirstFolded) {
        this.setState(() => ({
          menuFirstFolded: true
        }));
        return AnimationDirection.UP;
      }

      if (this.isScrollingUp(deltaY)) {
        return AnimationDirection.DOWN;
      }

      if (this.isScrollingDown(deltaY) && menuFirstFolded && currentScrollPosition > this.secondMenuFoldThreshold) {
        return AnimationDirection.UP;
      }

      return menuFoldState;
    });

    _defineProperty(this, "scrollHandler", () => {
      if (!this.scrollTicking) {
        const originalY = window.scrollY;
        setTimeout(() => {
          const {
            menuFoldState
          } = this.state;
          const menuAnimation = this.getMenuAnimation(originalY);

          if (menuAnimation !== menuFoldState) {
            this.setState(() => ({
              menuFoldState: menuAnimation
            }));
          }

          this.scrollTicking = false;
        }, 100);
      }

      this.scrollTicking = true;
    });
  }

  componentDidMount() {
    window.addEventListener('scroll', this.scrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollHandler);
  }

  getHeaderStyleWithAnimation() {
    const {
      menuFoldState
    } = this.state;
    const style = getStyle$b();

    if (menuFoldState === AnimationDirection.NONE) {
      return css(style.header);
    }

    const animationStyle = menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
    return css(style.header, animationStyle);
  }

  isScrollingUp(deltaY) {
    return deltaY < 0;
  }

  isScrollingDown(deltaY) {
    return deltaY > 0;
  }

  generateSecondaryHeader(secondaryHeader) {
    if (!secondaryHeader) {
      return null;
    }

    const {
      menuFoldState
    } = this.state;
    return React__default.createElement(SecondaryHeader, {
      moreLinksText: secondaryHeader.moreLinksText,
      secondaryLinks: secondaryHeader.secondaryLinks,
      secondaryLogos: secondaryHeader.secondaryLogos,
      animation: menuFoldState,
      animationDuration: animationDuration,
      pagePath: secondaryHeader.pagePath
    });
  }

  render() {
    const {
      primaryHeader,
      secondaryHeader
    } = this.props;
    const {
      primaryLinks,
      primaryLogos,
      editions,
      cta,
      menu,
      videoLogo,
      pagePath,
      editionEndpoint
    } = primaryHeader;
    const {
      menuFoldState
    } = this.state;
    return React__default.createElement(ErrorBoundary, null, React__default.createElement(MontiAnchor, {
      isSecondaryHeaderEnabled: !!secondaryHeader,
      menuAnimation: menuFoldState
    }), React__default.createElement("header", {
      className: this.getHeaderStyleWithAnimation()
    }, React__default.createElement(PrimaryHeader, {
      primaryLinks: primaryLinks,
      primaryLogos: primaryLogos,
      editions: editions,
      cta: cta,
      menu: menu,
      videoLogo: videoLogo,
      pagePath: pagePath,
      editionEndpoint: editionEndpoint
    }), this.generateSecondaryHeader(secondaryHeader)));
  }

}

const componentConfigThemeKey$m = 'errorPage';

const createErrorStyle = theme => {
  const {
    vrLineHeight,
    titleColor,
    textColor,
    buttonColor,
    buttonBackgroundColor
  } = theme;
  return StyleSheet.create({
    title: {
      textAlign: 'center',
      color: titleColor
    },
    secondaryTitle: {
      textAlign: 'center',
      color: titleColor,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: 0
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: 0
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    },
    content: {
      textAlign: 'center',
      color: textColor
    },
    buttonWrapper: {
      textAlign: 'center',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px 0`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0`
      }
    },
    button: {
      color: buttonColor,
      backgroundColor: buttonBackgroundColor,
      borderRadius: '16px',
      textDecoration: 'none',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4}px ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
      }
    }
  });
};

const createImageErrorStyle = theme => {
  const {
    vrLineHeight
  } = theme;
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        width: '300px',
        height: '300px',
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px auto ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px auto`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        width: '300px',
        height: '300px',
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px auto ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px auto`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        width: '180px',
        height: '180px',
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px auto ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px auto`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        width: '180px',
        height: '180px',
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px auto ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px auto`
      }
    }
  });
};

const ErrorComponent = props => {
  const {
    title,
    image,
    secondaryTitle,
    content,
    button
  } = props;
  const aspectRatio = {
    x: 1,
    y: 1
  };
  const errorStyle = createErrorStyle(props);
  const imageErrorStyle = createImageErrorStyle(props);
  return createElement(Fragment, null, createElement("div", {
    className: css(imageErrorStyle.style)
  }, createElement(Image, _extends({}, image, {
    additionalStyle: imageErrorStyle,
    aspectRatio: aspectRatio
  }))), createElement("h1", {
    className: css(errorStyle.title)
  }, title), createElement("h4", {
    className: css(errorStyle.secondaryTitle)
  }, secondaryTitle), createElement("p", {
    className: css(errorStyle.content)
  }, content), createElement("div", {
    className: css(errorStyle.buttonWrapper)
  }, createElement(Link, {
    href: button.href,
    className: css(errorStyle.button)
  }, createElement("span", null, button.text))));
};

const mapThemeProps$t = ({
  vrLineHeight,
  titleColor,
  textColor,
  buttonColor,
  buttonBackgroundColor
}) => ({
  vrLineHeight,
  titleColor,
  textColor,
  buttonColor,
  buttonBackgroundColor
});

const Error$1 = props => {
  const themeContext = useTheme(componentConfigThemeKey$m, mapThemeProps$t);
  return createElement(ErrorBoundary, null, createElement(ErrorComponent, _extends({}, themeContext, props)));
};

const createStyle$2 = () => StyleSheet.create({
  ad: {
    display: 'flex',
    justifyContent: 'center'
  }
});

class Ad extends Component {
  shouldComponentUpdate() {
    return false;
  }

  render() {
    const {
      id,
      additionalStyle
    } = this.props;
    const style = createStyle$2();
    return id ? createElement("div", {
      id: id,
      className: additionalStyle ? css(style.ad, additionalStyle.style) : css(style.ad)
    }) : null;
  }

}

const componentConfigurationKey$f = 'postMainContent';
const styleForFixedComponentWidth = {
  [MEDIA_BREAKPOINTS.X_LARGE]: {
    maxWidth: '100%',
    minWidth: '100%'
  },
  [MEDIA_BREAKPOINTS.LARGE]: {
    maxWidth: '100%',
    minWidth: '100%'
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    maxWidth: '100%',
    minWidth: '100%'
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    maxWidth: '100%',
    minWidth: '100%'
  }
};

const createStyle$3 = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    margin: '0 auto',
    overflow: 'hidden',
    ...styleForFixedComponentWidth
  }
});

const PostMainContentComponent = ({
  children,
  backgroundColor
}) => {
  const postMainContentStyle = createStyle$3(backgroundColor);
  return createElement("article", {
    className: css(postMainContentStyle.style)
  }, children);
};

const mapThemeToProps$9 = theme => ({
  backgroundColor: theme.backgroundColor
});

const PostMainContent = props => {
  const themeContext = useTheme(componentConfigurationKey$f, mapThemeToProps$9);
  return createElement(PostMainContentComponent, _extends({}, themeContext, props));
};

const componentConfigurationKey$g = 'postSidebar';

const createStyle$4 = vrLineHeight => {
  return StyleSheet.create({
    sidebar: {
      gridRowEnd: 'span 2',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridRowEnd: 'span 2',
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridRowEnd: 'span 2',
        padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    }
  });
};

const createAdditionalStyleForStickyAd = (headerHeight, vrLineHeight) => {
  return StyleSheet.create({
    style: {
      position: 'sticky',
      top: headerHeight,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    }
  });
};

const PostSidebarComponent = props => {
  const {
    adId1,
    adId2,
    isSecondaryHeaderEnabled,
    vrLineHeight
  } = props;
  const style = createStyle$4(vrLineHeight);
  const headerHeight = getHeaderHeight(isSecondaryHeaderEnabled);
  return createElement("div", {
    className: css(style.sidebar)
  }, createElement(Ad, {
    id: adId1
  }), createElement(Ad, {
    id: adId2,
    additionalStyle: createAdditionalStyleForStickyAd(headerHeight, vrLineHeight)
  }));
};

const mapThemeToProps$a = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const PostSidebar = props => {
  const themeContext = useTheme(componentConfigurationKey$g, mapThemeToProps$a);
  return createElement(PostSidebarComponent, _extends({}, themeContext, props));
};

const getStyle$c = () => {
  return StyleSheet.create({
    wrapper: {
      maxWidth: '1200px',
      margin: 'auto',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        width: '75%',
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        width: '100%',
        display: 'block'
      }
    }
  });
};

const ContentAndSidebar = props => {
  const {
    children
  } = props;
  const style = getStyle$c();
  return createElement(ErrorBoundary, null, createElement("div", {
    className: css(style.wrapper)
  }, children));
};

const getStyle$d = color => StyleSheet.create({
  author: {
    color
  }
});

const Author = props => {
  const {
    name,
    link,
    color,
    prefix = ''
  } = props;
  const style = getStyle$d(color);

  if (link) {
    return createElement(Fragment, null, prefix, createElement(Link, {
      href: link,
      className: css(style.author)
    }, name));
  }

  return createElement(Fragment, null, `${prefix}${name}`);
};

const getPrefix = index => {
  return index > 0 ? ', ' : '';
};

const AuthorsList = props => {
  const {
    authors,
    by,
    color
  } = props;
  return createElement(Fragment, null, `${by} `, authors.map((author, index) => createElement(Author, _extends({
    key: index
  }, author, {
    color: color,
    prefix: getPrefix(index)
  }))));
};

const componentConfigurationKey$h = 'articleTitle';

const createStyle$5 = ({
  titleColor,
  metaDataColor,
  vrLineHeight
}) => StyleSheet.create({
  wrapper: {
    position: 'relative'
  },
  title: {
    color: titleColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  metaData: {
    color: metaDataColor
  },
  sidesPadding: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      marginLeft: 0,
      marginRight: 0
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      marginLeft: 0,
      marginRight: 0
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      marginLeft: 0,
      marginRight: 0
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    }
  }
});

const shouldDisplayAuthors = authors => {
  return authors.length > 0;
};

const ArticleTitleComponent = props => {
  const {
    title,
    by,
    updatedAtFormatted,
    children,
    authors,
    metaDataColor
  } = props;
  const style = createStyle$5(props);
  const hasAuthors = shouldDisplayAuthors(authors);
  const separator = hasAuthors ? ' | ' : '';
  return createElement(Fragment, null, createElement("h1", {
    className: css(style.title, style.sidesPadding)
  }, title), createElement("div", {
    className: css(style.wrapper)
  }, createElement("h4", {
    className: css(style.metaData, style.sidesPadding)
  }, hasAuthors && createElement(AuthorsList, {
    by: by,
    authors: authors,
    color: metaDataColor
  }), `${separator}${updatedAtFormatted}`), children));
};

const mapThemeToProps$b = theme => ({
  titleColor: theme.titleColor,
  metaDataColor: theme.metaDataColor,
  vrLineHeight: theme.vrLineHeight
});

const ArticleTitle = props => {
  const themeContext = useTheme(componentConfigurationKey$h, mapThemeToProps$b);
  return createElement(ErrorBoundary, null, createElement(ArticleTitleComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$n = 'imageWithFigcaptionWrapper';
const getStyle$e = (aspectRatio, vrLineHeight, figcaptionTextColor, backgroundColor) => StyleSheet.create({
  base: {
    backgroundColor,
    position: 'relative',
    overflow: 'hidden',
    margin: 0,
    boxSizing: 'border-box',
    flex: '1 1 100%',
    minWidth: '0%',
    paddingTop: `calc(100% / (${aspectRatio.x / aspectRatio.y}))`
  },
  figcaption: {
    color: figcaptionTextColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
    }
  }
});
const ImageWithFigcaptionWrapperComponent = ({
  additionalStyle,
  aspectRatio,
  figcaption,
  vrLineHeight,
  figcaptionTextColor,
  backgroundColor,
  dataId,
  children
}) => {
  useViewabilityCallback(dataId);
  const imageWrapperStyle = getStyle$e(aspectRatio, vrLineHeight, figcaptionTextColor, backgroundColor);
  const figcaptionText = StyleSheet.create(getGeneratedGlobalStyles().p());
  return createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, createElement("div", {
    className: css(imageWrapperStyle.base)
  }, children), createElement("figcaption", {
    className: css(imageWrapperStyle.figcaption, figcaptionText.style)
  }, figcaption));
};

const mapThemeProps$u = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    figcaptionTextColor: theme.figcaptionTextColor,
    backgroundColor: theme.backgroundColor
  };
};

const ImageWithFigcaptionWrapper = props => {
  const themeContext = useTheme(componentConfigThemeKey$n, mapThemeProps$u);
  return createElement(ImageWithFigcaptionWrapperComponent, _extends({}, themeContext, props));
};

const ImageWithFigcaption = props => {
  const {
    host,
    path,
    description,
    aspectRatio = {
      x: 16,
      y: 9
    },
    additionalStyle,
    lazyLoad,
    figcaption,
    dataId
  } = props;
  const CalculatedImage = lazyLoad ? LazyImage : EagerImage;
  return createElement(ImageWithFigcaptionWrapper, {
    aspectRatio: aspectRatio,
    additionalStyle: additionalStyle,
    figcaption: figcaption,
    dataId: dataId
  }, createElement(CalculatedImage, getCalculatedThumbnail(aspectRatio, description, host, path)));
};

const componentConfigurationKey$i = 'iframe';

const createStyle$6 = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor
  }
});

const StyleForMontiElement = () => {
  return (
    /* eslint-disable-next-line react/no-danger */
    createElement("style", {
      dangerouslySetInnerHTML: {
        __html: `
  .monti-placeholder-mm-article-player {
    margin: 0px;
  }
`
      }
    })
  );
};

const JwComponent = props => {
  const {
    html,
    additionalStyle,
    backgroundColor,
    siteName,
    templateName,
    videoId,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const JwComponentStyle = createStyle$6(backgroundColor);
  const figureRef = useViewabilityHandler();

  const initMonti = () => {
    if (figureRef.current && figureRef.current.firstElementChild) {
      const jwHtmlRef = figureRef.current.firstElementChild; // @ts-ignore TS2339

      window.monti().initiate({
        config_name: `Editor Embedded player in ${siteName}`,
        uniq_key: 'mm-article-player',
        publisher_contribution: siteName,
        content_id: videoId,
        content_type: 'curated',
        player_pos: jwHtmlRef,
        category_name: templateName,
        monetization: {
          vpaid_mode: 'ENABLED',
          strategy: 'on_player_load',
          ad_type: 'auto_dynamic_tag',
          ad_request_timeout: '10000',
          ad_break_slots: {
            on: [0]
          }
        },
        brand_color: '#EEEEEE',
        playback_method: 'none',
        powered_by_strip: false
      });
    }
  };

  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.monti !== 'undefined') {
      initMonti();
    } else {
      window.addEventListener('mplayerPlayerLoaded', initMonti);
    }
  }, []);
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", {
    async: true,
    defer: true,
    src: "https://www.oo-syringe.com/prod/players/mplayer_wrapper_v3_latest.js"
  })), createElement("figure", {
    ref: figureRef,
    "data-id": dataId,
    className: css(JwComponentStyle.style, additionalStyle.style),
    dangerouslySetInnerHTML: {
      __html: html
    }
  }), createElement(StyleForMontiElement, null));
};

const mapThemeToProps$c = theme => ({
  backgroundColor: theme.backgroundColor
});

const Jw = props => {
  const themeContext = useTheme(componentConfigurationKey$i, mapThemeToProps$c);
  return createElement(ErrorBoundary, null, createElement(JwComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$j = 'iframe';

const createIframeWrapperStyle = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    paddingTop: `calc(100% / (${16 / 9}))`,
    position: 'relative'
  }
});

const createIframeStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const parseIframeHtml = html => {
  const iframeStyle = createIframeStyle();
  return html.replace(/<iframe/, `<iframe class="${css(iframeStyle.style)}"`);
};

const IframeComponent = props => {
  const {
    html,
    additionalStyle,
    backgroundColor,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const iframeWrapperStyle = createIframeWrapperStyle(backgroundColor);
  return (// eslint-disable-next-line react/no-danger
    createElement("figure", {
      ref: useViewabilityHandler(),
      "data-id": dataId,
      className: css(iframeWrapperStyle.style, additionalStyle.style),
      dangerouslySetInnerHTML: {
        __html: parseIframeHtml(html)
      }
    })
  );
};

const mapThemeToProps$d = theme => ({
  backgroundColor: theme.backgroundColor
});

const Iframe = props => {
  const themeContext = useTheme(componentConfigurationKey$j, mapThemeToProps$d);
  return createElement(ErrorBoundary, null, createElement(IframeComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$k = 'mmPlayer';

const createMMPlayerWrapperStyle = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    paddingTop: `calc(100% / (${16 / 9}))`,
    position: 'relative',
    margin: 0
  }
});

const createMMPlayerStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const MMPlayerComponent = props => {
  const {
    html,
    additionalStyle,
    backgroundColor,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const MMPlayerWrapperStyle = createMMPlayerWrapperStyle(backgroundColor);
  const MMPlayerStyle = createMMPlayerStyle();
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: true,
    defer: true,
    src: "https://vms-players.minutemediaservices.com/mplayer_embed_mount.js"
  })), createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, createElement("div", {
    className: css(MMPlayerWrapperStyle.style)
  }, createElement("div", {
    className: css(MMPlayerStyle.style),
    dangerouslySetInnerHTML: {
      __html: html
    }
  }))));
};

const mapThemeToProps$e = theme => ({
  backgroundColor: theme.backgroundColor
});

const MMPlayer = props => {
  const themeContext = useTheme(componentConfigurationKey$k, mapThemeToProps$e);
  return createElement(ErrorBoundary, null, createElement(MMPlayerComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$o = 'articleMainImage';

const getMainImageStyle = vrLineHeight => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
      }
    }
  });
};

const getCoverStyles = () => {
  return StyleSheet.create({
    wrapper: {
      position: 'relative'
    }
  });
};

const getBlockStyle = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%'
    }
  });
};

const getMainImage = (image, credit, style) => {
  return credit ? createElement(ImageWithFigcaption, _extends({}, image, {
    figcaption: credit,
    additionalStyle: style
  })) : createElement(Image, _extends({}, image, {
    additionalStyle: style
  }));
};

const getMedia = (media, siteName, templateName) => {
  const additionalStyle = getBlockStyle();
  const {
    type
  } = media;

  switch (type) {
    case 'jw':
      {
        const {
          html,
          videoId
        } = media;
        return createElement(Jw, {
          html: html,
          additionalStyle: additionalStyle,
          videoId: videoId,
          siteName: siteName,
          templateName: templateName
        });
      }

    case 'youtube':
      {
        const {
          html
        } = media;
        return createElement(Iframe, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    case 'mmPlayer':
      {
        const {
          html
        } = media;
        return createElement(MMPlayer, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    default:
      return null;
  }
};

const mapThemeProps$v = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const ArticleMainImageComponent = ({
  vrLineHeight,
  image,
  media,
  siteName,
  templateName
}) => {
  const mainImageStyle = getMainImageStyle(vrLineHeight);
  const coverStyles = getCoverStyles();
  const {
    credit
  } = image;
  return createElement("div", {
    className: css(coverStyles.wrapper)
  }, getMainImage(image, credit, mainImageStyle), media && getMedia(media, siteName, templateName));
};
const ArticleMainImage = props => {
  const themeContext = useTheme(componentConfigThemeKey$o, mapThemeProps$v);
  return createElement(ErrorBoundary, null, createElement(ArticleMainImageComponent, _extends({}, themeContext, props)));
};

const getStyles$1 = vrLineHeight => {
  return StyleSheet.create({
    icon: {
      margin: 'auto',
      display: 'block',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        width: '32px',
        height: '32px',
        flex: `0 0 ${(32 + vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4) * 2}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        width: '24px',
        height: '24px',
        flex: `0 0 ${(24 + vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4) * 2}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        width: '16px',
        height: '16px',
        flex: `0 0 ${(16 + vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4) * 2}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        width: '16px',
        height: '16px',
        flex: `0 0 ${(16 + vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 4) * 2}px`
      }
    }
  });
};

const QuoteIcon = props => {
  const {
    iconColor,
    vrLineHeight
  } = props;
  const styles = getStyles$1(vrLineHeight);
  return createElement("svg", {
    className: css(styles.icon),
    viewBox: "0 0 16 16",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, createElement("g", {
    id: "Icons-/-quote",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, createElement("path", {
    d: "M9.0788,1 L16.00005,1 L16.00005,5.6195 C16.00005,8.41675 15.5258,10.498 14.5773,11.86525 C13.6283,13.2325 12.2058,14.27725 10.3088,15 L9.0498,12.313 C11.44405,11.52775 12.6413,9.90875 12.6413,7.458 L9.0788,7.458 L9.0788,1 Z M0.029,1 L6.95025,1 L6.95025,5.6195 C6.95025,8.41675 6.476,10.498 5.5275,11.86525 C4.5785,13.2325 3.156,14.27725 1.259,15 L-8.8817842e-15,12.313 C2.39425,11.52775 3.5915,9.90875 3.5915,7.458 L0.029,7.458 L0.029,1 Z",
    id: "icons-/-quote",
    fill: iconColor,
    fillRule: "nonzero"
  })));
};

const componentConfigurationKey$l = 'quote';

const getStyles$2 = theme => {
  const {
    vrLineHeight,
    borderColor,
    textColor,
    citeColor
  } = theme;
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      borderBottom: `1px solid ${borderColor}`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
      }
    },
    text: {
      fontStyle: 'italic',
      color: textColor,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
      }
    },
    cite: {
      color: citeColor,
      fontStyle: 'normal',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
      }
    },
    quoteTop: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      alignItems: 'center',
      ':before': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0'
      },
      ':after': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0'
      }
    }
  });
};

const QuoteComponent = props => {
  const {
    text,
    cite,
    iconColor,
    vrLineHeight,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const styles = getStyles$2(props);
  const citeStyle = StyleSheet.create(getGeneratedGlobalStyles().p());
  return createElement("blockquote", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(styles.wrapper, additionalStyle.style)
  }, createElement("div", {
    className: css(styles.quoteTop)
  }, createElement(QuoteIcon, {
    iconColor: iconColor,
    vrLineHeight: vrLineHeight
  })), createElement("h3", {
    className: css(styles.text)
  }, text), cite ? createElement("cite", {
    className: css(styles.cite, citeStyle.style)
  }, "-\xA0", cite) : null);
};

const mapThemeProps$w = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    borderColor: theme.borderColor,
    textColor: theme.textColor,
    citeColor: theme.citeColor,
    iconColor: theme.iconColor
  };
};

const Quote = props => {
  const themeContext = useTheme(componentConfigurationKey$l, mapThemeProps$w);
  const {
    additionalStyle
  } = props;
  return createElement(ErrorBoundary, null, createElement(QuoteComponent, _extends({}, props, themeContext, {
    additionalStyle: additionalStyle
  })));
};

const componentConfigurationKey$m = 'divider';

const mapThemeToProps$f = theme => ({
  color: theme.color
});

const createStyle$7 = ({
  color
}) => StyleSheet.create({
  divider: {
    backgroundColor: color,
    width: 'auto',
    height: '1px'
  }
});

const DividerComponent = props => {
  const style = createStyle$7(props);
  const {
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  return createElement("div", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(style.divider, additionalStyle.style)
  });
};
const Divider = props => {
  const themeContext = useTheme(componentConfigurationKey$m, mapThemeToProps$f);
  return createElement(ErrorBoundary, null, createElement(DividerComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$n = 'articleParagraph';

const getStyle$f = theme => {
  const {
    linkColor,
    vrLineHeight
  } = theme;
  return element => {
    switch (element) {
      case 'a':
        return StyleSheet.create({
          style: {
            color: linkColor,
            textDecoration: 'none'
          }
        }).style;

      case 'ol':
      case 'ul':
        return StyleSheet.create({
          style: {
            listStylePosition: 'inside',
            padding: 0,
            [MEDIA_BREAKPOINTS.X_LARGE]: {
              margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px 0`
            },
            [MEDIA_BREAKPOINTS.LARGE]: {
              margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0`
            },
            [MEDIA_BREAKPOINTS.MEDIUM]: {
              margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0`
            },
            [MEDIA_BREAKPOINTS.SMALL]: {
              margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0`
            }
          }
        }).style;

      default:
        return null;
    }
  };
};

const applyCssClasses = (html, theme) => {
  const style = getStyle$f(theme);
  return html.replace(/<ol/g, `<ol class="${css(style('ol'))}"`).replace(/<ul/g, `<ul class="${css(style('ul'))}"`).replace(/<a/g, `<a class="${css(style('a'))}"`);
};

const extractWrapper = html => {
  const regex = /^<(.*?)>/;
  const match = html.match(regex);

  if (!match) {
    return ['p', html];
  }

  const indexOfLastElement = html.lastIndexOf('<');
  return [match[1], html.substr(0, indexOfLastElement).replace(match[0], '')];
};

const getWrapperAsElement = (wrapper, content, style, additionalStyle, dataId) => {
  return createElement(wrapper, {
    dangerouslySetInnerHTML: {
      __html: content
    },
    className: css(style, additionalStyle.style),
    ref: useViewabilityHandler(),
    'data-id': dataId
  });
};

const ParagraphComponent = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const [wrapper, content] = extractWrapper(html);
  const wrapperStyle = getStyle$f(props)(wrapper);
  return getWrapperAsElement(wrapper, applyCssClasses(content, props), wrapperStyle, additionalStyle, dataId);
};

const mapThemeProps$x = theme => ({
  linkColor: theme.linkColor,
  vrLineHeight: theme.vrLineHeight
});

const Paragraph = props => {
  const themeContext = useTheme(componentConfigurationKey$n, mapThemeProps$x);
  return createElement(ErrorBoundary, null, createElement(ParagraphComponent, _extends({}, themeContext, props)));
};

const PlaybuzzScript = "(function(d,s,id){var js,fjs=d.getElementsByTagName(s)[0];if(d.getElementById(id))return;js=d.createElement(s);js.id=id;js.src='https://embed.playbuzz.com/sdk.js';fjs.parentNode.insertBefore(js,fjs);}(document,'script','playbuzz-sdk'));";
const PlaybuzzComponent = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.Playbuzz !== 'undefined') {
      // @ts-ignore TS2339
      window.Playbuzz.render();
    }
  }, []);
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", null, PlaybuzzScript)), createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(additionalStyle.style)
  }));
};
const Playbuzz = props => createElement(ErrorBoundary, null, createElement(PlaybuzzComponent, props));

const componentConfigurationKey$o = 'streamable';

const mapThemeToProps$g = theme => ({
  backgroundColor: theme.backgroundColor
});

const Streamable = props => {
  const themeContext = useTheme(componentConfigurationKey$o, mapThemeToProps$g);
  return createElement(ErrorBoundary, null, createElement(IframeComponent, _extends({}, themeContext, props)));
};

const createStyle$8 = () => StyleSheet.create({
  style: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const TwitterScript = `window.twttr = (function(d, s, id) {
  var js, fjs = d.getElementsByTagName(s)[0],
    t = window.twttr || {};
  if (d.getElementById(id)) return t;
  js = d.createElement(s);
  js.id = id;
  js.src = "https://platform.twitter.com/widgets.js";
  fjs.parentNode.insertBefore(js, fjs);

  t._e = [];
  t.ready = function(f) {
    t._e.push(f);
  };

  return t;
}(document, "script", "twitter-wjs"));`;
const TwitterComponent = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const twitterStyle = createStyle$8();
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.twttr !== 'undefined' && typeof window.twttr.widgets !== 'undefined') {
      // @ts-ignore TS2339
      window.twttr.widgets.load();
    }
  }, []);
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", null, TwitterScript)), createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(twitterStyle.style, additionalStyle.style)
  }));
};
const Twitter = props => createElement(ErrorBoundary, null, createElement(TwitterComponent, props));

const getStyle$g = (width, height) => {
  return StyleSheet.create({
    figure: {
      position: 'relative',
      overflow: 'hidden',
      margin: 0,
      boxSizing: 'border-box',
      flex: '1 1 100%',
      minWidth: '0%',
      paddingTop: `calc(100% / (${width / height}))`
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%'
    }
  });
};

const GiphyComponent = props => {
  const {
    additionalStyle,
    url,
    width,
    height,
    description,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const style = getStyle$g(width, height);
  return createElement("div", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, createElement("figure", {
    className: css(style.figure)
  }, createElement("img", {
    src: url,
    className: css(style.image),
    alt: description
  })));
};
const Giphy = props => createElement(ErrorBoundary, null, createElement(GiphyComponent, props));

const createStyle$9 = () => StyleSheet.create({
  style: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const InstagramComponent = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const instagramStyle = createStyle$9();
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.instgrm !== 'undefined') {
      // @ts-ignore TS2339
      window.instgrm.Embeds.process();
    }
  }, []);
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", {
    async: true,
    src: "https://www.instagram.com/embed.js"
  })), createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(instagramStyle.style, additionalStyle.style)
  }));
};
const Instagram = props => createElement(ErrorBoundary, null, createElement(InstagramComponent, props));

const createStyle$a = () => StyleSheet.create({
  style: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const removeDataWidthFromHtml = html => html.replace(/data-width="\d+"/, 'data-width=""');
const facebookVersion = 'v3.3';
const FacebookComponent = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const facebookStyle = createStyle$a();
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.FB !== 'undefined') {
      // @ts-ignore TS2339
      // eslint-disable-next-line no-undef
      FB.init({
        xfbml: true,
        version: facebookVersion
      });
    }
  }, []);
  const htmlWithFluidWidth = removeDataWidthFromHtml(html);
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", {
    async: true,
    defer: true,
    src: `https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=${facebookVersion}`
  })), createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: htmlWithFluidWidth
    },
    className: css(facebookStyle.style, additionalStyle.style)
  }));
};
const Facebook = props => createElement(ErrorBoundary, null, createElement(FacebookComponent, props));

const componentConfigurationKey$p = 'listItemTitle';

const getStyle$h = theme => StyleSheet.create({
  numberStyle: {
    color: theme.numberColor
  },
  titleStyle: {
    color: theme.titleColor
  }
});

const ListItemTitleComponent = props => {
  const {
    listItemNumber,
    text,
    additionalStyle,
    dataId
  } = props;
  useViewabilityCallback(dataId);
  const {
    numberStyle,
    titleStyle
  } = getStyle$h(props);
  const h3Style = StyleSheet.create(getGeneratedGlobalStyles().h3());
  return createElement("h3", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    className: css(additionalStyle.style, titleStyle)
  }, createElement("span", {
    className: css(numberStyle, h3Style.style)
  }, `${listItemNumber}. `), text);
};

const mapThemeProps$y = theme => {
  return {
    titleColor: theme.titleColor,
    numberColor: theme.numberColor,
    vrLineHeight: theme.vrLineHeight
  };
};

const ListItemTitle = props => {
  const themeContext = useTheme(componentConfigurationKey$p, mapThemeProps$y);
  return createElement(ErrorBoundary, null, createElement(ListItemTitleComponent, _extends({}, themeContext, props)));
};

const emitInjectArticleBlockByIndexEvent = dataId => {
  setEventEmitter('injectArticleBlockByIndexEvent', callback => callback(dataId));
};

const InjectedBlock = props => {
  const {
    html,
    additionalStyle,
    dataId,
    script
  } = props;
  useEffect(() => {
    emitInjectArticleBlockByIndexEvent(dataId);
  }, [dataId]);
  return createElement(ErrorBoundary, null, script ? createElement(Helmet$1, null, createElement("script", null, script)) : null, createElement("figure", {
    ref: useViewabilityHandler(),
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(additionalStyle.style)
  }));
};

const componentConfigurationKey$q = 'iframeEmbed';

const createIframeEmbedWrapperStyle = (backgroundColor, height) => StyleSheet.create({
  style: {
    backgroundColor,
    paddingTop: `${height}px`,
    position: 'relative'
  }
});

const createIframeEmbedStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const parseIframeHtml$1 = html => {
  const iframeStyle = createIframeEmbedStyle();
  return html.replace(/<iframe/, `<iframe class="${css(iframeStyle.style)}"`);
};

const IframeEmbedComponent = props => {
  const {
    html,
    additionalStyle,
    backgroundColor,
    dataId,
    height
  } = props;
  useViewabilityCallback(dataId);
  const iframeWrapperStyle = createIframeEmbedWrapperStyle(backgroundColor, height);
  return (// eslint-disable-next-line react/no-danger
    createElement("figure", {
      ref: useViewabilityHandler(),
      "data-id": dataId,
      className: css(iframeWrapperStyle.style, additionalStyle.style),
      dangerouslySetInnerHTML: {
        __html: parseIframeHtml$1(html)
      }
    })
  );
};

const mapThemeToProps$h = theme => ({
  backgroundColor: theme.backgroundColor
});

const IframeEmbed = props => {
  const themeContext = useTheme(componentConfigurationKey$q, mapThemeToProps$h);
  return createElement(ErrorBoundary, null, createElement(IframeEmbedComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$p = 'articleContent';

const getImageCaption = (credit, caption) => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};

const createBlockStyle = (vrLineHeight, vrLineHeightSize) => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const createBlocksComponents = ({
  body,
  vrLineHeight,
  templateName,
  siteName
}) => {
  let isPreviousElementTitle = false;
  return body.map(block => {
    const {
      type
    } = block;
    const blockStyle = isPreviousElementTitle ? createBlockStyle(vrLineHeight, 0.5) : createBlockStyle(vrLineHeight, 1);
    isPreviousElementTitle = false;

    switch (type) {
      case 'ad':
        {
          const {
            dataId,
            adId
          } = block;
          return createElement(Ad, {
            key: dataId,
            additionalStyle: blockStyle,
            id: adId
          });
        }

      case 'image':
        {
          const {
            image,
            dataId
          } = block;
          const {
            credit,
            caption
          } = image;
          const figcaption = getImageCaption(credit, caption);
          return figcaption ? createElement(ImageWithFigcaption, _extends({
            key: dataId
          }, image, {
            figcaption: figcaption,
            lazyLoad: true,
            additionalStyle: blockStyle,
            dataId: dataId
          })) : createElement(Image, _extends({
            key: dataId
          }, image, {
            lazyLoad: true,
            additionalStyle: blockStyle,
            dataId: dataId
          }));
        }

      case 'title':
        {
          isPreviousElementTitle = true;
          const {
            listItemNumber,
            text,
            dataId
          } = block;
          return createElement(ListItemTitle, {
            key: dataId,
            listItemNumber: listItemNumber,
            text: text,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'inline-text':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Paragraph, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'quote':
        {
          const {
            text,
            cite,
            dataId
          } = block;
          return createElement(Quote, {
            key: dataId,
            text: text,
            cite: cite,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'youtube':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Iframe, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'twitter':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Twitter, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'instagram':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Instagram, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'facebook':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Facebook, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'jw':
        {
          const {
            html,
            videoId,
            dataId
          } = block;
          return createElement(Jw, {
            key: dataId,
            html: html,
            videoId: videoId,
            templateName: templateName,
            siteName: siteName,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'mmPlayer':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(MMPlayer, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'giphy':
        {
          const {
            dataId
          } = block;
          return createElement(Giphy, _extends({
            key: dataId
          }, block, {
            additionalStyle: blockStyle,
            dataId: dataId
          }));
        }

      case 'playbuzz':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Playbuzz, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'dailyMotion':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Iframe, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'divider':
        {
          const {
            dataId
          } = block;
          return createElement(Divider, {
            key: dataId,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'streamable':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Streamable, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'injectedBlock':
        {
          const {
            html,
            dataId,
            script
          } = block;
          return createElement(InjectedBlock, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId,
            script: script
          });
        }

      case 'soundcloud':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Iframe, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'iframeEmbed':
        {
          const {
            html,
            dataId,
            height
          } = block;
          return createElement(IframeEmbed, {
            key: dataId,
            height: height,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      default:
        return null;
    }
  });
};

const ArticleContentComponent = props => {
  const {
    templateName
  } = props;
  const viewabilityHandlerContext = createViewabilityHandlerContext(templateName);
  return createElement(ViewabilityHandlerContext.Provider, {
    value: viewabilityHandlerContext
  }, createBlocksComponents(props));
};

const mapThemeProps$z = theme => {
  return {
    vrLineHeight: theme.vrLineHeight
  };
};

const ArticleContent = props => {
  const themeContext = useTheme(componentConfigThemeKey$p, mapThemeProps$z);
  return createElement(ErrorBoundary, null, createElement(ArticleContentComponent, _extends({}, themeContext, props)));
};

const preloadImage = (host, path, aspectRatio = {
  x: 16,
  y: 9
}) => {
  const srcSet = getSrcSet(aspectRatio, host, path);
  return Object.keys(srcSet).filter(key => key !== 'lowResUrl') // @ts-ignore TS2345
  .reduce((elements, key, index) => {
    const media = getMinWidthBySize(key);
    const Link = createElement("link", {
      key: index,
      rel: "preload",
      href: srcSet[key],
      media: media,
      as: "image"
    });
    return [...elements, Link];
  }, []);
};
const normalizeDimensions = (aspectRatio = {
  x: 16,
  y: 9
}) => {
  return [aspectRatio.x / aspectRatio.y, 1];
};

const componentConfigThemeKey$q = 'imageWithFigcaptionWrapper';
const getStyle$i = (vrLineHeight, figcaptionTextColor) => StyleSheet.create({
  figcaption: {
    color: figcaptionTextColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
    }
  }
});
const getAMPImageProps = image => {
  const {
    description,
    host,
    path,
    aspectRatio = {
      x: 16,
      y: 9
    }
  } = image;
  const calculated = getCalculatedThumbnail(aspectRatio, description, host, path);
  const [width, height] = normalizeDimensions(aspectRatio);
  return {
    src: calculated.imageUrl,
    srcSet: calculated.srcSet,
    alt: calculated.alt,
    width,
    height
  };
};

const getAMPImage = imageProps => {
  const {
    width,
    height,
    src,
    srcSet,
    alt
  } = imageProps; // @ts-ignore TS2339

  return createElement("amp-img", {
    src: src,
    srcSet: srcSet,
    alt: alt,
    layout: "responsive",
    width: width,
    height: height
  });
};

const getImageCaption$1 = (credit, caption) => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};

const mapThemeProps$A = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    figcaptionTextColor: theme.figcaptionTextColor
  };
};

const AMPImageComponent = props => {
  const {
    additionalStyle,
    preload,
    credit,
    caption,
    vrLineHeight,
    figcaptionTextColor,
    host,
    path,
    aspectRatio
  } = props;
  const imageProps = getAMPImageProps(props);
  const figCaption = getImageCaption$1(credit, caption);
  const imageStyle = additionalStyle ? css(additionalStyle.style) : '';
  const figCaptionStyle = getStyle$i(vrLineHeight, figcaptionTextColor);
  const figcaptionText = StyleSheet.create(getGeneratedGlobalStyles().p());
  return createElement("div", {
    className: imageStyle
  }, preload && preloadImage(host, path, aspectRatio), getAMPImage(imageProps), figCaption && createElement("figcaption", {
    className: css(figCaptionStyle.figcaption, figcaptionText.style)
  }, figCaption));
};
const AMPImage = props => {
  const themeContext = useTheme(componentConfigThemeKey$q, mapThemeProps$A);
  return createElement(AMPImageComponent, _extends({}, props, themeContext));
};

const AMPJWPlayer = props => {
  const {
    playerId,
    videoId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-jwplayer",
    src: "https://cdn.ampproject.org/v0/amp-jwplayer-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-jwplayer", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-media-id": videoId,
    "data-player-id": playerId
  })));
};

const AMPYoutube = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-youtube",
    src: "https://cdn.ampproject.org/v0/amp-youtube-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-youtube", {
    "data-videoid": mediaId,
    layout: "responsive",
    width: "480",
    height: "270"
  })));
};

const AMPMMPlayer = props => {
  const {
    playerId,
    playlistId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-minute-media-player",
    src: "https://cdn.ampproject.org/v0/amp-minute-media-player-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-minute-media-player", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-content-type": "specific",
    "data-content-id": `${playlistId}#${playerId}`
  })));
};

const componentConfigThemeKey$r = 'articleMainImage';

const getStyles$3 = () => StyleSheet.create({
  wrapper: {
    position: 'relative'
  }
});

const getMainImageStyle$1 = vrLineHeight => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`
      }
    }
  });
};

const getBlockStyle$1 = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%'
    }
  });
};

const getMedia$1 = media => {
  const {
    type
  } = media;
  const blockStyle = getBlockStyle$1();

  switch (type) {
    case 'jw':
      {
        const {
          videoId,
          playerId
        } = media;
        return createElement(AMPJWPlayer, {
          videoId: videoId,
          playerId: playerId,
          additionalStyle: blockStyle
        });
      }

    case 'youtube':
      {
        const {
          mediaId
        } = media;
        return createElement(AMPYoutube, {
          mediaId: mediaId,
          additionalStyle: blockStyle
        });
      }

    case 'mmPlayer':
      {
        const {
          mediaId,
          playerId
        } = media;
        return createElement(AMPMMPlayer, {
          playlistId: mediaId,
          playerId: playerId,
          additionalStyle: blockStyle
        });
      }

    default:
      return null;
  }
};

const AMPCoverComponent = props => {
  const {
    vrLineHeight,
    media
  } = props;
  const styles = getStyles$3();
  const additionalStyle = getMainImageStyle$1(vrLineHeight);
  return createElement("div", {
    className: css(styles.wrapper)
  }, createElement(AMPImage, _extends({
    preload: true
  }, props, {
    additionalStyle: additionalStyle
  })), media && getMedia$1(media));
};

const mapThemeProps$B = theme => {
  return {
    vrLineHeight: theme.vrLineHeight
  };
};

const AMPCover = props => {
  const themeContext = useTheme(componentConfigThemeKey$r, mapThemeProps$B);
  return createElement(AMPCoverComponent, _extends({}, props, themeContext));
};

const logoHeight = 24;

const getLogoStyle$1 = () => {
  return StyleSheet.create({
    base: {
      height: `${logoHeight}px`,
      alignItems: 'center'
    },
    largeSvg: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    },
    smallSvg: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'inline-flex'
      }
    },
    image: {
      height: '100%'
    }
  });
};

const getSvgWidthAndHeight = viewBoxValue => {
  const viewBowDimensions = viewBoxValue.split(' ');
  return {
    width: Number(viewBowDimensions[2]),
    height: Number(viewBowDimensions[3])
  };
};
const AMPLogo = props => {
  const {
    href,
    alt,
    svgLarge,
    svgSmall,
    svgLargeViewBox,
    svgSmallViewBox
  } = props;
  const style = getLogoStyle$1();
  const largeSvgWidthAndHeight = getSvgWidthAndHeight(svgLargeViewBox);
  const smallSvgWidthAndHeight = getSvgWidthAndHeight(svgSmallViewBox);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Link, {
    className: css(style.largeSvg, style.base),
    href: href
  }, React__default.createElement("amp-img", {
    layout: "fixed",
    class: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgLarge)}`,
    height: `${logoHeight}`,
    width: `${logoHeight * largeSvgWidthAndHeight.width / largeSvgWidthAndHeight.height}`
  })), React__default.createElement(Link, {
    className: css(style.smallSvg, style.base),
    href: href
  }, React__default.createElement("amp-img", {
    layout: "fixed",
    class: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgSmall)}`,
    height: `${logoHeight}`,
    width: `${logoHeight * smallSvgWidthAndHeight.width / smallSvgWidthAndHeight.height}`
  })));
};

const additionalStyle$1 = {
  [MEDIA_BREAKPOINTS.X_LARGE]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.LARGE]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.MEDIUM]: {
    display: 'none'
  },
  [MEDIA_BREAKPOINTS.SMALL]: {
    display: 'none'
  }
};
const radiusBorder$1 = 4;

const createStyle$b = (isActive = false, activeBorderBottom) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder$1}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom
      } : {}
    }
  });
};

const createLink$2 = (idleColor, vrLineHeight, activeBorderBottom, hoverStateColor, activeStateColor, pagePath) => (link, index) => {
  const {
    href,
    text
  } = link;
  const isActiveLink = href === pagePath;
  const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
  const linkStyle = generateLinkStyleFromTheme({
    idleColor,
    activeStateColor,
    hoverStateColor
  }, additionalStyle$1);
  const style = createStyle$b(isActiveLink, activeBorderBottom);
  return createElement(TextLinkComponent, {
    key: index,
    href: href,
    linkStyle: linkStyle
  }, createElement("span", {
    className: css(contentStyle.style, style.link)
  }, text));
};

const AMPPrimaryLinksComponent = props => {
  const {
    links,
    idleColor,
    vrLineHeight,
    activeBorderBottom,
    hoverStateColor,
    activeStateColor,
    pagePath
  } = props;
  return createElement(Fragment, null, links.map(createLink$2(idleColor, vrLineHeight, activeBorderBottom, hoverStateColor, activeStateColor, pagePath)));
};
const componentConfigThemeKey$s = 'primaryLinks';

const mapThemeProps$C = theme => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    activeBorderBottom: theme.activeBorderBottom,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    vrLineHeight: theme.vrLineHeight
  };
};

const AMPPrimaryLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$s, mapThemeProps$C);
  return createElement(ErrorBoundary, null, createElement(AMPPrimaryLinksComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$t = 'editions';

const getStyle$j = color => {
  return StyleSheet.create({
    editionsWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      }
    },
    editionsTitleWrapper: {
      height: `${primaryHeaderHeight}px`,
      width: '144px',
      color,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      }
    },
    editionsTitle: {
      height: '100%',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between'
    }
  });
};

const AMPEditionsComponent = props => {
  const {
    editions,
    color,
    editionEndpoint
  } = props;
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const style = getStyle$j(color);
  return createElement(Fragment, null, createElement(Helmet, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-accordion",
    src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
  })), createElement("div", {
    className: css(style.editionsWrapper)
  }, createElement("amp-accordion", {
    id: "accordion-editions",
    "disable-session-states": undefined
  }, createElement("section", null, createElement("h4", {
    className: css(style.editionsTitleWrapper)
  }, createElement("div", {
    className: css(style.editionsTitle)
  }, createElement("div", null, activeEdition.text), createElement(ChevronIcon, {
    direction: Directions.DOWN,
    color: color
  }))), createElement(DropDownMenu, {
    menuLinks: editions,
    configThemeKey: "editionsMenu",
    activeEndpoint: editionEndpoint
  })))));
};

const mapThemeProps$D = theme => {
  return {
    color: theme.color
  };
};

const AMPEditions = props => {
  const themeContext = useTheme(componentConfigThemeKey$t, mapThemeProps$D);
  return createElement(ErrorBoundary, null, createElement(AMPEditionsComponent, _extends({}, themeContext, props)));
};

const componentConfigThemeKey$u = 'menuIcon';

const getMenuIconStyle$1 = color => StyleSheet.create({
  wrapper: {
    width: '16px',
    height: '16px',
    position: 'relative',
    cursor: 'pointer',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      display: 'inline-flex'
    },
    ':focus': {
      outline: 'none'
    }
  },
  span: {
    display: 'block',
    position: 'absolute',
    backgroundColor: color,
    height: '2px',
    width: '100%',
    opacity: 1,
    left: 0
  },
  firstSpanClose: {
    top: 0
  },
  secondSpanClose: {
    top: '7px'
  },
  thirdSpanClose: {
    top: '14px'
  }
});

const AMPMenuIconComponent = ({
  color
}) => {
  const menuIcon = getMenuIconStyle$1(color);
  return (// @ts-ignore
    createElement("div", {
      tabIndex: "0",
      on: "tap:sidebar.open",
      role: "button",
      className: css(menuIcon.wrapper)
    }, createElement("span", {
      className: css(menuIcon.span, menuIcon.firstSpanClose)
    }), createElement("span", {
      className: css(menuIcon.span, menuIcon.secondSpanClose)
    }), createElement("span", {
      className: css(menuIcon.span, menuIcon.thirdSpanClose)
    }))
  );
};

const mapThemeProps$E = theme => {
  return {
    color: theme.color
  };
};

const AMPMenuIcon = props => {
  const themeContext = useTheme(componentConfigThemeKey$u, mapThemeProps$E);
  return createElement(ErrorBoundary, null, createElement(AMPMenuIconComponent, _extends({}, themeContext, props)));
};

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

const componentConfigThemeKey$v = 'primaryHeader';

const getPrimaryHeaderStyle = (backgroundColor, vrLineHeight) => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'relative',
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100
  },
  itemsBasicContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    height: '100%',
    width: 'fit-content',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  itemsRightContainer: {
    alignItems: 'baseline',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`
    }
  },
  itemsLeftContainer: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  ctaWrapper: {
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    alignItems: 'center'
  }
});

const AMPPrimaryHeaderComponent = props => {
  const {
    primaryLinks,
    primaryLogos,
    cta,
    surfaceStyle,
    backgroundColor,
    vrLineHeight,
    shouldShowClosedMenuIcon,
    editions,
    pagePath,
    editionEndpoint
  } = props;
  const style = getPrimaryHeaderStyle(backgroundColor, vrLineHeight);
  return createElement("div", {
    className: css(style.wrapper, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle)
  }, createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsLeftContainer)
  }, shouldShowClosedMenuIcon ? createElement(AMPMenuIcon, null) : null, createElement(AMPLogo, primaryLogos), primaryLinks ? createElement(AMPPrimaryLinks, {
    links: primaryLinks,
    pagePath: pagePath
  }) : null), createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsRightContainer)
  }, cta ? createElement("div", {
    className: css(style.ctaWrapper)
  }, createElement(CtaButton, cta)) : null, editions ? createElement(AMPEditions, {
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null));
};

const mapThemeProps$F = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle
  };
};

const AMPPrimaryHeader = props => {
  const themeContext = useTheme(componentConfigThemeKey$v, mapThemeProps$F);
  return createElement(ErrorBoundary, null, createElement(AMPPrimaryHeaderComponent, _extends({}, themeContext, props)));
};

/* eslint-disable react/no-unknown-property */

const componentConfigThemeKey$w = 'menuLink';

const getAmpSideBarLinkStyle = vrLineHeight => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  title: {
    backgroundColor: 'inherit',
    border: 'inherit',
    paddingRight: 0,
    ':focus': {
      outline: 'none'
    }
  },
  titleAndChevronIconWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
});

const createAmpSideBarSubLink = (link, index) => {
  const {
    href,
    text
  } = link;
  return createElement(MenuSubLink, {
    text: text,
    href: href,
    key: index
  });
};

const AMPSideBarLinkComponent = props => {
  const {
    href,
    text,
    idleColor,
    vrLineHeight,
    childrenLinks
  } = props;
  const style = getAmpSideBarLinkStyle(vrLineHeight);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor
  });
  return createElement("section", {
    className: css(style.wrapper)
  }, createElement("h4", {
    className: css(style.title)
  }, createElement("div", {
    className: css(style.titleAndChevronIconWrapper)
  }, createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, text), childrenLinks.length > 0 ? createElement(ChevronIcon, {
    direction: Directions.DOWN,
    color: idleColor
  }) : null)), createElement("ul", null, childrenLinks.map(createAmpSideBarSubLink)));
};

const mapThemeProps$G = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor
  };
};

const AMPSideBarLink = props => {
  const themeContext = useTheme(componentConfigThemeKey$w, mapThemeProps$G);
  return createElement(AMPSideBarLinkComponent, _extends({}, themeContext, props));
};

const createStyle$c = (dividerColor, vrLineHeight) => StyleSheet.create({
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const AMPMenuEditions = props => {
  const {
    editions,
    dividerColor,
    vrLineHeight,
    editionEndpoint
  } = props;
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const {
    href,
    text
  } = activeEdition;
  const notActivatedEditions = editions.filter(edition => edition.text !== text);
  const style = createStyle$c(dividerColor, vrLineHeight);
  return createElement(Fragment, null, createElement("div", {
    className: css(style.divider)
  }), createElement("amp-accordion", {
    id: "editions-accordion",
    "disable-session-states": undefined
  }, createElement(AMPSideBarLink, {
    href: href,
    text: text,
    childrenLinks: notActivatedEditions
  })));
};

/* eslint-disable react/no-unknown-property */
const componentMenuLinksConfigThemeKey = 'menuLinks';
const componentMenuIconConfigThemeKey = 'ampCloseMenuIcon';

const getAmpSideBarStyle = backgroundColor => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'fixed',
    width: '100%',
    left: 0,
    overflowY: 'auto',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      display: 'inline-flex'
    }
  }
});

const getMenuIconStyle$2 = (vrLineHeight, menuIconColor) => StyleSheet.create({
  wrapper: {
    width: '16px',
    height: '16px',
    minHeight: '16px',
    position: 'relative',
    transition: '.5s ease-in-out',
    cursor: 'pointer',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: 0
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: 0
    },
    ':focus': {
      outline: 'none'
    }
  },
  span: {
    display: 'block',
    position: 'absolute',
    height: '2px',
    width: '100%',
    backgroundColor: menuIconColor,
    opacity: 1,
    left: 0,
    transition: '.25s ease-in-out',
    transformOrigin: 'right center'
  },
  firstSpanOpen: {
    transform: 'rotate(-45deg)',
    width: '130%',
    left: '-6px',
    top: 0
  },
  secondSpanOpen: {
    top: '7px',
    transform: 'scaleX(0.1)',
    opacity: 0
  },
  thirdSpanOpen: {
    transform: 'rotate(45deg)',
    top: '14px',
    width: '130%',
    left: '-6px'
  }
});

const createAMPSideBarLink = (menuLink, index) => {
  const {
    href,
    text,
    childrenLinks
  } = menuLink;
  return createElement(AMPSideBarLink, {
    href: href,
    text: text,
    childrenLinks: childrenLinks,
    key: index
  });
};

const AMPSideBarComponent = props => {
  const {
    menu,
    editions,
    backgroundColor,
    vrLineHeight,
    dividerColor,
    color,
    editionEndpoint
  } = props;
  const menuIconStyle = getMenuIconStyle$2(vrLineHeight, color);
  const ampSideBarStyle = getAmpSideBarStyle(backgroundColor);
  return (// @ts-ignore TS2339: Property 'amp-sidebar' does not exist on type 'JSX.IntrinsicElements'.
    createElement(Fragment, null, createElement(Helmet, null, createElement("script", {
      async: undefined,
      "custom-element": "amp-sidebar",
      src: "https://cdn.ampproject.org/v0/amp-sidebar-0.1.js"
    }), createElement("script", {
      async: undefined,
      "custom-element": "amp-accordion",
      src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
    })), createElement("amp-sidebar", {
      id: "sidebar",
      layout: "nodisplay",
      side: "left",
      class: css(ampSideBarStyle.wrapper)
    }, createElement("div", {
      tabIndex: "0",
      on: "tap:sidebar.close,accordion.collapse,editions-accordion.collapse",
      role: "button",
      className: css(menuIconStyle.wrapper)
    }, createElement("span", {
      className: css(menuIconStyle.span, menuIconStyle.firstSpanOpen)
    }), createElement("span", {
      className: css(menuIconStyle.span, menuIconStyle.secondSpanOpen)
    }), createElement("span", {
      className: css(menuIconStyle.span, menuIconStyle.thirdSpanOpen)
    })), createElement("amp-accordion", {
      id: "accordion",
      "disable-session-states": undefined
    }, menu.map(createAMPSideBarLink)), editions ? createElement(AMPMenuEditions, {
      editionEndpoint: editionEndpoint,
      editions: editions,
      vrLineHeight: vrLineHeight,
      dividerColor: dividerColor
    }) : null))
  );
};

const mapThemeMenuLinkProps = theme => {
  return {
    backgroundColor: theme.backgroundColor,
    dividerColor: theme.dividerColor,
    vrLineHeight: theme.vrLineHeight
  };
};

const mapThemeMenuIconProps = theme => {
  return {
    color: theme.color
  };
};

const AMPSideBar = props => {
  const themeContextForMenuLinks = useTheme(componentMenuLinksConfigThemeKey, mapThemeMenuLinkProps);
  const themeContextForMenuIcon = useTheme(componentMenuIconConfigThemeKey, mapThemeMenuIconProps);
  return createElement(ErrorBoundary, null, createElement(AMPSideBarComponent, _extends({}, themeContextForMenuLinks, themeContextForMenuIcon, props)));
};

const componentConfigThemeKey$x = 'moreLinks';
const getStyle$k = titleColor => {
  return StyleSheet.create({
    wrapper: {
      height: 'inherit',
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none'
      }
    },
    titleWrapper: {
      width: 'fit-content',
      height: `${secondaryHeaderHeight}px`,
      color: titleColor,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      }
    },
    title: {
      height: '100%',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between'
    },
    text: {
      marginRight: '9px'
    }
  });
};
const AMPMoreLinksComponent = props => {
  const {
    titleColor,
    links,
    moreLinksText,
    pagePath
  } = props;
  const titleStyle = StyleSheet.create(getGeneratedGlobalStyles().p());
  const style = getStyle$k(titleColor);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Helmet, null, React__default.createElement("script", {
    async: undefined,
    "custom-element": "amp-accordion",
    src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
  })), React__default.createElement("div", {
    className: css(style.wrapper)
  }, React__default.createElement("amp-accordion", {
    id: "accordion-more-links",
    "disable-session-states": undefined
  }, React__default.createElement("section", null, React__default.createElement("h4", {
    className: css(style.titleWrapper, titleStyle.style)
  }, React__default.createElement("div", {
    className: css(style.title)
  }, React__default.createElement("div", {
    className: css(style.text)
  }, moreLinksText), React__default.createElement(ChevronIcon, {
    direction: Directions.DOWN
  }))), React__default.createElement(DropDownMenu, {
    menuLinks: links,
    configThemeKey: "moreLinks",
    activeEndpoint: pagePath
  })))));
};

const mapThemeToProps$i = theme => ({
  titleColor: theme.titleColor,
  menuBackground: theme.menuBackground,
  linkColor: theme.linkColor,
  hoverColor: theme.hoverColor
});

const AMPMoreLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$x, mapThemeToProps$i);
  return React__default.createElement(AMPMoreLinksComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$y = 'secondaryLinks';

const getTextStyle$1 = (vrLineHeight, lastLink) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px` : 0
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: lastLink ? `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px` : 0
      }
    }
  });
};

const getContainerStyle$2 = vrLineHeight => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        overflowX: 'auto'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        overflowX: 'auto'
      }
    }
  });
};

const getCreateLinkFunction = (idleColor, activeStateColor, hoverStateColor, vrLineHeight, linksLength, pagePath) => {
  const createLink = (link, index) => {
    const {
      href,
      text
    } = link;
    const isActiveLink = href === pagePath;
    const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1 ? {
      [MEDIA_BREAKPOINTS.LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'inline-flex'
      }
    } : {};
    const linkStyle = generateLinkStyleFromTheme({
      idleColor: isActiveLink ? activeStateColor : idleColor,
      activeStateColor,
      hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor
    }, linkStyleBasedOnScreen);
    const style = getTextStyle$1(vrLineHeight, isLastLink(index, linksLength));
    const contentStyle = StyleSheet.create(isActiveLink ? getGeneratedGlobalStyles().h4() : getGeneratedGlobalStyles().p());
    return React__default.createElement(TextLinkComponent, {
      key: index,
      href: href,
      linkStyle: linkStyle
    }, React__default.createElement("span", {
      className: css(contentStyle.style, style.link)
    }, text));
  };

  return createLink;
};

const AMPSecondaryLinksComponent = props => {
  const {
    idleColor,
    moreLinksText,
    vrLineHeight,
    activeStateColor,
    hoverStateColor,
    links,
    pagePath
  } = props;
  const moreLinks = links.length > maxVisibleLinksOnLargeAndXLarge ? links.slice(maxVisibleLinksOnLargeAndXLarge - 1) : [];
  const style = getContainerStyle$2(vrLineHeight);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: css(style.container)
  }, links.map(getCreateLinkFunction(idleColor, activeStateColor, hoverStateColor, vrLineHeight, links.length, pagePath))), moreLinks.length > 0 ? React__default.createElement(AMPMoreLinks, {
    links: moreLinks,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null);
};

const mapThemeProps$H = theme => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    vrLineHeight: theme.vrLineHeight,
    hoverOtherLinksColors: theme.hoverOtherLinksColors
  };
};

const AMPSecondaryLinks = props => {
  const themeContext = useTheme(componentConfigThemeKey$y, mapThemeProps$H);
  return React__default.createElement(AMPSecondaryLinksComponent, _extends({}, themeContext, props));
};

const componentConfigThemeKey$z = 'secondaryHeader';

const createStyle$d = (vrLineHeight, backgroundColor) => {
  return StyleSheet.create({
    wrapper: {
      backgroundColor,
      position: 'relative',
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 50,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    }
  });
};

const AMPSecondaryHeaderComponent = props => {
  const {
    vrLineHeight,
    backgroundColor,
    surfaceStyle,
    moreLinksText,
    secondaryLinks,
    secondaryLogos,
    pagePath
  } = props;
  const style = createStyle$d(vrLineHeight, backgroundColor);
  return createElement("div", {
    className: css(style.wrapper, StyleSheet.create({
      surfaceStyle
    }).surfaceStyle)
  }, secondaryLogos ? createElement(AMPLogo, secondaryLogos) : null, secondaryLinks ? createElement(AMPSecondaryLinks, {
    links: secondaryLinks,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null);
};

const mapThemeProps$I = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle
  };
};

const AMPSecondaryHeader = props => {
  const themeContext = useTheme(componentConfigThemeKey$z, mapThemeProps$I);
  return createElement(ErrorBoundary, null, createElement(AMPSecondaryHeaderComponent, _extends({}, themeContext, props)));
};

const getHeaderStyle = () => StyleSheet.create({
  wrapper: {
    position: 'fixed',
    width: '100%',
    zIndex: 1500,
    top: 0
  }
});

const AMPHeader = props => {
  const {
    primaryLinks,
    primaryLogos,
    cta,
    shouldShowClosedMenuIcon,
    secondaryHeader,
    editions,
    editionEndpoint,
    pagePath
  } = props;
  const style = getHeaderStyle();
  return createElement("header", {
    className: css(style.wrapper)
  }, createElement(AMPPrimaryHeader, {
    primaryLinks: primaryLinks,
    primaryLogos: primaryLogos,
    cta: cta,
    shouldShowClosedMenuIcon: shouldShowClosedMenuIcon,
    editions: editions,
    pagePath: pagePath,
    editionEndpoint: editionEndpoint
  }), secondaryHeader ? createElement(AMPSecondaryHeader, _extends({}, secondaryHeader, {
    pagePath: pagePath
  })) : null);
};
const AMPHeaderAndSideBar = props => {
  const {
    secondaryHeader,
    primaryHeader
  } = props;
  const {
    primaryLinks,
    primaryLogos,
    editions,
    cta,
    menu,
    editionEndpoint,
    pagePath
  } = primaryHeader;
  return createElement(Fragment, null, createElement(AMPHeader, {
    primaryLinks: primaryLinks,
    primaryLogos: primaryLogos,
    secondaryHeader: secondaryHeader,
    cta: cta,
    shouldShowClosedMenuIcon: !!menu,
    editions: editions,
    pagePath: pagePath,
    editionEndpoint: editionEndpoint
  }), menu ? createElement(AMPSideBar, {
    menu: menu,
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null);
};

const createPageViewParams = ({
  property,
  articleAuthor,
  articleId,
  articlePublicationTime,
  articleTemplate,
  articleTitle,
  contentTags,
  distributionChannels,
  mainMedia,
  language,
  markupFormat,
  pageIdentifier,
  commercialTags,
  articleContentCount,
  articleWordCount
}) => ({
  property,
  articleAuthor,
  articleId,
  articlePublicationTime,
  articleTemplate,
  articleTitle,
  contentTags,
  distributionChannels,
  mainMedia,
  language,
  markupFormat,
  pageIdentifier,
  commercialTags,
  articleWordCount,
  articleContentCount
});

const AMPGoogleAnalytics = props => {
  const {
    googleAnalyticsId
  } = props;
  const scriptJson = {
    vars: {
      account: googleAnalyticsId
    },
    triggers: {
      page_view: {
        on: 'visible',
        request: 'pageview'
      }
    },
    extraUrlParams: createPageViewParams(props)
  };
  return createElement(Fragment, null, createElement("amp-analytics", {
    id: "analytics",
    type: "googleanalytics",
    "data-credentials": "include"
  }, createElement("script", {
    type: "application/json",
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(scriptJson)
    }
  })));
};

const AMPComscore = props => {
  const {
    comscoreId
  } = props;
  const scriptJson = {
    vars: {
      c2: comscoreId
    },
    extraUrlParams: {
      comscorekw: 'amp'
    }
  };
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-analytics",
    src: "https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
  })), createElement("amp-analytics", {
    type: "comscore"
  }, createElement("script", {
    type: "application/json",
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(scriptJson)
    }
  })));
};

const AMPAnalytics = props => {
  const {
    comscoreId,
    analyticsId,
    property,
    owner,
    articleId,
    articlePublicationTime,
    articleTemplate,
    articleTitle,
    contentTags,
    distributionChannels,
    mainMedia,
    language,
    markupFormat = 'web',
    pageIdentifier,
    commercialTags,
    articleContentCount,
    articleWordCount
  } = props;
  const {
    name: ownerName
  } = owner;
  return createElement(ErrorBoundary, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-analytics",
    src: "https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
  })), createElement(AMPGoogleAnalytics, {
    googleAnalyticsId: analyticsId,
    property: property,
    articleAuthor: ownerName,
    articleId: articleId,
    articlePublicationTime: articlePublicationTime,
    articleTemplate: articleTemplate,
    articleTitle: articleTitle,
    contentTags: contentTags,
    distributionChannels: distributionChannels,
    mainMedia: mainMedia,
    language: language,
    markupFormat: markupFormat,
    pageIdentifier: pageIdentifier,
    commercialTags: commercialTags,
    articleWordCount: articleWordCount,
    articleContentCount: articleContentCount
  }), createElement(AMPComscore, {
    comscoreId: comscoreId
  }));
};

const getStyle$l = (vrLineHeight, type, src) => {
  const style = {
    backgroundSize: '32px',
    borderRadius: '100%',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    },
    ...(type === 'reddit' && {
      backgroundColor: '#ff4500',
      backgroundImage: `url("${src}")`
    })
  };
  return StyleSheet.create({
    style
  });
};

const preconfiguredProviders = ['twitter', 'facebook', 'email', 'linkedin', 'pinterest', 'tumblr', 'whatsapp'];

const isNotPreconfiguredProvider = type => preconfiguredProviders.indexOf(type) >= 0;

const AMPSocialButton = props => {
  const {
    type,
    shareURL,
    src,
    vrLineHeight
  } = props;
  const socialShareProps = {
    type,
    ...(isNotPreconfiguredProvider && {
      'data-share-endpoint': shareURL
    })
  };
  const styles = getStyle$l(vrLineHeight, type, src); // @ts-ignore TS2339

  return createElement("amp-social-share", _extends({}, socialShareProps, {
    class: css(styles.style),
    width: "42",
    height: "42"
  }));
};

const componentConfigurationKey$r = 'ampSocialContainer';

const getStyle$m = vrLineHeight => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const getSocialButtons = (shareConfig, vrLineHeight) => {
  return shareConfig.map((data, index) => createElement(AMPSocialButton, _extends({
    key: index
  }, data, {
    vrLineHeight: vrLineHeight
  })));
};

const AMPSocialContainerComponent = props => {
  const {
    vrLineHeight,
    shareConfig
  } = props;
  const styles = getStyle$m(vrLineHeight);
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-social-share",
    src: "https://cdn.ampproject.org/v0/amp-social-share-0.1.js"
  })), createElement("div", {
    className: css(styles.wrapper)
  }, getSocialButtons(shareConfig, vrLineHeight)));
};

const mapThemeProps$J = theme => {
  return {
    vrLineHeight: theme.vrLineHeight
  };
};

const AMPSocialContainer = props => {
  const themeContext = useTheme(componentConfigurationKey$r, mapThemeProps$J);
  const {
    shareConfig
  } = props;
  return createElement(ErrorBoundary, null, createElement(AMPSocialContainerComponent, _extends({
    shareConfig: shareConfig
  }, themeContext)));
};

const componentConfigurationKey$s = 'sponsor';

const createSponsorStyle = (vrLineHeight, dividerColor, textColor) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  text: {
    color: textColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  divider: {
    backgroundColor: dividerColor,
    width: 'auto',
    height: '1px'
  },
  topDivider: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  bottomDivider: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  imageWrapper: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`
    }
  },
  image: {
    borderRadius: '50%'
  }
});

const AMPSponsorComponent = props => {
  const {
    dividerColor,
    vrLineHeight,
    text,
    image,
    textColor
  } = props;
  const style = createSponsorStyle(vrLineHeight, dividerColor, textColor);
  const {
    src,
    height,
    width,
    alt,
    srcSet
  } = getAMPImageProps(image);
  return createElement(Fragment, null, createElement("div", {
    className: css(style.topDivider, style.divider)
  }), createElement("div", {
    className: css(style.wrapper)
  }, createElement("div", {
    className: css(style.imageWrapper)
  }, createElement("amp-img", {
    class: css(style.image),
    src: src,
    srcSet: srcSet,
    alt: alt,
    layout: "responsive",
    width: width,
    height: height
  })), createElement("p", {
    className: css(style.text)
  }, text)), createElement("div", {
    className: css(style.bottomDivider, style.divider)
  }));
};

const mapThemeProps$K = theme => ({
  dividerColor: theme.dividerColor,
  textColor: theme.textColor,
  vrLineHeight: theme.vrLineHeight
});

const AMPSponsor = props => {
  const themeContext = useTheme(componentConfigurationKey$s, mapThemeProps$K);
  return createElement(ErrorBoundary, null, createElement(AMPSponsorComponent, _extends({}, props, themeContext)));
};

const AMPTwitter = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-twitter",
    src: "https://cdn.ampproject.org/v0/amp-twitter-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-twitter", {
    width: "1",
    height: "1",
    layout: "responsive",
    "data-tweetid": mediaId
  })));
};

const mediaIdValidator = mediaId => {
  return typeof mediaId === 'string' && mediaId.trim() !== '';
};

const AMPMediaValidator = props => {
  const {
    mediaId,
    children
  } = props;
  const isMediaIdValid = mediaIdValidator(mediaId);
  return createElement(Fragment, null, isMediaIdValid && children);
};

const AMPInstagram = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-instagram",
    src: "https://cdn.ampproject.org/v0/amp-instagram-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-instagram", {
    "data-shortcode": mediaId,
    width: "1",
    height: "1",
    layout: "responsive"
  })));
};

const AMPFacebook = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-facebook",
    src: "https://cdn.ampproject.org/v0/amp-facebook-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-facebook", {
    width: "552",
    height: "303",
    layout: "responsive",
    "data-href": mediaId
  })));
};

const AMPPlaybuzz = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-playbuzz",
    src: "https://cdn.ampproject.org/v0/amp-playbuzz-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-playbuzz", {
    width: "552",
    height: "303",
    layout: "responsive",
    src: mediaId
  })));
};

const AMPDailymotion = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-dailymotion",
    src: "https://cdn.ampproject.org/v0/amp-dailymotion-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-dailymotion", {
    width: "552",
    height: "303",
    layout: "responsive",
    "data-videoid": mediaId
  })));
};

const createStyle$e = () => StyleSheet.create({
  ad: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const AMPAd = props => {
  const {
    adUnit,
    additionalStyle,
    height,
    width
  } = props;
  const style = createStyle$e();
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-ad",
    src: "https://cdn.ampproject.org/v0/amp-ad-0.1.js"
  })), createElement("div", {
    className: css(additionalStyle.style, style.ad)
  }, createElement("amp-ad", {
    width: width,
    height: height,
    type: "doubleclick",
    "data-slot": adUnit
  })));
};

const AMPSoundclound = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-soundcloud",
    src: "https://cdn.ampproject.org/v0/amp-soundcloud-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-soundcloud", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-trackid": mediaId,
    "data-visual": "true"
  })));
};

const AMPIframe = props => {
  const {
    src,
    additionalStyle,
    height
  } = props;
  return createElement(Fragment, null, createElement(Helmet$1, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-iframe",
    src: "https://cdn.ampproject.org/v0/amp-iframe-0.1.js"
  })), createElement("figure", {
    className: css(additionalStyle.style)
  }, createElement("amp-iframe", {
    height: height,
    sandbox: "allow-scripts allow-same-origin",
    layout: "fixed-height",
    frameborder: "0",
    src: src
  })));
};

const componentConfigThemeKey$A = 'articleContent';

const createBlockStyle$1 = (vrLineHeight, vrLineHeightSize) => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * vrLineHeightSize}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const createBlocksComponents$1 = ({
  body,
  vrLineHeight
}) => {
  let isPreviousElementTitle = false;
  return body.map(block => {
    const {
      type
    } = block;
    const blockStyle = isPreviousElementTitle ? createBlockStyle$1(vrLineHeight, 0.5) : createBlockStyle$1(vrLineHeight, 1);
    isPreviousElementTitle = false;

    switch (type) {
      case 'ad':
        {
          const {
            adWidth,
            adHeight,
            dataId,
            adUnitPath
          } = block;
          return createElement(AMPAd, {
            key: dataId,
            adUnit: adUnitPath,
            width: adWidth,
            height: adHeight,
            additionalStyle: blockStyle
          });
        }

      case 'image':
        {
          const {
            image,
            dataId
          } = block;
          return createElement(AMPImage, _extends({
            key: dataId
          }, image, {
            additionalStyle: blockStyle
          }));
        }

      case 'title':
        {
          isPreviousElementTitle = true;
          const {
            listItemNumber,
            text,
            dataId
          } = block;
          return createElement(ListItemTitle, {
            key: dataId,
            listItemNumber: listItemNumber,
            text: text,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'inline-text':
        {
          const {
            html,
            dataId
          } = block;
          return createElement(Paragraph, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'quote':
        {
          const {
            text,
            cite,
            dataId
          } = block;
          return createElement(Quote, {
            key: dataId,
            text: text,
            cite: cite,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'youtube':
        {
          const {
            dataId,
            mediaId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPYoutube, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'twitter':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPTwitter, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'instagram':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPInstagram, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'facebook':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPFacebook, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'jw':
        {
          const {
            playerId,
            videoId,
            dataId
          } = block;
          return createElement(AMPJWPlayer, {
            key: dataId,
            videoId: videoId,
            playerId: playerId,
            additionalStyle: blockStyle
          });
        }

      case 'giphy':
        {
          const {
            dataId
          } = block;
          const className = css(blockStyle.style); // @ts-ignore TS2339

          return createElement("amp-img", {
            key: dataId,
            alt: block.caption,
            src: block.url,
            layout: "responsive",
            width: block.width,
            height: block.height,
            class: className
          });
        }

      case 'playbuzz':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPPlaybuzz, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'dailyMotion':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPDailymotion, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'divider':
        {
          const {
            dataId
          } = block;
          return createElement(Divider, {
            key: dataId,
            additionalStyle: blockStyle,
            dataId: dataId
          });
        }

      case 'soundcloud':
        {
          const {
            mediaId,
            dataId
          } = block;
          return createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, createElement(AMPSoundclound, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'iframeEmbed':
        {
          const {
            src,
            dataId,
            height
          } = block;
          return createElement(AMPIframe, {
            key: dataId,
            height: height,
            src: src,
            additionalStyle: blockStyle
          });
        }

      case 'mmPlayer':
        {
          const {
            dataId,
            playerId,
            mediaId
          } = block;
          return createElement(AMPMMPlayer, {
            key: dataId,
            playlistId: mediaId,
            additionalStyle: blockStyle,
            playerId: playerId
          });
        }

      default:
        return null;
    }
  });
};

const AMPArticleContentComponent = props => {
  return createElement("div", null, createBlocksComponents$1(props));
};

const mapThemeProps$L = theme => {
  return {
    vrLineHeight: theme.vrLineHeight
  };
};

const AMPArticleContent = props => {
  const themeContext = useTheme(componentConfigThemeKey$A, mapThemeProps$L);
  return createElement(ErrorBoundary, null, createElement(AMPArticleContentComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$t = 'copyLinkButton';

const getStyles$4 = () => {
  return StyleSheet.create({
    copyLinkIcon: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '16px',
      transition: '200ms ease-in-out'
    },
    copyLinkIconAction: {
      left: '-150%'
    },
    successIcon: {
      left: '150%'
    },
    successIconAction: {
      left: '50%'
    }
  });
};

const copyToClipboard = text => {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

const CopyLinkButtonComponent = props => {
  const {
    canonical,
    buttonClass,
    color
  } = props;
  const styles = getStyles$4();
  let timeout;
  const [wasCopied, setWasCopied] = useState(false);

  const onClickCopy = () => {
    copyToClipboard(canonical);
    clearTimeout(timeout);
    setWasCopied(true);
    timeout = setTimeout(() => {
      setWasCopied(false);
    }, 1000);
  };

  const copyIconAction = wasCopied ? styles.copyLinkIconAction : styles.copyLinkIcon;
  const succcessIconAction = wasCopied ? styles.successIconAction : styles.successIcon;
  return createElement(ErrorBoundary, null, createElement("button", {
    type: "button",
    className: buttonClass,
    onClick: onClickCopy
  }, createElement("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64",
    className: css(styles.copyLinkIcon, copyIconAction)
  }, createElement("g", {
    fill: color
  }, createElement("path", {
    d: "M4.3,49.8c-0.9,0-1.8-0.3-2.6-0.9C0.6,48.1,0,46.8,0,45.5L0,4.3C0,1.9,1.9,0,4.3,0l41.2,0c2.4,0,4.3,1.9,4.3,4.3l0,6.5 l-7.1,0l0-3.7l-35.6,0l0,35.5l3.8,0l0,7.1l-6.3,0C4.5,49.8,4.4,49.8,4.3,49.8z"
  }), createElement("path", {
    d: "M18.6,64c-2.4,0-4.3-1.9-4.3-4.3l0-41.1c0-2.4,1.9-4.3,4.3-4.3l41,0c2.4,0,4.3,1.9,4.3,4.3L64,59.7c0,2.4-1.9,4.3-4.3,4.3 L18.6,64z M21.3,21.3l0,35.5l35.5,0l-0.1-35.5L21.3,21.3z M56.8,18.5C56.8,18.5,56.8,18.5,56.8,18.5L56.8,18.5z"
  }))), createElement("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64",
    className: css(styles.copyLinkIcon, succcessIconAction)
  }, createElement("g", {
    fill: color
  }, createElement("path", {
    d: "M59.5,10.8"
  }), createElement("path", {
    d: "M23.1,56L1,32.9c-1.4-1.4-1.3-3.7,0.1-5.1c1.4-1.4,3.7-1.4,5,0.1l17,17.8L57.9,9.1c1.4-1.4,3.6-1.5,5-0.1 c1.4,1.4,1.4,3.7,0.1,5.1L23.1,56z"
  }), createElement("path", {
    d: "M23.4,43.4"
  })))));
};

const mapThemeProps$M = theme => {
  return {
    color: theme.color
  };
};

const CopyLinkButton = props => {
  const themeContext = useTheme(componentConfigurationKey$t, mapThemeProps$M);
  return createElement(ErrorBoundary, null, createElement(CopyLinkButtonComponent, _extends({}, themeContext, props)));
};

const getMockVrLineHeight = () => {
  return {
    '@media (min-width: 1201px)': 24,
    '@media (max-width: 1200px)': 24,
    '@media (max-width: 900px)': 24,
    '@media (max-width: 600px)': 24
  };
};

const componentConfigurationKey$u = 'articleShareButton';
const socialButtonAnimation = {
  '0%': {
    opacity: 0
  },
  '100%': {
    opacity: 1
  }
};

const getStyles$5 = (vrLineHeight = getMockVrLineHeight(), backgroundColor) => {
  return StyleSheet.create({
    shareMenu: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
        width: '48px'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
        width: '48px'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
        width: '40px'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
        width: '40px'
      }
    },
    socialButton: {
      backgroundColor,
      border: 'none',
      borderRadius: '100%',
      position: 'relative',
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
      cursor: 'pointer',
      outline: 'none',
      overflow: 'hidden',
      opacity: 0,
      animationName: [socialButtonAnimation],
      animationDuration: '500ms',
      animationFillMode: 'forwards',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
        width: '40px',
        height: '40px'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
        width: '40px',
        height: '40px'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4}px`,
        width: '40px',
        height: '40px'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 3}px`,
        width: '36px',
        height: '36px'
      }
    },
    socialIcon: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        height: '20px'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        height: '20px'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        height: '20px'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        height: '16px'
      }
    }
  });
};

const getAnimationDelayClass = index => {
  return StyleSheet.create({
    animationModifier: {
      animationDelay: `${index * 50}ms`
    }
  });
};

const getShareButtons = (buttons, styles) => buttons.map((button, index) => {
  const {
    shareURL,
    src,
    type
  } = button;
  const animationDurationClass = getAnimationDelayClass(index);
  return (// eslint-disable-next-line jsx-a11y/anchor-is-valid
    createElement(Link, {
      className: css(styles.socialButton, animationDurationClass.animationModifier),
      key: index,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: () => {
        window.open(shareURL, '_blank', 'noopener');
        return false;
      }
    }, createElement("img", {
      src: src,
      alt: type,
      className: css(styles.socialIcon)
    }))
  );
});

const ShareMenuComponent = props => {
  const {
    shareConfig,
    canonical,
    vrLineHeight,
    buttonBackgroundColor
  } = props;
  const styles = getStyles$5(vrLineHeight, buttonBackgroundColor);
  const copyLinkAnimationClass = getAnimationDelayClass(shareConfig.length);
  return createElement("div", {
    className: css(styles.shareMenu)
  }, getShareButtons(shareConfig, styles), createElement(CopyLinkButton, {
    canonical: canonical,
    buttonClass: css(styles.socialButton, copyLinkAnimationClass.animationModifier)
  }));
};

const mapThemeProps$N = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    buttonBackgroundColor: theme.buttonBackgroundColor
  };
};

const ShareMenu = props => {
  const themeContext = useTheme(componentConfigurationKey$u, mapThemeProps$N);
  return createElement(ErrorBoundary, null, createElement(ShareMenuComponent, _extends({}, props, themeContext)));
};

const componentConfigurationKey$v = 'articleShareButton';
const shareButtonSize = {
  xLarge: 48,
  large: 48,
  medium: 40,
  small: 40
};

const getStyles$6 = (vrLineHeight, backgroundColor, buttonHoverColor) => {
  return StyleSheet.create({
    wrapper: {
      position: 'absolute',
      right: 0,
      zIndex: 1000,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        width: '40px',
        top: `calc((${shareButtonSize.xLarge}px - 100%)/-2)`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        width: '40px',
        top: `calc((${shareButtonSize.large}px - 100%)/-2)`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        width: '32px',
        top: `calc((${shareButtonSize.medium}px - 100%)/-2)`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        width: '32px',
        top: `calc((${shareButtonSize.small}px - 100%)/-2)`
      }
    },
    shareButton: {
      outline: 'none',
      border: 'none',
      background: 'transparent',
      borderRadius: '100%',
      '-webkit-tap-highlight-color': 'transparent',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        width: `${shareButtonSize.xLarge}px`,
        height: `${shareButtonSize.xLarge}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        width: `${shareButtonSize.large}px`,
        height: `${shareButtonSize.large}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        width: `${shareButtonSize.medium}px`,
        height: `${shareButtonSize.medium}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        width: `${shareButtonSize.small}px`,
        height: `${shareButtonSize.small}px`
      },
      '@media (hover: hover)': {
        ':hover': {
          backgroundColor: buttonHoverColor
        }
      }
    },
    shareButtonOpen: {
      backgroundColor: buttonHoverColor
    },
    shareIcon: {
      margin: 'auto',
      display: 'block',
      cursor: 'pointer',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        width: '32px'
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        width: '32px'
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        width: '24px'
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        width: '24px'
      }
    }
  });
};

const ShareButtonComponent = props => {
  const {
    vrLineHeight,
    buttonBackgroundColor,
    buttonHoverColor,
    shareConfig,
    canonical
  } = props;
  const styles = getStyles$6(vrLineHeight, buttonBackgroundColor, buttonHoverColor);
  const [isOpen, setIsOpen] = useState(false);
  const shareButtonClass = isOpen ? css(styles.shareButton, styles.shareButtonOpen) : css(styles.shareButton);
  return createElement("div", {
    className: css(styles.wrapper)
  }, createElement("button", {
    type: "button",
    className: shareButtonClass
  }, createElement("svg", {
    viewBox: "0 0 16 16",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    className: css(styles.shareIcon),
    onClick: () => setIsOpen(!isOpen)
  }, createElement("g", {
    id: "Icons-/-Social-/-Share",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, createElement("path", {
    d: "M9.58105,13.3911 L9.58105,9.7861 C9.58105,9.7861 4.17905,9.4641 5.00000001e-05,14.0001 C5.00000001e-05,14.0001 1.13705,5.6716 9.57955,4.6051 L9.57955,1.0001 L16.00005,7.1946 L9.58105,13.3911 Z",
    id: "share-icon",
    fill: buttonBackgroundColor,
    fillRule: "nonzero"
  })))), isOpen ? createElement(ShareMenu, {
    shareConfig: shareConfig,
    canonical: canonical
  }) : null);
};

const mapThemeProps$O = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    buttonBackgroundColor: theme.buttonBackgroundColor,
    buttonHoverColor: theme.buttonHoverColor
  };
};

const ShareButton = props => {
  const themeContext = useTheme(componentConfigurationKey$v, mapThemeProps$O);
  return createElement(ErrorBoundary, null, createElement(ShareButtonComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$w = 'taboola';

const createStyle$f = vrLineHeight => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 2}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`,
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
      }
    }
  });
};

class TaboolaComponent extends Component {
  componentDidMount() {
    // @ts-ignore TS2339
    // eslint-disable-next-line no-underscore-dangle
    window._taboola = window._taboola || []; // @ts-ignore TS2339
    // eslint-disable-next-line no-underscore-dangle

    window._taboola.push({
      mode: 'thumbnails-c',
      container: 'taboola-below-article-feed',
      placement: 'Below Article Feed',
      target_type: 'mix'
    }); // @ts-ignore TS2339
    // eslint-disable-next-line no-underscore-dangle


    window._taboola.push({
      flush: true
    });
  }

  shouldComponentUpdate() {
    return false;
  }

  render() {
    const {
      vrLineHeight
    } = this.props;
    const style = createStyle$f(vrLineHeight);
    return createElement(ErrorBoundary, null, createElement("div", {
      className: css(style.style),
      id: "taboola-below-article-feed"
    }));
  }

}

const mapThemeToProps$j = theme => ({
  vrLineHeight: theme.vrLineHeight
});

const Taboola = props => {
  const themeContext = useTheme(componentConfigurationKey$w, mapThemeToProps$j);
  return createElement(ErrorBoundary, null, createElement(TaboolaComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$x = 'recommendationCardPlaceHolder';
const getRecommendationCardPlaceHolderStyle = (vrLineHeight, backgroundColor) => StyleSheet.create({
  cardPlaceHolderWrapper: {
    width: '100%',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  cardPlaceHolder: {
    paddingTop: 'calc(100% / (16 / 9))',
    backgroundColor
  }
});
const RecommendationCardPlaceHolderComponent = props => {
  const {
    vrLineHeight,
    backgroundColor
  } = props;
  const style = getRecommendationCardPlaceHolderStyle(vrLineHeight, backgroundColor);
  return React__default.createElement("div", {
    className: css(style.cardPlaceHolderWrapper)
  }, React__default.createElement("div", {
    className: css(style.cardPlaceHolder)
  }));
};

const mapThemeProps$P = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor
  };
};

const RecommendationCardPlaceHolder = () => {
  const themeContext = useTheme(componentConfigurationKey$x, mapThemeProps$P);
  return React__default.createElement(ErrorBoundary, null, React__default.createElement(RecommendationCardPlaceHolderComponent, themeContext));
};

const useRecommendationScrollTrigger = onScrolledIntoView => {
  const wrapper = useRef(null);
  useEffect(() => {
    if (!wrapper.current || typeof IntersectionObserver === 'undefined') return;
    const options = {
      rootMargin: '0px 0px 100px 0px',
      threshold: 0.1
    };
    const obsrever = new IntersectionObserver(e => {
      if (e[0].isIntersecting) {
        obsrever.unobserve(wrapper.current);
        onScrolledIntoView();
      }
    }, options);
    obsrever.observe(wrapper.current);
  }, []);
  return wrapper;
};
const mapThemeProps$Q = theme => {
  return {
    vrLineHeight: theme.vrLineHeight
  };
};
const getSectionStyle = vrLineHeight => StyleSheet.create({
  section: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  }
});

const createArrayWithPlaceHoldersNumberLength = numberOfPlaceHolders => new Array(numberOfPlaceHolders).fill(0);

const createPlaceholders = numberOfPlaceHolders => {
  return createArrayWithPlaceHoldersNumberLength(numberOfPlaceHolders).map((item, index) => createElement(RecommendationCardPlaceHolder, {
    key: index
  }));
};

const componentConfigurationKey$y = 'postRecommendationsHeader';

const getStyle$n = (color, vrLineHeight) => StyleSheet.create({
  square: {
    width: '10px',
    height: '10px',
    backgroundColor: color,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      height: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      height: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      height: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      height: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`
    }
  }
});

const PostRecommendationsHeaderComponent = ({
  header,
  color,
  vrLineHeight
}) => {
  const style = getStyle$n(color, vrLineHeight);
  return createElement("div", {
    className: css(style.container)
  }, createElement("div", {
    className: css(style.square)
  }), createElement("h2", null, header));
};

const mapThemeProps$R = theme => {
  return {
    vrLineHeight: theme.vrLineHeight,
    color: theme.color
  };
};

const PostRecommendationsHeader = props => {
  const themeContext = useTheme(componentConfigurationKey$y, mapThemeProps$R);
  return createElement(ErrorBoundary, null, createElement(PostRecommendationsHeaderComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$z = 'postRecommendations';
const SixSixPostRecommendationsComponent = props => {
  const {
    sectionName,
    sectionCards,
    onScrolledIntoView,
    vrLineHeight
  } = props;
  const wrapper = useRecommendationScrollTrigger(onScrolledIntoView);
  const sectionStyle = getSectionStyle(vrLineHeight);
  return createElement("section", {
    ref: wrapper,
    className: css(sectionStyle.section)
  }, createElement(EventProvider, {
    category: "Recommendations"
  }, createElement(PostRecommendationsHeader, {
    header: sectionName
  }), sectionCards.length !== 0 ? createElement(SixSixArticleList, {
    cardsProps: sectionCards,
    order: "horizontal-vertical"
  }) : createPlaceholders(12)));
};
const SixSixPostRecommendations = props => {
  const themeContext = useTheme(componentConfigurationKey$z, mapThemeProps$Q);
  return createElement(ErrorBoundary, null, createElement(SixSixPostRecommendationsComponent, _extends({}, themeContext, props)));
};

const componentConfigurationKey$A = 'postRecommendations';

const getStyle$o = vrLineHeight => StyleSheet.create({
  card: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  }
});

const OnTopCardsPostRecommendationsComponent = props => {
  const {
    sectionName,
    sectionCards,
    onScrolledIntoView,
    vrLineHeight
  } = props;
  const wrapper = useRecommendationScrollTrigger(onScrolledIntoView);
  const style = getStyle$o(vrLineHeight);
  const sectionStyle = getSectionStyle(vrLineHeight);
  return createElement("section", {
    ref: wrapper,
    className: css(sectionStyle.section)
  }, createElement(EventProvider, {
    category: "Recommendations"
  }, createElement(PostRecommendationsHeader, {
    header: sectionName
  }), sectionCards.length !== 0 ? sectionCards.map(({
    title,
    image,
    articleUrl
  }, index) => createElement("div", {
    className: css(style.card),
    key: index
  }, createElement(OnTopCard, {
    title: title,
    image: image,
    articleUrl: articleUrl,
    lazyLoad: true
  }))) : createPlaceholders(4)));
};
const OnTopCardsPostRecommendations = props => {
  const themeContext = useTheme(componentConfigurationKey$A, mapThemeProps$Q);
  return createElement(ErrorBoundary, null, createElement(OnTopCardsPostRecommendationsComponent, _extends({}, themeContext, props)));
};

const WEB_FONTS_PROVIDERS = {
  GOOGLE: 'google',
  CUSTOM: 'custom'
};

const fontsWeightNameMap = {
  100: ['Thin', 'Hairline'],
  Thin: 100,
  Hairline: 100,
  200: ['Extra Light', 'Ultra Light'],
  ExtraLight: 200,
  UltraLight: 200,
  300: ['Light'],
  Light: 300,
  400: ['Regular', 'Normal'],
  Normal: 400,
  Regular: 400,
  500: ['Medium'],
  Medium: 500,
  600: ['Semi Bold', 'Demi Bold'],
  SemiBold: 600,
  DemiBold: 600,
  700: ['Bold'],
  Bold: 700,
  800: ['Extra Bold', 'Ultra Bold'],
  ExtraBold: 800,
  UltraBold: 800,
  900: ['Black', 'Heavy'],
  Black: 900,
  Heavy: 900
};

const isName = value => Number.isNaN(parseFloat(value));

const getFontWeight = weightOrName => {
  return isName(weightOrName) ? fontsWeightNameMap[weightOrName] : weightOrName;
};

const getFontName = weightOrName => {
  return isName(weightOrName) ? weightOrName : fontsWeightNameMap[weightOrName];
};

const weightNameTransformer = (weightOrName, name = false) => {
  return name ? getFontName(weightOrName) : getFontWeight(weightOrName);
};

const baseGoogleFontsUrl = 'https://fonts.googleapis.com/css?family';
const baseCustomFontsUrl = () => 'https://static.minutemedia.com/static/fonts';

const webFontFormats = {
  woff: 'woff',
  woff2: 'woff2',
  eot: 'eot'
};
const defaultFormat = webFontFormats.woff;
const createFontFamilyNameParam = familyName => {
  return familyName.split(' ').join('+');
};
const parseVariant = (variant = '400') => {
  const match = variant.match(/^([0-9]*)([a-z])$/i);

  if (match) {
    return {
      weight: match[1],
      style: match[2]
    };
  }

  return {
    weight: variant,
    style: 'n'
  };
};
const createFVDKey = (family, variant) => {
  const {
    weight,
    style
  } = parseVariant(variant);
  return `wf-${family.replace(/\s/g, '').toLowerCase()}-${style[0] + weight[0]}`;
};
const getStyle$p = (key = 'n', transform = style => style) => {
  const fontStyles = {
    i: 'italic',
    n: 'normal'
  };
  return transform(fontStyles[key]);
};
const createGoogleFontsParams = fontConfig => {
  return fontConfig.variants.reduce((acc, variant, index) => {
    const divider = index === 0 ? ':' : ',';
    return `${acc}${divider}${variant}`;
  }, createFontFamilyNameParam(fontConfig.family));
};
const createGoogleFontsUrl = fontConfig => {
  return `${baseGoogleFontsUrl}=${createGoogleFontsParams(fontConfig)}`;
};
const createFontFamilyDeclaration = ({
  family,
  weight,
  style,
  format = defaultFormat
}) => {
  const fontStyle = style !== 'n' ? getStyle$p(style, s => s.charAt(0).toUpperCase() + s.slice(1)) : '';
  const localName = `${family}-${weightNameTransformer(weight, true)[0].replace(/\s/g, '')}${fontStyle}`;
  return `
  @font-face {font-family: '${family}';
  font-style: ${getStyle$p(style)};
  font-weight: ${weightNameTransformer(weight)};
  src: local('${family} ${weight} ${style}'), local('${localName}'),url(${baseCustomFontsUrl()}/${family.toLowerCase()}/${format}/${localName}.${format}) format('${format}');
  }`;
};
const createCustomStylesheetDataUri = fontConfig => {
  const cssSource = fontConfig.variants.reduce((css, variant) => {
    const styleAndWeight = parseVariant(variant);
    const fontFaceDeclaration = createFontFamilyDeclaration({ ...fontConfig,
      ...styleAndWeight
    });
    return `${css}${fontFaceDeclaration}`;
  }, '');
  return `data:text/css;base64,${Buffer.from(cssSource).toString('base64')}`;
};
const createCustomStylesheet = fontConfig => {
  return fontConfig.stylesheetUrl || createCustomStylesheetDataUri(fontConfig);
};
const getStylesheetByProvider = fontConfig => {
  switch (fontConfig.provider) {
    case WEB_FONTS_PROVIDERS.GOOGLE:
      return createGoogleFontsUrl(fontConfig);

    case WEB_FONTS_PROVIDERS.CUSTOM:
      return createCustomStylesheet(fontConfig);

    default:
      return createGoogleFontsUrl(fontConfig);
  }
};

var fontfaceobserver_standalone = createCommonjsModule(function (module) {
/* Font Face Observer v2.1.0 - © Bram Stein. License: BSD-3-Clause */
(function () {
  function l(a, b) {
    document.addEventListener ? a.addEventListener("scroll", b, !1) : a.attachEvent("scroll", b);
  }

  function m(a) {
    document.body ? a() : document.addEventListener ? document.addEventListener("DOMContentLoaded", function c() {
      document.removeEventListener("DOMContentLoaded", c);
      a();
    }) : document.attachEvent("onreadystatechange", function k() {
      if ("interactive" == document.readyState || "complete" == document.readyState) document.detachEvent("onreadystatechange", k), a();
    });
  }

  function t(a) {
    this.a = document.createElement("div");
    this.a.setAttribute("aria-hidden", "true");
    this.a.appendChild(document.createTextNode(a));
    this.b = document.createElement("span");
    this.c = document.createElement("span");
    this.h = document.createElement("span");
    this.f = document.createElement("span");
    this.g = -1;
    this.b.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.c.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.f.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.h.style.cssText = "display:inline-block;width:200%;height:200%;font-size:16px;max-width:none;";
    this.b.appendChild(this.h);
    this.c.appendChild(this.f);
    this.a.appendChild(this.b);
    this.a.appendChild(this.c);
  }

  function u(a, b) {
    a.a.style.cssText = "max-width:none;min-width:20px;min-height:20px;display:inline-block;overflow:hidden;position:absolute;width:auto;margin:0;padding:0;top:-999px;white-space:nowrap;font-synthesis:none;font:" + b + ";";
  }

  function z(a) {
    var b = a.a.offsetWidth,
        c = b + 100;
    a.f.style.width = c + "px";
    a.c.scrollLeft = c;
    a.b.scrollLeft = a.b.scrollWidth + 100;
    return a.g !== b ? (a.g = b, !0) : !1;
  }

  function A(a, b) {
    function c() {
      var a = k;
      z(a) && a.a.parentNode && b(a.g);
    }

    var k = a;
    l(a.b, c);
    l(a.c, c);
    z(a);
  }

  function B(a, b) {
    var c = b || {};
    this.family = a;
    this.style = c.style || "normal";
    this.weight = c.weight || "normal";
    this.stretch = c.stretch || "normal";
  }

  var C = null,
      D = null,
      E = null,
      F = null;

  function G() {
    if (null === D) if (J() && /Apple/.test(window.navigator.vendor)) {
      var a = /AppleWebKit\/([0-9]+)(?:\.([0-9]+))(?:\.([0-9]+))/.exec(window.navigator.userAgent);
      D = !!a && 603 > parseInt(a[1], 10);
    } else D = !1;
    return D;
  }

  function J() {
    null === F && (F = !!document.fonts);
    return F;
  }

  function K() {
    if (null === E) {
      var a = document.createElement("div");

      try {
        a.style.font = "condensed 100px sans-serif";
      } catch (b) {}

      E = "" !== a.style.font;
    }

    return E;
  }

  function L(a, b) {
    return [a.style, a.weight, K() ? a.stretch : "", "100px", b].join(" ");
  }

  B.prototype.load = function (a, b) {
    var c = this,
        k = a || "BESbswy",
        r = 0,
        n = b || 3E3,
        H = new Date().getTime();
    return new Promise(function (a, b) {
      if (J() && !G()) {
        var M = new Promise(function (a, b) {
          function e() {
            new Date().getTime() - H >= n ? b(Error("" + n + "ms timeout exceeded")) : document.fonts.load(L(c, '"' + c.family + '"'), k).then(function (c) {
              1 <= c.length ? a() : setTimeout(e, 25);
            }, b);
          }

          e();
        }),
            N = new Promise(function (a, c) {
          r = setTimeout(function () {
            c(Error("" + n + "ms timeout exceeded"));
          }, n);
        });
        Promise.race([N, M]).then(function () {
          clearTimeout(r);
          a(c);
        }, b);
      } else m(function () {
        function v() {
          var b;
          if (b = -1 != f && -1 != g || -1 != f && -1 != h || -1 != g && -1 != h) (b = f != g && f != h && g != h) || (null === C && (b = /AppleWebKit\/([0-9]+)(?:\.([0-9]+))/.exec(window.navigator.userAgent), C = !!b && (536 > parseInt(b[1], 10) || 536 === parseInt(b[1], 10) && 11 >= parseInt(b[2], 10))), b = C && (f == w && g == w && h == w || f == x && g == x && h == x || f == y && g == y && h == y)), b = !b;
          b && (d.parentNode && d.parentNode.removeChild(d), clearTimeout(r), a(c));
        }

        function I() {
          if (new Date().getTime() - H >= n) d.parentNode && d.parentNode.removeChild(d), b(Error("" + n + "ms timeout exceeded"));else {
            var a = document.hidden;
            if (!0 === a || void 0 === a) f = e.a.offsetWidth, g = p.a.offsetWidth, h = q.a.offsetWidth, v();
            r = setTimeout(I, 50);
          }
        }

        var e = new t(k),
            p = new t(k),
            q = new t(k),
            f = -1,
            g = -1,
            h = -1,
            w = -1,
            x = -1,
            y = -1,
            d = document.createElement("div");
        d.dir = "ltr";
        u(e, L(c, "sans-serif"));
        u(p, L(c, "serif"));
        u(q, L(c, "monospace"));
        d.appendChild(e.a);
        d.appendChild(p.a);
        d.appendChild(q.a);
        document.body.appendChild(d);
        w = e.a.offsetWidth;
        x = p.a.offsetWidth;
        y = q.a.offsetWidth;
        I();
        A(e, function (a) {
          f = a;
          v();
        });
        u(e, L(c, '"' + c.family + '",sans-serif'));
        A(p, function (a) {
          g = a;
          v();
        });
        u(p, L(c, '"' + c.family + '",serif'));
        A(q, function (a) {
          h = a;
          v();
        });
        u(q, L(c, '"' + c.family + '",monospace'));
      });
    });
  };

   module.exports = B ;
})();
});

class WebfontLoader {
  constructor(fonts) {
    _defineProperty(this, "fonts", void 0);

    _defineProperty(this, "createFontObserver", (family, variant) => {
      const {
        weight,
        style
      } = parseVariant(variant);
      return new fontfaceobserver_standalone(family, {
        weight: weight || variant,
        style: getStyle$p(style)
      }).load();
    });

    this.fonts = fonts;
  }

  observeFontLoading() {
    const {
      fonts
    } = this;
    const fontObservers = {};
    fonts.forEach(font => {
      const {
        family,
        variants
      } = font;
      variants.forEach(variant => {
        const fvd = createFVDKey(family, variant);
        const fontVariantObserver = this.createFontObserver(family, variant);
        fontObservers[fvd] = fontVariantObserver;
      });
    });
    return Promise.all(Object.values(fontObservers));
  }

}

const createFontFamilyLink = (fontConfiguration, index) => {
  const [rel, setRel] = useState('preload');
  useEffect(() => {
    setRel('stylesheet');
  }, []);
  return createElement("link", {
    rel: rel,
    key: index,
    href: getStylesheetByProvider(fontConfiguration),
    as: "style"
  });
};

const DEFAULT_FONT_FAMILY_CLASS_NAME = 'default-font-family';
const FONT_FAMILY_CLASS_NAME = 'font-family';
const WebFonts = props => {
  const {
    fontsConfiguration,
    fontFamilyByElement,
    children
  } = props;
  const [className, setClassName] = useState(DEFAULT_FONT_FAMILY_CLASS_NAME);
  useEffect(() => {
    new WebfontLoader(fontsConfiguration).observeFontLoading().then(() => setClassName(FONT_FAMILY_CLASS_NAME));
  }, [fontsConfiguration]);
  return createElement(Fragment, null, createElement(Helmet, null, fontsConfiguration.map(createFontFamilyLink), createElement("html", {
    className: className
  })), createElement("style", {
    dangerouslySetInnerHTML: {
      __html: `
.${DEFAULT_FONT_FAMILY_CLASS_NAME} h1 { font-family: ${fontFamilyByElement.h1.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} h2 { font-family: ${fontFamilyByElement.h2.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} h3 { font-family: ${fontFamilyByElement.h3.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} h4 { font-family: ${fontFamilyByElement.h4.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} span { font-family: ${fontFamilyByElement.span.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} p { font-family: ${fontFamilyByElement.p.defaultFontFamily}; }
.${DEFAULT_FONT_FAMILY_CLASS_NAME} { font-family: ${fontFamilyByElement.p.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} h1 { font-family: ${fontFamilyByElement.h1.fontFamily}, ${fontFamilyByElement.h1.defaultFontFamily};}
.${FONT_FAMILY_CLASS_NAME} h2 { font-family: ${fontFamilyByElement.h2.fontFamily}, ${fontFamilyByElement.h2.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} h3 { font-family: ${fontFamilyByElement.h3.fontFamily}, ${fontFamilyByElement.h3.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} h4 { font-family: ${fontFamilyByElement.h4.fontFamily}, ${fontFamilyByElement.h4.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} span { font-family: ${fontFamilyByElement.span.fontFamily}, ${fontFamilyByElement.span.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} p { font-family: ${fontFamilyByElement.p.fontFamily}, ${fontFamilyByElement.p.defaultFontFamily}; }
.${FONT_FAMILY_CLASS_NAME} { font-family: ${fontFamilyByElement.p.fontFamily}, ${fontFamilyByElement.p.defaultFontFamily}; }`
    }
  }), children);
};

const createFontFamilyStylesheet = fontConfiguration => {
  const errorReporting = useContext(ErrorReporting);

  try {
    return createElement("link", {
      rel: "stylesheet",
      href: getStylesheetByProvider(fontConfiguration)
    });
  } catch (e) {
    errorReporting.reportException(e);
    return null;
  }
};

const createStyle$g = (fontFamily, defaultFontFamily) => StyleSheet.create({
  defaultFontFamily: {
    fontFamily: defaultFontFamily
  },
  fontFamily: {
    fontFamily
  }
});

const AmpFont = props => {
  const {
    fontFamily,
    fontFamilyClassName,
    defaultFontFamilyClassName
  } = props;
  return (// @ts-ignore TS2339: Property 'amp-font' does not exist on type 'JSX.IntrinsicElements'.
    createElement("amp-font", {
      layout: "nodisplay",
      "font-family": fontFamily,
      "on-load-add-class": fontFamilyClassName,
      "on-load-remove-class": defaultFontFamilyClassName
    })
  );
};
const AmpWebFonts = props => {
  const {
    children,
    fontsConfiguration,
    fontFamilyByElement
  } = props;
  const {
    fontFamily
  } = fontFamilyByElement.p;
  const style = createStyle$g(fontFamily, fontFamilyByElement.p.defaultFontFamily);
  const defaultFontFamilyClassName = css(style.defaultFontFamily);
  const fontFamilyClassName = css(style.fontFamily);
  return createElement(Fragment, null, createElement(Helmet, null, createElement("script", {
    async: undefined,
    "custom-element": "amp-font",
    src: "https://cdn.ampproject.org/v0/amp-font-0.1.js"
  }), createFontFamilyStylesheet(fontsConfiguration.find(({
    family
  }) => family === fontFamily)), createElement("html", {
    className: defaultFontFamilyClassName
  })), createElement(AmpFont, {
    defaultFontFamilyClassName: defaultFontFamilyClassName,
    fontFamily: fontFamily,
    fontFamilyClassName: fontFamilyClassName
  }), children);
};

const componentConfigurationKey$B = 'sponsor';

const createSponsorStyle$1 = (vrLineHeight, dividerColor, textColor) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`
    }
  },
  text: {
    color: textColor,
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  divider: {
    backgroundColor: dividerColor,
    width: 'auto',
    height: '1px'
  },
  topDivider: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  bottomDivider: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`
    }
  },
  imageWrapper: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 2}px`
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 2}px`
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      width: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`
    }
  }
});

const createImageAdditionalStyle = () => StyleSheet.create({
  style: {
    borderRadius: '50%'
  }
});

const SponsorComponent = props => {
  const {
    dividerColor,
    vrLineHeight,
    text,
    image,
    textColor
  } = props;
  const style = createSponsorStyle$1(vrLineHeight, dividerColor, textColor);
  return createElement(Fragment, null, createElement("div", {
    className: css(style.topDivider, style.divider)
  }), createElement("div", {
    className: css(style.wrapper)
  }, createElement("div", {
    className: css(style.imageWrapper)
  }, createElement(Image, _extends({}, image, {
    additionalStyle: createImageAdditionalStyle()
  }))), createElement("p", {
    className: css(style.text)
  }, text)), createElement("div", {
    className: css(style.bottomDivider, style.divider)
  }));
};

const mapThemeProps$S = theme => ({
  dividerColor: theme.dividerColor,
  textColor: theme.textColor,
  vrLineHeight: theme.vrLineHeight
});

const Sponsor = props => {
  const themeContext = useTheme(componentConfigurationKey$B, mapThemeProps$S);
  return createElement(ErrorBoundary, null, createElement(SponsorComponent, _extends({}, props, themeContext)));
};

export { AMPAnalytics, AMPAppContent, AMPArticleContent, AMPCover, AMPHeaderAndSideBar, AMPImage, AMPSocialContainer, AMPSponsor, Ad, AmpWebFonts, AppContent, ArticleContent, ArticleMainImage, ArticleTitle, Button, ContentAndSidebar, Error$1 as Error, ErrorBoundary, ErrorReporting, ExternalScripts, Footer, Header, HeadlinesArticleList, HighlightsArticleList, LoadMoreButtonWrapper, MainWrapper, MetaHeaderTags, OnTopCardsPostRecommendations, OneThreeArticleList, PostMainContent, PostMetaHeaderTags, PostSidebar, ScrollableArticleList, Section, SectionHeader, ShareButton, SixSixArticleList, SixSixPostRecommendations, Sponsor, Taboola, ThemeProviderWithFonts, WebFonts };
//# sourceMappingURL=components.bundle.esm.js.map
